import React, { useEffect, useLayoutEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ReactSortable } from 'react-sortablejs';
import './Chapter.css';
import '../Animate/animate.css';
import Navbar from '../Navigation/Navbar';
import QuizBlock from '../Block/QuizBlock';
import ModalBlock from '../Modal/ModalBlock';
import ModalEditorHistory from '../Modal/ModalEditorHistory';
import Video from '../Video/Video';
import TemplateManager from './TemplateManager';
import Loading from '../Common/Loading';
import Toast from '../Common/Toast';
import SavingIndicator from '../Common/SavingIndicator';
import useToast from '../Common/useToast';
import {
    STAGE_WIDTH, STAGE_HEIGHT, getStageScale, readStageScale, findStageEl, clientPointToStage,
} from './chapterStage';


import { getCurrentUser, isAuthenticated, isAdmin } from '../../api/users_institutions';
import { getChapterAdminPermissions } from '../../api/admin_permissions';
import { getChaptersByTitle, updateChapter } from '../../api/chapters';
import {
    getLessonsByChapter, createLesson, updateLesson, deleteLesson,
    getLessonPagesByIds, createLessonPage, updateLessonPage, deleteLessonPage,
    createLessonPageBlock, updateLessonPageBlock, deleteLessonPageBlock,
    createQuizBlock, updateQuizBlock,
    getQuizQuestionsByChapter, createQuizQuestion, updateQuizQuestion, deleteQuizQuestion,
    getQuizChoicesByChapter, createQuizChoice, updateQuizChoice, deleteQuizChoice,
    getUserQuizAttemptsByChapter, createUserQuizAttempt
} from '../../api/lessons';
import { getLessonPageBlockMediaUrl } from '../../api/lessons';
import { createChapterHistoryLog } from '../../api/history_logs';

// Import Progresses
import { getUserLessonProgresses, createUserLessonProgress, updateUserLessonProgress, deleteUserLessonProgress} from '../../api/progresses';
import { getUserLessonVideoProgresses, createUserLessonVideoProgress } from '../../api/progresses';
import { getUserTopicContentProgresses, updateUserTopicContentProgress } from '../../api/progresses';
import { getPersistedEditorMode } from '../../utils/editorMode';
import { isLevelNameLockedForUser, checkAdminLevelEditorPermission } from '../../utils/levelAccess';

// Custom drag-and-drop MIME type carrying the block type being dragged from a toolbar tool-pill
// (see the "toolbar-actions" tool-pills and .blocks-container's onDrop) to the canvas.
const DRAG_BLOCK_TYPE_MIME = 'application/x-lessonblock-type';

const coerceInteger = (value, fallback = 0) => {
    const parsedValue = Number.parseInt(value, 10);
    return Number.isFinite(parsedValue) ? parsedValue : fallback;
};

/**
 * One lesson page's canvas: the fluid .blocks-container, plus the fixed-size .blocks-stage inside
 * it that block coordinates are relative to (see chapterStage.js).
 *
 * Each page measures ITSELF rather than sharing a chapter-wide scale, because no two
 * .blocks-containers are necessarily the same height: page height is per-page
 * (--lesson-section-height), only the first page of a lesson carries the tall header-area (the
 * rest get a 60px spacer), and a full-bleed background page drops the header entirely. A single
 * shared measurement makes every page but the measured one scale against the wrong height, which
 * shows up as blocks sitting at the wrong vertical position.
 *
 * Module scope, not nested in Chapter: a component type re-created each render would remount this
 * subtree (and blow away the ResizeObserver) on every keystroke.
 */
function LessonPageCanvas({ containerProps, children }) {
    const containerRef = useRef(null);
    const [metrics, setMetrics] = useState({ scale: 1, offsetX: 0, offsetY: 0 });

    useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el) return undefined;

        const measure = () => {
            // clientWidth/Height, not getBoundingClientRect: the container is never itself
            // transformed, and these exclude scrollbars.
            const { clientWidth: width, clientHeight: height } = el;
            if (!width || !height) return;
            const scale = getStageScale(width, height);
            setMetrics((previous) => {
                // Centred on both axes: "contain" always leaves slack on one of them, and pinning
                // to a corner makes that slack read as the page being misaligned.
                const offsetX = Math.max(0, (width - STAGE_WIDTH * scale) / 2);
                const offsetY = Math.max(0, (height - STAGE_HEIGHT * scale) / 2);
                // Sub-pixel churn would re-render the page on every frame of a resize.
                if (Math.abs(previous.scale - scale) < 0.0005
                    && Math.abs(previous.offsetX - offsetX) < 0.5
                    && Math.abs(previous.offsetY - offsetY) < 0.5) {
                    return previous;
                }
                return { scale, offsetX, offsetY };
            });
        };

        measure();
        if (typeof ResizeObserver === 'undefined') {
            window.addEventListener('resize', measure);
            return () => window.removeEventListener('resize', measure);
        }
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    return (
        <div {...containerProps} ref={containerRef}>
            <div
                className="blocks-stage"
                style={{
                    '--stage-width': `${STAGE_WIDTH}px`,
                    '--stage-height': `${STAGE_HEIGHT}px`,
                    '--stage-scale': metrics.scale,
                    '--stage-offset-x': `${metrics.offsetX}px`,
                    '--stage-offset-y': `${metrics.offsetY}px`,
                }}
            >
                {children}
            </div>
        </div>
    );
}

const getEmbeddedVideoSrc = (url = '') => {
    if (!url) return '';

    if (url.includes('youtube.com')) {
        if (url.includes('watch?v=')) return url.replace('watch?v=', 'embed/');
        if (url.includes('/watch/')) return url.replace('/watch/', '/embed/');
        return url;
    }

    if (url.includes('youtu.be/')) {
        return url.replace('youtu.be/', 'www.youtube.com/embed/');
    }

    if (url.includes('drive.google.com') || url.includes('docs.google.com')) {
        const fileIdMatch = url.match(/\/file\/d\/([^/]+)/) || url.match(/[?&]id=([^&]+)/);
        if (fileIdMatch && fileIdMatch[1]) {
            return `https://drive.google.com/file/d/${fileIdMatch[1]}/preview`;
        }
        return url.replace('/view', '/preview');
    }

    if (url.includes('onedrive.live.com') || url.includes('1drv.ms')) {
        const residMatch = url.match(/[?&]resid=([^&]+)/i);
        const authKeyMatch = url.match(/[?&]authkey=([^&]+)/i);

        if (residMatch && residMatch[1]) {
            const resid = decodeURIComponent(residMatch[1]);
            const authKey = authKeyMatch?.[1] ? `&authkey=${decodeURIComponent(authKeyMatch[1])}` : '';
            return `https://onedrive.live.com/embed?resid=${encodeURIComponent(resid)}${authKey}&em=2`;
        }
    }

    return url;
};

const normalizeBlockType = (blockType = '') => (blockType === 'text-block' ? 'card' : blockType);

const isInlineTextBlockType = (blockType = '') => ['text', 'title', 'text-block'].includes(normalizeBlockType(blockType));

// Transforms a raw LessonPageBlock API object into the shape chapterData expects locally.
// Shared by the initial chapter fetch and by any flow (e.g. media block creation) that needs
// to merge a freshly created/updated block into local state without a full refetch.
const mapApiBlockToLocal = (block, quizQuestions = [], quizChoices = []) => {
    let questions = undefined;
    let quizBlock = null;
    const normalizedType = normalizeBlockType(block.block_type);

    if (normalizedType === 'quiz') {
        const quizBlockId = block.quiz_block?.id;
        const blockQuizQuestions = quizBlockId ? quizQuestions.filter((question) => question.quiz_block === quizBlockId) : [];
        questions = blockQuizQuestions.map((question) => ({
            ...question,
            type: question.choice_type || 'single',
            choices: quizChoices.filter((choice) => choice.quiz_question === question.id),
        }));
        quizBlock = block.quiz_block ? {
            ...block.quiz_block,
            questions,
        } : {
            id: null,
            lesson_page_block: block.id,
            choice_type: 'single',
            questions,
        };
    }

    return {
        id: block.id,
        type: normalizedType,
        title: block.title,
        text: block.text,
        titleSize: block.title_size,
        textSize: block.text_size,
        titleFont: block.title_font || block.font,
        titleBold: block.title_bold ?? block.bold,
        titleLayout: block.title_layout || block.layout,
        titleColor: block.title_color || block.color,
        titleToggle: block.title_toggle ?? true,
        textFont: block.text_font || block.font,
        textBold: block.text_bold ?? block.bold,
        textLayout: block.text_layout || block.layout,
        textColor: block.text_color || block.color,
        backgroundColor: block.background_color || '#ffffff',
        backgroundOpacity: block.background_opacity ?? 1,
        backgroundToggleWidth: !!block.background_toggle_width,
        backgroundCorners: block.background_corners || 'Round',
        backgroundText: block.background_text || '',
        backgroundTextToggle: block.background_text_toggle ?? true,
        backgroundTextSize: block.background_text_size || 16,
        backgroundTextFont: block.background_text_font || block.font || 'Inter',
        backgroundTextBold: block.background_text_bold ?? block.bold ?? false,
        backgroundTextLayout: block.background_text_layout || block.layout || 'left',
        backgroundTextColor: block.background_text_color || block.color || '#0f172a',
        font: block.font,
        bold: block.bold,
        layout: block.layout,
        color: block.color,
        mediaUrl: getLessonPageBlockMediaUrl(block),
        width: block.width,
        height: block.height,
        left: block.left_position,
        top: block.top_position,
        order: block.order,
        questions,
        quizBlock,
    };
};

// A lesson is complete once every quiz block the user has attempted is marked completed and
// every video block has been watched to the end. A lesson with no quiz/video blocks is
// vacuously complete as soon as it exists (nothing else gates it).
//
// Every quiz/video block in the lesson is checked here, including ones the admin has only
// staged locally and not saved yet (block.id / block.quizBlock.id still null at that point) --
// those simply can't have a matching attempt/progress row (which is keyed by the real backend
// id) and so correctly count as not-done. Filtering them out instead (as this used to do) meant
// adding a new required quiz/video block to an already-completed lesson silently left it marked
// completed until the chapter was explicitly saved.
const isLessonBlocksCompleted = (lesson, userId, userQuizAttempts, userLessonVideoProgresses) => {
    const blocks = (lesson.pages || []).flatMap((page) => page.blocks || []);
    const videoBlocks = blocks.filter((b) => b.type === 'video');
    const quizBlocks = blocks.filter((b) => b.type === 'quiz');

    const videosDone = videoBlocks.every((b) =>
        b.id != null && userLessonVideoProgresses.find((p) => p.user === userId && p.lesson_page_block === b.id)?.video_completed === true
    );
    const quizzesDone = quizBlocks.every((b) =>
        b.quizBlock?.id != null && userQuizAttempts.find((a) => a.user === userId && a.quiz_block === b.quizBlock.id)?.completed === true
    );

    return videosDone && quizzesDone;
};

const getStylePrefixForField = (field) => {
    if (field === 'title') return 'title';
    if (field === 'background_text') return 'backgroundText';
    return 'text';
};

const getBlockStyleFieldName = (field, styleKey) => {
    const prefix = getStylePrefixForField(field);

    if (styleKey === 'font') return `${prefix}Font`;
    if (styleKey === 'bold') return `${prefix}Bold`;
    if (styleKey === 'layout') return `${prefix}Layout`;
    if (styleKey === 'color') return `${prefix}Color`;

    return styleKey;
};

const renderInlineBoldText = (value = '') => {
    const segments = String(value).split('/b');
    return segments.map((segment, index) => {
        const isBold = index % 2 === 1;
        if (!segment) return null;
        return isBold ? <strong key={`${index}-${segment}`}>{segment}</strong> : <React.Fragment key={`${index}-${segment}`}>{segment}</React.Fragment>;
    });
};

const hexToRgba = (hexColor = '#ffffff', alpha = 1) => {
    if (hexColor.startsWith('var(')) return hexColor;
    const safeHex = String(hexColor).replace('#', '');
    const expandedHex = safeHex.length === 3
        ? safeHex.split('').map((char) => char + char).join('')
        : safeHex.padEnd(6, 'f').slice(0, 6);
    const red = Number.parseInt(expandedHex.slice(0, 2), 16) || 255;
    const green = Number.parseInt(expandedHex.slice(2, 4), 16) || 255;
    const blue = Number.parseInt(expandedHex.slice(4, 6), 16) || 255;
    return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
};

function Chapter() {

    const { t } = useTranslation();
    const { levelName, chapterTitle } = useParams();
    const navigate = useNavigate();
    
    const userIsAuthenticated = isAuthenticated();
    const currentUser = getCurrentUser();
    // An is_staff admin without their own ChapterAdminPermission row still gets full access as
    // long as no permission rows exist at all for this chapter (matching the "Leave empty for
    // all admins" semantics from ModalLevel.jsx's admin access UI); once at least one row
    // exists, only the listed admins qualify (see the fetch effect below, near chapterData).
    const [chapterAdminPermissions, setChapterAdminPermissions] = useState([]);
    const userIsStaff = currentUser?.is_staff;
    const isStaff = () => {
        return userIsStaff && (
        chapterAdminPermissions.length === 0 ||
        chapterAdminPermissions.some((permission) => Number(permission.user?.id ?? permission.user) === Number(currentUser?.id))
    );
    } 

    // Editor access for the level this chapter belongs to: admins/superadmins can open a chapter
    // under a level whose category isn't aimed at "admins", but only to read it (see
    // checkAdminLevelEditorPermission). Starts view-only and flips off once the check clears, so
    // the toolbar/handles never flash on for someone who isn't allowed to edit.
    const [levelViewOnly, setLevelViewOnly] = useState(true);
    useEffect(() => {
        if (!levelName || !currentUser?.id) return;
        let cancelled = false;
        checkAdminLevelEditorPermission(levelName, currentUser.id).then((canEdit) => {
            if (!cancelled) setLevelViewOnly(!canEdit);
        }).catch((error) => {
            console.error('Error checking level editor permission:', error);
            if (!cancelled) setLevelViewOnly(true);
        });
        return () => { cancelled = true; };
    }, [levelName, currentUser?.id]);
    

    // Progresses
    const [userLessonProgresses, setUserLessonProgresses] = useState([]);
    const [userLessonVideoProgresses, setUserLessonVideoProgresses] = useState([]);
    const [userQuizAttempts, setUserQuizAttempts] = useState([]);
    const [userTopicContentProgresses, setUserTopicContentProgresses] = useState([]);
    const lessonProgressSyncInProgressRef = useRef(false);

    // The Navbar toggle's own state; `editorMode` below is what the page actually renders from,
    // and stays off for the whole visit whenever the level is read-only for this user.
    const [editorModeEnabled, setEditorModeEnabled] = useState(() => getPersistedEditorMode());
    const editorMode = editorModeEnabled && !levelViewOnly;
    const [isToolbarCollapsed, setIsToolbarCollapsed] = useState(false);
    const [sessionHistoryMeta, setSessionHistoryMeta] = useState({ index: -1, length: 0 });
    const [textToolbarState, setTextToolbarState] = useState({
        show: false,
        lessonIdx: null,
        pageIdx: null,
        blkIdx: null,
        field: null,
        el: null,
        font: 'Inter',
        bold: false,
        layout: 'left',
        color: '#0f172a',
        size: 16,
        position: { top: 0, left: 0 },
    });
    const [shapeToolbarState, setShapeToolbarState] = useState({
        show: false,
        lessonIdx: null,
        pageIdx: null,
        blkIdx: null,
        el: null,
        color: '#ffffff',
        opacity: 1,
        toggleWidth: false,
        corners: 'Round',
        titleToggle: true,
        textToggle: true,
        position: { top: 0, left: 0 },
    });
    const updateEditorMode = (mode) => {
        setEditorModeEnabled(mode);
    }

    // Refs
    const canvasWrapperRef = useRef(null);
    const videoInputRef = useRef(null);
    const imageInputRef = useRef(null);

    // Chapter data state
    const [chapterData, setChapterData] = useState({
        id: null,
        title: 'Loading...',
        topicContent: null,
        lessons: []
    });

    useEffect(() => {
        let cancelled = false;
        if (!currentUser?.is_staff || !chapterData.id) {
            setChapterAdminPermissions([]);
            return;
        }
        getChapterAdminPermissions().then((permissions) => {
            if (cancelled) return;
            const permissionsForChapter = Array.isArray(permissions)
                ? permissions.filter((permission) => Number(permission.chapter?.id ?? permission.chapter) === Number(chapterData.id))
                : [];
            setChapterAdminPermissions(permissionsForChapter);
        }).catch((error) => {
            console.error('Error loading chapter admin permissions:', error);
            if (!cancelled) setChapterAdminPermissions([]);
        });
        return () => { cancelled = true; };
    }, [currentUser?.is_staff, chapterData.id]);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Manual-save feedback: a corner spinner while the request is in flight, then a
    // toast with the outcome (replaces the blocking window.alert this used to use).
    // The ref mirrors the state purely so saveChapterSession's re-entrancy guard can
    // read it without taking the state as a dependency -- saveChapterSession is
    // memoized and re-creating it on every save/finish would churn the Ctrl+S listener.
    const [isSavingChapter, setIsSavingChapter] = useState(false);
    const isSavingChapterRef = useRef(false);
    const { toast, showToast } = useToast();

    const setSavingChapter = useCallback((value) => {
        isSavingChapterRef.current = value;
        setIsSavingChapter(value);
    }, []);

    // Stage scaling is per-page and self-measuring -- see LessonPageCanvas at the top of this file.

    // UI State
    const [activeLessonIdx, setActiveLessonIdx] = useState(0);
    const [currentSnappedPageIdx, setCurrentSnappedPageIdx] = useState(0);
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [isAnimating, setIsAnimating] = useState(false);
    const [animationClass, setAnimationClass] = useState('anim-initial');

    // Drag and resize state
    const [dragState, setDragState] = useState(null);
    const [resizeState, setResizeState] = useState(null);
    
    // Element drag state (for header elements like hero card, part title, etc.)
    const [elementDragState, setElementDragState] = useState(null);
    const [headerResizeState, setHeaderResizeState] = useState(null);

    // Multi-block selection state (marquee select, ctrl+click, multi-drag, clipboard, context menu)
    const [selectedBlocks, setSelectedBlocks] = useState([]); // [{ lessonIdx, pageIdx, blkIdx }]
    const [marqueeState, setMarqueeState] = useState(null);
    const [multiDragState, setMultiDragState] = useState(null);
    const [blockContextMenu, setBlockContextMenu] = useState(null);
    const [blockCreateMenu, setBlockCreateMenu] = useState(null); // { x, y, lessonIdx, pageIdx, left, top }
    const clipboardBlocksRef = useRef([]);

    // Toolbar-pill -> canvas drag-and-drop (create a block of the dragged type at the drop
    // point). `dragOverPageKey` is only for the drop-target highlight, keyed by `${lessonIdx}-${pageIdx}`.
    const [dragOverPageKey, setDragOverPageKey] = useState(null);

    // Mirrored up from TemplateManager (which owns the slideshow) so the canvas can drop its
    // editing-oriented scroll behaviour while presenting -- see the clamp effect and
    // handleSlideshowGoTo below.
    const [isSlideshowActive, setIsSlideshowActive] = useState(false);

    // ---------------------------------------------------------------- slideshow pinch/wheel zoom
    // Applied to the whole presented .lesson-section (background layer included, so the slide
    // zooms as one picture) rather than to .blocks-stage, which only ever carries the fit scale.
    const [slideZoom, setSlideZoom] = useState({ scale: 1, x: 0, y: 0 });
    const slideGestureRef = useRef(null);

    // A fresh slide always starts un-zoomed; carrying a zoom across a slide change leaves the
    // next one showing an arbitrary corner of itself.
    useEffect(() => {
        setSlideZoom({ scale: 1, x: 0, y: 0 });
    }, [isSlideshowActive, activeLessonIdx, currentSnappedPageIdx]);

    useEffect(() => {
        if (!isSlideshowActive) return undefined;
        const wrapper = canvasWrapperRef.current;
        if (!wrapper) return undefined;

        const MIN_ZOOM = 1; // the fit scale already shows the whole slide; below it is dead space
        const MAX_ZOOM = 5;
        const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

        // Pan is clamped to the overhang the zoom actually creates, so the slide can never be
        // flung off-screen and always springs back to centred at 1x.
        const clampPan = (scale, x, y) => {
            const rect = wrapper.getBoundingClientRect();
            const maxX = Math.max(0, (rect.width * scale - rect.width) / 2);
            const maxY = Math.max(0, (rect.height * scale - rect.height) / 2);
            return { x: clamp(x, -maxX, maxX), y: clamp(y, -maxY, maxY) };
        };

        // Keep the point under the fingers (or cursor) pinned while the scale changes.
        // With transform-origin at the centre and `translate(pan) scale(z)`, a point p maps to
        // centre + pan + z*p, so holding the focal point still gives:
        //   pan' = u - (z'/z) * (u - pan),  where u = focal - centre.
        const zoomAround = (previous, nextScale, focalX, focalY) => {
            const rect = wrapper.getBoundingClientRect();
            const uX = focalX - (rect.left + rect.width / 2);
            const uY = focalY - (rect.top + rect.height / 2);
            const ratio = nextScale / previous.scale;
            const panned = {
                x: uX - ratio * (uX - previous.x),
                y: uY - ratio * (uY - previous.y),
            };
            // Snapping straight back to centre at 1x avoids a slide that is fully visible but
            // sitting off to one side.
            if (nextScale <= MIN_ZOOM + 0.001) return { scale: MIN_ZOOM, x: 0, y: 0 };
            return { scale: nextScale, ...clampPan(nextScale, panned.x, panned.y) };
        };

        const touchDistance = (a, b) => Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);

        const handleTouchStart = (e) => {
            if (e.touches.length === 2) {
                const [a, b] = [e.touches[0], e.touches[1]];
                slideGestureRef.current = {
                    mode: 'pinch',
                    startDistance: touchDistance(a, b) || 1,
                    startScale: slideZoom.scale,
                };
                e.preventDefault();
            } else if (e.touches.length === 1 && slideZoom.scale > MIN_ZOOM) {
                // One finger only pans once zoomed in; at 1x it is left alone so it can't
                // interfere with anything else the slide surface wants to do with a tap.
                slideGestureRef.current = {
                    mode: 'pan',
                    startX: e.touches[0].clientX,
                    startY: e.touches[0].clientY,
                    startPanX: slideZoom.x,
                    startPanY: slideZoom.y,
                };
            } else {
                slideGestureRef.current = null;
            }
        };

        const handleTouchMove = (e) => {
            const gesture = slideGestureRef.current;
            if (!gesture) return;

            if (gesture.mode === 'pinch' && e.touches.length === 2) {
                const [a, b] = [e.touches[0], e.touches[1]];
                const nextScale = clamp(
                    gesture.startScale * (touchDistance(a, b) / gesture.startDistance),
                    MIN_ZOOM, MAX_ZOOM,
                );
                const focalX = (a.clientX + b.clientX) / 2;
                const focalY = (a.clientY + b.clientY) / 2;
                setSlideZoom((previous) => zoomAround(previous, nextScale, focalX, focalY));
                e.preventDefault();
            } else if (gesture.mode === 'pan' && e.touches.length === 1) {
                const dx = e.touches[0].clientX - gesture.startX;
                const dy = e.touches[0].clientY - gesture.startY;
                setSlideZoom((previous) => ({
                    ...previous,
                    ...clampPan(previous.scale, gesture.startPanX + dx, gesture.startPanY + dy),
                }));
                e.preventDefault();
            }
        };

        const handleTouchEnd = (e) => {
            if (e.touches.length === 0) slideGestureRef.current = null;
        };

        // Trackpad pinch and Ctrl+wheel both arrive as a wheel event with ctrlKey set.
        const handleWheel = (e) => {
            if (!e.ctrlKey && !e.metaKey) return;
            e.preventDefault();
            setSlideZoom((previous) => {
                const nextScale = clamp(previous.scale * Math.exp(-e.deltaY / 200), MIN_ZOOM, MAX_ZOOM);
                return zoomAround(previous, nextScale, e.clientX, e.clientY);
            });
        };

        // Native listeners, not React props: React registers touchmove/wheel on its root as
        // PASSIVE, where preventDefault is a no-op and the browser's own page zoom wins.
        const options = { passive: false };
        wrapper.addEventListener('touchstart', handleTouchStart, options);
        wrapper.addEventListener('touchmove', handleTouchMove, options);
        wrapper.addEventListener('touchend', handleTouchEnd);
        wrapper.addEventListener('touchcancel', handleTouchEnd);
        wrapper.addEventListener('wheel', handleWheel, options);
        return () => {
            wrapper.removeEventListener('touchstart', handleTouchStart);
            wrapper.removeEventListener('touchmove', handleTouchMove);
            wrapper.removeEventListener('touchend', handleTouchEnd);
            wrapper.removeEventListener('touchcancel', handleTouchEnd);
            wrapper.removeEventListener('wheel', handleWheel);
            slideGestureRef.current = null;
        };
        // slideZoom is read at gesture START only (to seed startScale/startPan), so re-binding on
        // each change is what keeps a new gesture picking up where the last one left off.
    }, [isSlideshowActive, slideZoom.scale, slideZoom.x, slideZoom.y]);

    // Tracks the live mouse position so pasting with no menu open (e.g. Ctrl+V without a prior
    // right-click) can still drop blocks at the cursor instead of duplicating in place.
    const lastCursorPositionRef = useRef({ x: 0, y: 0 });
    useEffect(() => {
        const handleMouseMove = (e) => {
            lastCursorPositionRef.current = { x: e.clientX, y: e.clientY };
        };
        document.addEventListener('mousemove', handleMouseMove);
        return () => document.removeEventListener('mousemove', handleMouseMove);
    }, []);

    // Snap guide refs
    const guideCenterRef = useRef(null);
    const guideLeftRef = useRef(null);
    const guideRightRef = useRef(null);
    const guideAlignHRef = useRef(null);
    const guideAlignVRef = useRef(null);

    // Modal state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingBlock, setEditingBlock] = useState(null);
    const [modalHeading, setModalHeading] = useState('');
    const [modalBody, setModalBody] = useState('');
    const [modalHeadingSize, setModalHeadingSize] = useState(24);
    const [modalBodySize, setModalBodySize] = useState(16);
    const [modalBlockAction, setModalBlockAction] = useState('Add');
    const [modalType, setModalType] = useState('card');
    const [modalChanged, setModalChanged] = useState(false);
    const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
    const [openUseThumbnailModal, setOpenUseThumbnailModal] = useState(false);

    const [lastFetchTime, setLastFetchTime] = useState(Date.now());

    const currentSessionLessonsRef = useRef([]);
    const currentSessionPagesRef = useRef([]);
    const currentSessionBlocksRef = useRef([]);
    const currentSessionBlockQuizesRef = useRef([]);
    const currentSessionBlockQuizQuestionRef = useRef([]);
    const currentSessionBlockQuizQuestionChoicesRef = useRef([]);
    const sessionHistoryRef = useRef([]);
    const sessionHistoryIndexRef = useRef(-1);
    const sessionBaselineRef = useRef(null);
    const hasInitializedSessionRef = useRef(false);
    const isRestoringSessionRef = useRef(false);
    const isSessionHistoryPausedRef = useRef(false);

    const hideTextToolbar = useCallback(() => {
        setTextToolbarState((previous) => (previous.show ? { ...previous, show: false } : previous));
    }, []);

    const hideShapeToolbar = useCallback(() => {
        setShapeToolbarState((previous) => (previous.show ? { ...previous, show: false } : previous));
    }, []);

    const showTextToolbar = useCallback((lessonIdx, pageIdx, blkIdx, field, el) => {
        if (!isStaff() || !editorMode || !el) return;

        const block = chapterData.lessons?.[lessonIdx]?.pages?.[pageIdx]?.blocks?.[blkIdx];
        const stylePrefix = getStylePrefixForField(field);

        const rect = el.getBoundingClientRect();
        hideShapeToolbar();
        setTextToolbarState({
            show: true,
            lessonIdx,
            pageIdx,
            blkIdx,
            field,
            el,
            font: block?.[`${stylePrefix}Font`] || block?.font || 'Inter',
            bold: !!(block?.[`${stylePrefix}Bold`] ?? block?.bold),
            layout: block?.[`${stylePrefix}Layout`] || block?.layout || 'left',
            color: block?.[`${stylePrefix}Color`] || block?.color || '#0f172a',
            size: coerceInteger(window.getComputedStyle(el).fontSize, field === 'title' ? 24 : 16),
            position: {
                top: Math.max(12, rect.top - 66),
                left: Math.max(12, rect.left),
            },
        });
    }, [chapterData.lessons, editorMode, hideShapeToolbar, isStaff]);

    const showShapeToolbar = useCallback((lessonIdx, pageIdx, blkIdx, el) => {
        if (!isStaff() || !editorMode || !el) return;

        const block = chapterData.lessons?.[lessonIdx]?.pages?.[pageIdx]?.blocks?.[blkIdx];
        const rect = el.getBoundingClientRect();
        hideTextToolbar();
        if (block.type === 'background') {
            setShapeToolbarState({
                show: true,
                lessonIdx,
                pageIdx,
                blkIdx,
                el,
                color: block?.backgroundColor || block?.background_color || '#ffffff',
                opacity: block?.backgroundOpacity ?? block?.background_opacity ?? 1,
                toggleWidth: !!(block?.backgroundToggleWidth ?? block?.background_toggle_width),
                corners: block?.backgroundCorners || block?.background_corners || 'Round',
                textToggle: block?.backgroundTextToggle ?? block?.background_text_toggle ?? true,
                position: {
                    top: Math.max(12, rect.top - 66),
                    left: Math.max(12, rect.left),
                },
            });
        } else if (block.type === 'card') {
            setShapeToolbarState({
                show: true,
                lessonIdx,
                pageIdx,
                blkIdx,
                el,
                color: block?.backgroundColor || '#ffffff',
                opacity: block?.backgroundOpacity ?? 1,
                titleToggle: block?.titleToggle ?? true,
                position: {
                    top: Math.max(12, rect.top - 66),
                    left: Math.max(12, rect.left),
                },
            });
        }
    }, [chapterData.lessons, editorMode, hideTextToolbar, isStaff]);

    const logChapterChange = useCallback(async (actionTitle, additionalData = {}) => {
        if (!chapterData.id) return;

        const snapshot = JSON.parse(JSON.stringify(chapterData));
        const lastEntry = sessionHistoryRef.current[sessionHistoryIndexRef.current];
        if (lastEntry && JSON.stringify(lastEntry) !== JSON.stringify(snapshot)) {
            sessionHistoryRef.current = sessionHistoryRef.current.slice(0, sessionHistoryIndexRef.current + 1);
        }

        sessionHistoryRef.current.push(snapshot);
        sessionHistoryIndexRef.current = sessionHistoryRef.current.length - 1;
        setSessionHistoryMeta({
            index: sessionHistoryIndexRef.current,
            length: sessionHistoryRef.current.length,
        });

        await createChapterHistoryLog({
            chapter: chapterData.id,
            log_title: actionTitle,
            log_data: additionalData,
        });
    }, [chapterData]);

    const applyTextFormat = useCallback(async (styleKey, styleValue) => {
        const { lessonIdx, pageIdx, blkIdx, field, el } = textToolbarState;
        if (lessonIdx === null || pageIdx === null || blkIdx === null || !field || !el) return;

        const block = chapterData.lessons?.[lessonIdx]?.pages?.[pageIdx]?.blocks?.[blkIdx];
        const blockStyleField = getBlockStyleFieldName(field, styleKey);
        const previousValue = block?.[blockStyleField];

        updateBlockData(lessonIdx, pageIdx, blkIdx, blockStyleField, styleValue);

        if (styleKey === 'bold') {
            el.style.fontWeight = styleValue ? '700' : '400';
        } else if (styleKey === 'font') {
            el.style.fontFamily = styleValue;
        } else if (styleKey === 'layout') {
            el.style.textAlign = styleValue;
        } else if (styleKey === 'color') {
            el.style.color = styleValue;
        }
        setTextToolbarState((previous) => ({
            ...previous,
            font: styleKey === 'font' ? styleValue : previous.font,
            bold: styleKey === 'bold' ? !!styleValue : previous.bold,
            layout: styleKey === 'layout' ? styleValue : previous.layout,
            color: styleKey === 'color' ? styleValue : previous.color,
        }));
        if (previousValue !== styleValue) {
            await logChapterChange(`Updated ${field} formatting`, {
                block_type: normalizeBlockType(block?.type) || field,
                field: blockStyleField,
                old_value: previousValue,
                new_value: styleValue,
            });
        }
    }, [chapterData.lessons, logChapterChange, textToolbarState]);

    const changeTextFontSize = useCallback(async (delta) => {
        const { lessonIdx, pageIdx, blkIdx, field, el } = textToolbarState;
        if (lessonIdx === null || pageIdx === null || blkIdx === null || !field || !el) return;

        const styleKey = field === 'title' ? 'titleSize' : field === 'background_text' ? 'backgroundTextSize' : 'textSize';
        const currentSize = coerceInteger(window.getComputedStyle(el).fontSize, textToolbarState.size || 16);
        const nextSize = Math.max(10, currentSize + delta);
        const block = chapterData.lessons?.[lessonIdx]?.pages?.[pageIdx]?.blocks?.[blkIdx];
        const previousValue = block?.[styleKey];
        updateBlockData(lessonIdx, pageIdx, blkIdx, styleKey, nextSize);
        el.style.fontSize = `${nextSize}px`;
        setTextToolbarState((previous) => ({ ...previous, size: nextSize }));
        if (previousValue !== nextSize) {
            await logChapterChange(`Updated ${field} font size`, {
                block_type: normalizeBlockType(block?.type) || field,
                field: styleKey,
                old_value: previousValue,
                new_value: nextSize,
            });
        }
    }, [chapterData.lessons, logChapterChange, textToolbarState]);

    const changeTextFontFamily = useCallback((font) => {
        void applyTextFormat('font', font);
    }, [applyTextFormat]);

    const changeTextAlignment = useCallback((layout) => {
        void applyTextFormat('layout', layout);
    }, [applyTextFormat]);

    const changeTextColor = useCallback((color) => {
        void applyTextFormat('color', color);
    }, [applyTextFormat]);

    const toggleTextBold = useCallback(() => {
        const currentBold = !!textToolbarState.bold;
        void applyTextFormat('bold', !currentBold);
    }, [applyTextFormat, textToolbarState.bold]);

    const applyBackgroundFormat = useCallback(async (styleKey, styleValue) => {
        const { lessonIdx, pageIdx, blkIdx, el } = shapeToolbarState;
        if (lessonIdx === null || pageIdx === null || blkIdx === null || !el) return;

        const block = chapterData.lessons?.[lessonIdx]?.pages?.[pageIdx]?.blocks?.[blkIdx];
        const fieldMap = {
            color: 'backgroundColor',
            opacity: 'backgroundOpacity',
            toggleWidth: 'backgroundToggleWidth',
            corners: 'backgroundCorners',
            titleToggle: 'titleToggle',
            textToggle: 'backgroundTextToggle',
        };
        const blockField = fieldMap[styleKey];
        if (!blockField) return;

        const previousValue = block?.[blockField];
        updateBlockData(lessonIdx, pageIdx, blkIdx, blockField, styleValue);

        if (styleKey === 'color') {
            el.style.backgroundColor = hexToRgba(styleValue, block?.backgroundOpacity ?? 1);
        } else if (styleKey === 'opacity') {
            el.style.backgroundColor = hexToRgba(block?.backgroundColor || '#ffffff', styleValue);
        } else if (styleKey === 'toggleWidth') {
            // Width/left/transform for full-bleed mode are derived reactively from
            // backgroundToggleWidth in the block's render style, so no direct DOM
            // mutation here - that used to leave stale inline styles (e.g. a leftover
            // transform) once the state-driven render ran, causing drag/snap to use
            // the wrong effective position.
            if (styleValue) {
                updateBlockData(lessonIdx, pageIdx, blkIdx, 'backgroundCorners', 'Square');
                setShapeToolbarState((previous) => ({ ...previous, corners: 'Square' }));
            }
        } else if (styleKey === 'corners') {
            el.style.borderRadius = styleValue === 'Square' ? '0px' : '28px';
        }

        setShapeToolbarState((previous) => ({
            ...previous,
            color: styleKey === 'color' ? styleValue : previous.color,
            opacity: styleKey === 'opacity' ? styleValue : previous.opacity,
            toggleWidth: styleKey === 'toggleWidth' ? !!styleValue : previous.toggleWidth,
            corners: styleKey === 'corners' ? styleValue : previous.corners,
            titleToggle: styleKey === 'titleToggle' ? !!styleValue : previous.titleToggle,
            textToggle: styleKey === 'textToggle' ? !!styleValue : previous.textToggle,
        }));

        if (previousValue !== styleValue) {
            const actionLabel = block?.type === 'card' ? 'Updated card formatting' : 'Updated background formatting';
            await logChapterChange(actionLabel, {
                block_type: normalizeBlockType(block?.type) || 'background',
                field: blockField,
                old_value: previousValue,
                new_value: styleValue,
            });
        }
    }, [chapterData.lessons, logChapterChange, shapeToolbarState]);

    const changeBackgroundColor = useCallback((color) => {
        void applyBackgroundFormat('color', color);
    }, [applyBackgroundFormat]);

    const changeBackgroundOpacity = useCallback((opacity) => {
        const nextOpacity = Math.max(0, Math.min(1, Number.parseFloat(opacity) || 0));
        void applyBackgroundFormat('opacity', nextOpacity);
    }, [applyBackgroundFormat]);

    const toggleBackgroundWidth = useCallback(() => {
        void applyBackgroundFormat('toggleWidth', !shapeToolbarState.toggleWidth);
    }, [applyBackgroundFormat, shapeToolbarState.toggleWidth]);

    const changeBackgroundCorners = useCallback((corners) => {
        void applyBackgroundFormat('corners', corners);
    }, [applyBackgroundFormat]);

    const toggleTitle = useCallback(() => {
        void applyBackgroundFormat('titleToggle', !shapeToolbarState.titleToggle);
    }, [applyBackgroundFormat, shapeToolbarState.titleToggle]);

    const toggleBackgroundText = useCallback(() => {
        void applyBackgroundFormat('textToggle', !shapeToolbarState.textToggle);
    }, [applyBackgroundFormat, shapeToolbarState.textToggle]);

    useEffect(() => {
        const handlePointerDown = (event) => {
            if (!textToolbarState.show) return;
            if (event.target.closest('.text-format-toolbar') || event.target.closest('.textual-editable')) return;
            hideTextToolbar();
        };

        document.addEventListener('mousedown', handlePointerDown);
        return () => document.removeEventListener('mousedown', handlePointerDown);
    }, [hideTextToolbar, textToolbarState.show]);

    useEffect(() => {
        const handlePointerDown = (event) => {
            if (!shapeToolbarState.show) return;
            if (event.target.closest('.shape-format-toolbar') || event.target.closest('.background-block')) return;
            hideShapeToolbar();
        };

        document.addEventListener('mousedown', handlePointerDown);
        return () => document.removeEventListener('mousedown', handlePointerDown);
    }, [hideShapeToolbar, shapeToolbarState.show]);

    const cloneSessionData = useCallback((value) => JSON.parse(JSON.stringify(value)), []);

    const syncCurrentSessionLists = useCallback((snapshot) => {
        const lessons = snapshot?.lessons || [];
        const pages = [];
        const blocks = [];
        const quizzes = [];
        const questions = [];
        const choices = [];

        lessons.forEach((lesson) => {
            pages.push(...(lesson.pages || []));
            (lesson.pages || []).forEach((page) => {
                (page.blocks || []).forEach((block) => {
                    blocks.push(block);
                    if (block.type === 'quiz' && block.quizBlock) {
                        quizzes.push(block.quizBlock);
                        (block.quizBlock.questions || []).forEach((quizQuestion) => {
                            questions.push(quizQuestion);
                            (quizQuestion.choices || []).forEach((choice) => choices.push(choice));
                        });
                    }
                });
            });
        });

        currentSessionLessonsRef.current = lessons;
        currentSessionPagesRef.current = pages;
        currentSessionBlocksRef.current = blocks;
        currentSessionBlockQuizesRef.current = quizzes;
        currentSessionBlockQuizQuestionRef.current = questions;
        currentSessionBlockQuizQuestionChoicesRef.current = choices;
    }, []);

    const captureSessionSnapshot = useCallback((snapshot) => {
        const clonedSnapshot = cloneSessionData(snapshot);
        sessionHistoryRef.current = sessionHistoryRef.current.slice(0, sessionHistoryIndexRef.current + 1);
        sessionHistoryRef.current.push(clonedSnapshot);
        sessionHistoryIndexRef.current = sessionHistoryRef.current.length - 1;
        setSessionHistoryMeta({
            index: sessionHistoryIndexRef.current,
            length: sessionHistoryRef.current.length,
        });
        syncCurrentSessionLists(clonedSnapshot);
        if (!sessionBaselineRef.current) {
            sessionBaselineRef.current = cloneSessionData(clonedSnapshot);
        }
    }, [cloneSessionData, syncCurrentSessionLists]);

    const resetSessionHistory = useCallback((snapshot) => {
        const clonedSnapshot = cloneSessionData(snapshot);
        sessionHistoryRef.current = [clonedSnapshot];
        sessionHistoryIndexRef.current = 0;
        sessionBaselineRef.current = cloneSessionData(clonedSnapshot);
        hasInitializedSessionRef.current = true;
        setSessionHistoryMeta({ index: 0, length: 1 });
        syncCurrentSessionLists(clonedSnapshot);
    }, [cloneSessionData, syncCurrentSessionLists]);

    useEffect(() => {
        if (loading || error || !chapterData?.lessons?.length) return;

        syncCurrentSessionLists(chapterData);

        if (isSessionHistoryPausedRef.current) return;

        if (!hasInitializedSessionRef.current) {
            hasInitializedSessionRef.current = true;
            sessionHistoryRef.current = [];
            sessionHistoryIndexRef.current = -1;
            sessionBaselineRef.current = cloneSessionData(chapterData);
            captureSessionSnapshot(chapterData);
            return;
        }

        if (isRestoringSessionRef.current) {
            isRestoringSessionRef.current = false;
            return;
        }

        const snapshot = cloneSessionData(chapterData);
        sessionHistoryRef.current = sessionHistoryRef.current.slice(0, sessionHistoryIndexRef.current + 1);
        sessionHistoryRef.current.push(snapshot);
        sessionHistoryIndexRef.current = sessionHistoryRef.current.length - 1;
        setSessionHistoryMeta({
            index: sessionHistoryIndexRef.current,
            length: sessionHistoryRef.current.length,
        });
    }, [chapterData, loading, error, cloneSessionData, captureSessionSnapshot, syncCurrentSessionLists]);

    useEffect(() => {
        if (dragState || multiDragState) {
            isSessionHistoryPausedRef.current = true;
            return;
        }

        if (isSessionHistoryPausedRef.current) {
            isSessionHistoryPausedRef.current = false;
            captureSessionSnapshot(chapterData);
        }
    }, [dragState, multiDragState, chapterData, captureSessionSnapshot]);

    // Warn the admin before leaving/refreshing the page if there are unsaved editor changes.
    // sessionHistoryIndexRef sits at 0 exactly when the session matches the last saved baseline
    // (resetSessionHistory/saveChapterSession both collapse it back to index 0), so any higher
    // index means an edit has been made since the last save.
    useEffect(() => {
        if (!isStaff()) return;

        const handleBeforeUnload = (e) => {
            if (sessionHistoryIndexRef.current > 0) {
                e.preventDefault();
                e.returnValue = '';
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [isStaff]);

    // Fetch chapter data on mount and when modalChanged
    const fetchChapterData = async () => {
        try {
            // Get the chapter by title (server-side filtered instead of fetching every chapter
            // on the platform) -- title is only unique per topic_content, not globally, so this
            // keeps the same "first match" semantics the previous client-side .find() had.
            const chapters = await getChaptersByTitle(chapterTitle);
            const chapter = chapters[0];
            if (!chapter) {
                setError('Chapter not found');
                setLoading(false);
                return;
            }
            // Get lessons for this chapter, and this chapter's quiz questions/choices -- all
            // server-side scoped instead of fetching every lesson/quiz question/quiz choice on
            // the whole platform and filtering in memory.
            const [chapterLessons, quizQuestions, quizChoices, userQuizAttemptsData] = await Promise.all([
                getLessonsByChapter(chapter.id),
                getQuizQuestionsByChapter(chapter.id),
                getQuizChoicesByChapter(chapter.id),
                getUserQuizAttemptsByChapter(chapter.id),
            ]);

            // Get pages for every lesson in one batched request instead of one request per lesson.
            const pagesByLessonId = new Map();
            const allPages = await getLessonPagesByIds(chapterLessons.map(l => l.id));
            allPages.forEach(page => {
                if (!pagesByLessonId.has(page.lesson)) pagesByLessonId.set(page.lesson, []);
                pagesByLessonId.get(page.lesson).push(page);
            });
            const lessonsWithPages = chapterLessons.map((lesson) => {
                const pagesWithBlocks = (pagesByLessonId.get(lesson.id) || []).map((page) => ({
                    ...page,
                    blocks: (page.blocks || []).map((block) => mapApiBlockToLocal(block, quizQuestions, quizChoices)),
                }));
                return { ...lesson, pages: pagesWithBlocks };
            });
            const [userLessonProgressesData, userLessonVideoProgressesData, userTopicContentProgressesData] = await Promise.all([
                getUserLessonProgresses(),
                getUserLessonVideoProgresses(),
                getUserTopicContentProgresses(),
            ]);

            // Commit chapterData together with the progress arrays in one batch. Setting
            // chapterData first (on its own) would let the lesson-completion sync effect below
            // fire with chapterData.lessons populated but the progress arrays still stale/empty
            // from a previous render -- which was computing completion/unlock state from
            // incomplete data (e.g. a second lesson reading a still-empty userLessonProgresses
            // and concluding its prerequisite doesn't exist, so nothing blocks it).
            setChapterData({
                id: chapter.id,
                title: chapter.title,
                topicContent: chapter.topic_content,
                lessons: lessonsWithPages,
            });
            setUserLessonProgresses(userLessonProgressesData);
            setUserLessonVideoProgresses(userLessonVideoProgressesData);
            setUserQuizAttempts(userQuizAttemptsData);
            setUserTopicContentProgresses(userTopicContentProgressesData);

            setLastFetchTime(Date.now());
            setLoading(false);
        } catch (err) {
            setError('Failed to load chapter');
            setLoading(false);
        }
    };
    useEffect(() => {
        if (!userIsAuthenticated) {
            navigate('/');
            return;
        }

        fetchChapterData();
        if (modalChanged) setModalChanged(false);
    }, [chapterTitle, userIsAuthenticated, navigate, modalChanged]);

    // Guard direct/typed/bookmarked visits to a chapter whose level is locked -- the normal
    // dashboard click flow already hides locked levels behind a lock icon, but that's purely a
    // rendering choice in DashboardGrid.jsx and doesn't stop someone from navigating straight to
    // the URL. Editor mode bypasses the lock, matching the level-card's own bypass. This is a
    // client-side redirect only (no backend endpoint enforces this), same as the lock icon it
    // mirrors.
    useEffect(() => {
        if (!levelName || !currentUser?.id || isAdmin()) return;
        let cancelled = false;
        isLevelNameLockedForUser(levelName, currentUser.id).then(locked => {
            if (!cancelled && locked) navigate('/dashboard');
        }).catch(error => {
            console.error('Error checking level access:', error);
        });
        return () => { cancelled = true; };
    }, [levelName, currentUser?.id, isAdmin, navigate]);

    // Keep UserLessonProgress records in sync with this chapter's lessons, both creating
    // missing records and fixing stale prerequisites/completion whenever lessons are added,
    // removed, their visibility changes, or the user completes a quiz/video block.
    //
    // Each lesson's prerequisite is the nearest preceding VISIBLE lesson (by order); a private
    // lesson in between is skipped over as a prerequisite target. If no visible lesson precedes
    // it at all, there is no prerequisite and it starts unlocked. This means completing a
    // visible lesson unlocks the next one even if the immediately-following one is still
    // private -- and once that private lesson is made visible again, it automatically resumes
    // being used as the prerequisite for what follows it.
    //
    // A lesson is marked completed once every quiz block and video block it contains is
    // completed (see isLessonBlocksCompleted), so this effect re-runs whenever quiz attempts or
    // video progress change too.
    useEffect(() => {
        if (!currentUser?.id || !chapterData.lessons.length) return;
        // Guard against overlapping runs: if unrelated state changes trigger this effect again
        // while a previous run is still awaiting a create/update, that second run would start
        // from a stale local snapshot and could create/lock records using out-of-date info (e.g.
        // failing to see a sibling lesson its own run just created a moment ago).
        if (lessonProgressSyncInProgressRef.current) return;

        const orderedLessons = [...chapterData.lessons].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

        let cancelled = false;

        const syncLessonProgress = async () => {
            lessonProgressSyncInProgressRef.current = true;
            let progresses = [...userLessonProgresses];
            let changedAny = false;
            let lastVisibleId = null;

            for (const lesson of orderedLessons) {
                if (lesson?.id == null) continue;

                const prerequisiteId = lastVisibleId;
                const existing = progresses.find(p => p.user === currentUser.id && p.lesson === lesson.id);
                const prerequisiteProgress = prerequisiteId
                    ? progresses.find(p => p.user === currentUser.id && p.lesson === prerequisiteId)
                    : null;
                const nextUnlocked = prerequisiteId ? prerequisiteProgress?.lesson_completed === true : true;
                const nextCompleted = isLessonBlocksCompleted(lesson, currentUser.id, userQuizAttempts, userLessonVideoProgresses);

                if (!existing) {
                    const totalBlocks = (lesson.pages || []).reduce((sum, page) => sum + (page.blocks?.length || 0), 0);
                    const newProgressData = {
                        user: currentUser.id,
                        lesson: lesson.id,
                        total_blocks: totalBlocks,
                        completed_blocks: 0,
                        lesson_completed: nextCompleted,
                        unlocked: nextUnlocked,
                        prerequisite_lesson: prerequisiteId,
                    };

                    try {
                        const created = await createUserLessonProgress(newProgressData);
                        progresses = [...progresses, created];
                        changedAny = true;
                    } catch (error) {
                        console.error('Error creating user lesson progress:', error);
                    }
                } else if (
                    (existing.prerequisite_lesson ?? null) !== prerequisiteId ||
                    existing.unlocked !== nextUnlocked ||
                    existing.lesson_completed !== nextCompleted
                ) {
                    try {
                        const updated = await updateUserLessonProgress(existing.id, {
                            prerequisite_lesson: prerequisiteId,
                            unlocked: nextUnlocked,
                            lesson_completed: nextCompleted,
                        });
                        progresses = progresses.map(p => (p.id === existing.id ? updated : p));
                        changedAny = true;
                    } catch (error) {
                        console.error('Error updating user lesson progress:', error);
                    }
                }

                if (lesson.is_visible) {
                    lastVisibleId = lesson.id;
                }
            }

            if (!cancelled && changedAny) {
                setUserLessonProgresses(progresses);
            }
            lessonProgressSyncInProgressRef.current = false;
        };

        syncLessonProgress();

        return () => { cancelled = true; };
    }, [chapterData.lessons, currentUser?.id, userLessonProgresses, userQuizAttempts, userLessonVideoProgresses]);

    // Keep this chapter's UserTopicContentProgress in sync: completed exactly when every lesson
    // in the chapter is completed. Re-evaluated (not just set-once) so that adding a new
    // required block to an already-completed lesson correctly re-locks this chapter -- and
    // anything downstream that depends on it -- rather than leaving it stuck completed. The row
    // is expected to already exist (Level.jsx owns creation), so this only ever patches.
    useEffect(() => {
        if (!currentUser?.id || chapterData.topicContent == null || !chapterData.lessons.length) return;

        const existing = userTopicContentProgresses.find(
            p => p.user === currentUser.id && p.topic_content === chapterData.topicContent
        );
        if (!existing) return;

        const allLessonsCompleted = chapterData.lessons.every(lesson => {
            const progress = userLessonProgresses.find(p => p.user === currentUser.id && p.lesson === lesson.id);
            return progress?.lesson_completed === true;
        });
        if (existing.completed === allLessonsCompleted) return;

        let cancelled = false;
        (async () => {
            try {
                const updated = await updateUserTopicContentProgress(existing.id, { completed: allLessonsCompleted });
                if (!cancelled) {
                    setUserTopicContentProgresses(prev => prev.map(p => (p.id === updated.id ? updated : p)));
                }
            } catch (error) {
                console.error('Error updating user topic content progress:', error);
            }
        })();

        return () => { cancelled = true; };
    }, [chapterData.topicContent, chapterData.lessons, currentUser?.id, userLessonProgresses, userTopicContentProgresses]);

    // Callback for modal save
    const handleModalSave = (options = {}) => {
        const shouldRefresh = options.refresh === true;

        // Media blocks (image/video) are created/updated directly against the backend by
        // ModalBlock, bypassing chapterData entirely -- merge the saved block in locally here
        // so it appears immediately instead of only after a refetch/refresh.
        if (options.block && editingBlock) {
            const { l, p, b } = editingBlock;
            const mappedBlock = mapApiBlockToLocal(options.block);
            setChapterData((prev) => {
                const lessons = [...prev.lessons];
                const pages = [...lessons[l].pages];
                const blocks = [...pages[p].blocks];
                if (b === null || b === undefined) {
                    blocks.push(mappedBlock);
                } else {
                    blocks[b] = mappedBlock;
                }
                pages[p] = { ...pages[p], blocks };
                lessons[l] = { ...lessons[l], pages };
                return { ...prev, lessons };
            });
        }

        setModalChanged(shouldRefresh);
        setIsModalOpen(false);
        setEditingBlock(null);
        setModalBlockAction(null);
    };

    // Trigger enter animation on mount
    useEffect(() => {
        const timer = setTimeout(() => {
            setAnimationClass('anim-enter-right');
        }, 50);
        return () => clearTimeout(timer);
    }, []);

    // Smooth scroll function
    const smoothScrollTo = useCallback((element, target, duration, onComplete) => {
        if (isAnimating) return;
        setIsAnimating(true);
        
        const start = element.scrollTop;
        const change = target - start;
        const startTime = performance.now();

        function animate(currentTime) {
            const timeElapsed = currentTime - startTime;
            const progress = Math.min(timeElapsed / duration, 1);
            const ease = progress < 0.5 
                ? 4 * progress * progress * progress 
                : 1 - Math.pow(-2 * progress + 2, 3) / 2;

            element.scrollTop = start + (change * ease);

            if (timeElapsed < duration) {
                requestAnimationFrame(animate);
            } else {
                setIsAnimating(false);
                element.scrollTop = target;
                if (onComplete) onComplete();
            }
        }
        requestAnimationFrame(animate);
    }, [isAnimating]);

    // Scroll to page
    const scrollToPage = useCallback((lessonIdx, pageIndex) => {
        const target = document.getElementById(`page-${lessonIdx}-${pageIndex}`);
        const wrapper = canvasWrapperRef.current;
        if (target && wrapper) {
            smoothScrollTo(wrapper, target.offsetTop, 2000);
            if (lessonIdx === activeLessonIdx) setCurrentSnappedPageIdx(pageIndex);
        }
    }, [activeLessonIdx, smoothScrollTo]);

    // Limit normal scrolling (wheel, trackpad, scrollbar drag) to the current lesson-section
    // (page) the user is viewing. Moving to the next/previous page is only possible through
    // the scroll-down-btn / scroll-up-btn, which animate via scrollToPage above.
    useEffect(() => {
        const wrapper = canvasWrapperRef.current;
        if (!wrapper) return;
        // Not while presenting. These handlers clamp scrollTop to the bounds of whatever page
        // their closure captured, and that closure is stale for the render on which a slide
        // changes (currentSnappedPageIdx has updated but the listeners haven't been re-attached
        // yet, and isAnimating is still false). The stale handler then yanks the in-flight scroll
        // back to the previous page -- which is why stepping to any non-first page of a lesson in
        // the slideshow snapped back to the first one. Slides are navigated programmatically, so
        // there is nothing to clamp here anyway.
        if (isSlideshowActive) return;

        const getBounds = () => {
            const pageEl = document.getElementById(`page-${activeLessonIdx}-${currentSnappedPageIdx}`);
            if (!pageEl) return null;
            const minScroll = pageEl.offsetTop;
            const maxScroll = Math.max(minScroll, pageEl.offsetTop + pageEl.offsetHeight - wrapper.clientHeight);
            return { minScroll, maxScroll };
        };

        const handleWheel = (e) => {
            if (isAnimating) return;
            // Horizontal scrolling (trackpad swipe, shift+wheel) is exempt from the page
            // boundary clamp -- only vertical scrolling is limited to the current page.
            if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;

            const bounds = getBounds();
            if (!bounds) return;

            const nextScrollTop = wrapper.scrollTop + e.deltaY;
            const clampedScrollTop = Math.min(Math.max(nextScrollTop, bounds.minScroll), bounds.maxScroll);

            e.preventDefault();
            wrapper.scrollTop = clampedScrollTop;
        };

        // Fallback for input methods that don't fire 'wheel' (scrollbar drag, keyboard scroll)
        const handleScroll = () => {
            if (isAnimating) return;
            const bounds = getBounds();
            if (!bounds) return;

            if (wrapper.scrollTop < bounds.minScroll) {
                wrapper.scrollTop = bounds.minScroll;
            } else if (wrapper.scrollTop > bounds.maxScroll) {
                wrapper.scrollTop = bounds.maxScroll;
            }
        };

        wrapper.addEventListener('wheel', handleWheel, { passive: false });
        wrapper.addEventListener('scroll', handleScroll);
        return () => {
            wrapper.removeEventListener('wheel', handleWheel);
            wrapper.removeEventListener('scroll', handleScroll);
        };
        // `loading`/`error` are included because canvasWrapperRef.current is null while either
        // gates the canvas-wrapper out of the tree (see the early-return loading/error JSX below).
        // Without them, if the ref is still null the one time this effect runs while activeLessonIdx
        // and currentSnappedPageIdx both happen to still be their initial 0 values (i.e. the very
        // first lesson/page on initial load), the listeners never get attached -- since neither
        // dependency actually changes value once loading finishes, the effect wouldn't re-run.
    }, [activeLessonIdx, currentSnappedPageIdx, isAnimating, loading, error, isSlideshowActive]);

    // Load lesson
    const loadLesson = useCallback((index) => {
        setActiveLessonIdx(index);
        setCurrentSnappedPageIdx(0);
    }, []);

    // Handle back to level
    const handleBackToLevel = () => {
        setAnimationClass('anim-exit-right');
        setTimeout(() => {
            navigate(`/dashboard/${levelName}`);
        }, 300);
    };

    // Toggle sidebar
    const toggleSidebar = () => {
        setIsSidebarOpen(!isSidebarOpen);
        document.documentElement.style.setProperty(
            '--chapter-sidebar-width', 
            isSidebarOpen ? '0px' : '320px'
        );
    };

    // Rename chapter
    const renameChapter = async () => {
        const newName = prompt(t('chapter.renameChapterPrompt'), chapterData.title);
        if (newName && newName !== chapterData.title) {
            try {
                if (chapterData.id) {
                    await updateChapter(chapterData.id, { title: newName });
                }
                setChapterData(prev => ({ ...prev, title: newName }));
            } catch (err) {
                console.error('Error renaming chapter:', err);
                alert(t('chapter.errors.renameChapterFailed'));
            }
        }
    };

    // Rename lesson
    const renameLesson = async (index) => {
        const lesson = chapterData.lessons[index];
        const newName = prompt(t('chapter.renameLessonPrompt'), lesson.title);
        if (newName && newName !== lesson.title) {
            setChapterData(prev => {
                const lessons = [...prev.lessons];
                lessons[index] = { ...lessons[index], title: newName };
                return { ...prev, lessons };
            });
            
            // Log the change
            await logChapterChange(`Renamed lesson: ${lesson.title} → ${newName}`, { 
                old_title: lesson.title, 
                new_title: newName 
            });
        }
    };

    // Add new lesson
    const addNewLesson = async () => {
        const name = prompt(t('chapter.newLessonNamePrompt'), "New Lesson");
        if (name) {
            setChapterData(prev => ({
                ...prev,
                lessons: [...prev.lessons, {
                    id: null,
                    title: name,
                    order: prev.lessons.length,
                    pages: [{
                        id: null,
                        title: "Part 1",
                        lesson: null,
                        order: 0,
                        blocks: []
                    }]
                }]
            }));
            setActiveLessonIdx(chapterData.lessons.length);

            // Log the change
            await logChapterChange(`Added lesson: ${name}`, { lesson_title: name });
        }
    };

    // Delete lesson
    const handleDeleteLesson = async (index) => {
        if (window.confirm(t('chapter.confirmDeleteLesson'))) {
            const lesson = chapterData.lessons[index];
            setChapterData(prev => ({
                ...prev,
                lessons: prev.lessons.filter((_, i) => i !== index),
            }));
            if (activeLessonIdx >= chapterData.lessons.length - 1) {
                setActiveLessonIdx(0);
            }

            // Log the change
            await logChapterChange(`Deleted lesson: ${lesson.title}`, { lesson_title: lesson.title });
        }
    };

    // Add page
    const addPage = async () => {
        const defaultTitle = "Part " + (chapterData.lessons[activeLessonIdx].pages.length + 1);
        const lesson = chapterData.lessons[activeLessonIdx];

        setChapterData(prev => {
            const lessons = [...prev.lessons];
            lessons[activeLessonIdx] = {
                ...lessons[activeLessonIdx],
                pages: [...lessons[activeLessonIdx].pages, {
                    id: null,
                    title: defaultTitle,
                    lesson: lesson.id,
                    order: lesson.pages.length,
                    height: 100,
                    blocks: []
                }]
            };
            return { ...prev, lessons };
        });
        setTimeout(() => scrollToPage(activeLessonIdx, lesson.pages.length), 100);
        
        // Log the change
        await logChapterChange(`Added page: ${defaultTitle}`, { page_title: defaultTitle, lesson_id: lesson.id });
    };

    // Rename page
    const renamePage = async (lessonIdx, pageIdx) => {
        const page = chapterData.lessons[lessonIdx].pages[pageIdx];
        const newName = prompt(t('chapter.renamePagePrompt'), page.title || `Part ${pageIdx + 1}`);
        if (newName && newName !== page.title) {
            setChapterData(prev => {
                const lessons = [...prev.lessons];
                lessons[lessonIdx].pages[pageIdx] = { ...lessons[lessonIdx].pages[pageIdx], title: newName };
                return { ...prev, lessons };
            });
            
            // Log the change
            await logChapterChange(`Renamed page: ${page.title} → ${newName}`, { 
                old_title: page.title, 
                new_title: newName 
            });
        }
    };

    // Delete page
    const deletePage = async (lessonIdx, pageIdx) => {
        if (window.confirm(t('chapter.confirmDeletePart'))) {
            const pages = chapterData.lessons[lessonIdx].pages;
            if (pages.length <= 1) {
                alert(t('chapter.cannotDeleteOnlyPart'));
                return;
            }
            const deletedPage = pages[pageIdx];
            setChapterData(prev => {
                const lessons = [...prev.lessons];
                lessons[lessonIdx] = {
                    ...lessons[lessonIdx],
                    pages: pages.filter((_, i) => i !== pageIdx)
                };
                return { ...prev, lessons };
            });
            if (currentSnappedPageIdx >= pages.length - 1) {
                setCurrentSnappedPageIdx(pages.length - 2);
            }
            
            // Log the change
            await logChapterChange(`Deleted page: ${deletedPage.title}`, { page_title: deletedPage.title });
        }
    };

    // Update page title from main content
    const updatePageTitleFromMain = (lessonIdx, pageIdx, newTitle) => {
        setChapterData(prev => {
            const lessons = [...prev.lessons];
            lessons[lessonIdx].pages[pageIdx] = { ...lessons[lessonIdx].pages[pageIdx], title: newTitle.trim() };
            return { ...prev, lessons };
        });
    };

    // Update lesson title from main content
    const updateLessonTitleFromMain = (lessonIdx, newTitle) => {
        setChapterData(prev => {
            const lessons = [...prev.lessons];
            lessons[lessonIdx] = { ...lessons[lessonIdx], title: newTitle.trim() };
            return { ...prev, lessons };
        });
    };

    // Open content modal (patched for quiz)
    const openContentModal = async (type, lessonIdx = null, pageIdx = null, blkIdx = null) => {
        // Existing logic for editing or other types
        if (pageIdx !== null && blkIdx !== null) {
            const block = chapterData.lessons[lessonIdx].pages[pageIdx].blocks[blkIdx];
            setEditingBlock({ l: lessonIdx, p: pageIdx, b: blkIdx, ...block });
            // Set modalType based on block type
            if (block.block_type === 'quiz' || block.type === 'quiz') {
                setModalType('quiz');
            } else if (block.block_type === 'image' || block.block_type === 'video' || block.type === 'image' || block.type === 'video') {
                setModalType(block.type || block.block_type);
            } else {
                setModalType('card');
                setModalHeading(block.title || '');
                setModalBody(block.text || '');
                setModalHeadingSize(block.titleSize || 24);
                setModalBodySize(block.textSize || 16);
            }
        } else {
            setEditingBlock({ l: activeLessonIdx, p: currentSnappedPageIdx, b: null });
            setModalType(type);
            setModalHeading('');
            setModalBody('');
            setModalHeadingSize(24);
            setModalBodySize(16);
        }
        setIsModalOpen(true);
    };

    // Close modal
    const closeModal = (shouldRefresh = false) => {
        setIsModalOpen(false);
        setEditingBlock(null);
        setModalBlockAction(null);
        if (shouldRefresh) {
            fetchChapterData();
        }
    };

    // Open history modal
    const openHistoryModal = () => {
        setIsHistoryModalOpen(true);
    };

    // Close history modal
    const closeHistoryModal = () => {
        setIsHistoryModalOpen(false);
    };

    const saveQuizDraft = useCallback(({ quizQuestions }) => {
        if (!editingBlock) return;

        const lessonIdx = editingBlock.l;
        const pageIdx = editingBlock.p;
        const blockIdx = editingBlock.b;
        const lesson = chapterData.lessons?.[lessonIdx];
        const page = lesson?.pages?.[pageIdx];

        if (!lesson || !page) return;

        const existingBlock = blockIdx !== null ? page.blocks?.[blockIdx] : null;
        const existingQuestions = existingBlock?.quizBlock?.questions || [];

        const normalizedQuestions = (quizQuestions || []).map((question, questionIndex) => {
            const previousQuestion = existingQuestions[questionIndex];

            return {
                id: previousQuestion?.id || null,
                text: question.text || '',
                type: question.type || 'single',
                choices: (question.options || []).map((option, optionIndex) => ({
                    id: previousQuestion?.choices?.[optionIndex]?.id || null,
                    text: option.text || '',
                    is_correct: !!option.isCorrect,
                })),
            };
        });

        const nextChapterData = cloneSessionData(chapterData);
        const targetPage = nextChapterData.lessons[lessonIdx].pages[pageIdx];

        if (blockIdx === null) {
            targetPage.blocks.push({
                id: null,
                type: 'quiz',
                title: '',
                text: '',
                titleSize: 24,
                textSize: 16,
                width: 700,
                height: 200,
                left: editingBlock.left ?? 0,
                top: editingBlock.top ?? 0,
                order: targetPage.blocks.length,
                questions: normalizedQuestions,
                quizBlock: {
                    id: null,
                    lesson_page_block: null,
                    choice_type: normalizedQuestions[0]?.type || 'single',
                    questions: normalizedQuestions,
                },
            });
        } else {
            const updatedBlock = { ...targetPage.blocks[blockIdx] };
            updatedBlock.type = 'quiz';
            updatedBlock.questions = normalizedQuestions;
            updatedBlock.quizBlock = {
                ...(updatedBlock.quizBlock || {}),
                id: updatedBlock.quizBlock?.id || null,
                lesson_page_block: updatedBlock.id || updatedBlock.quizBlock?.lesson_page_block || null,
                choice_type: normalizedQuestions[0]?.type || updatedBlock.quizBlock?.choice_type || 'single',
                questions: normalizedQuestions,
            };
            targetPage.blocks[blockIdx] = updatedBlock;
        }

        setChapterData(nextChapterData);
        syncCurrentSessionLists(nextChapterData);
    }, [chapterData, cloneSessionData, editingBlock, syncCurrentSessionLists]);

    // Save content from modal
    const saveContent = async () => {
        const { l, p, b } = editingBlock;
        const nextChapterData = cloneSessionData(chapterData);
        const page = nextChapterData.lessons[l].pages[p];
        
        try {
            isRestoringSessionRef.current = true;
            if (b === null) {
                const blocks = [...page.blocks];
                blocks.push({
                    type: 'card',
                    title: modalHeading,
                    text: modalBody,
                    titleSize: modalHeadingSize,
                    textSize: modalBodySize,
                    titleFont: 'Inter',
                    titleBold: false,
                    titleLayout: 'left',
                    titleColor: '#0f172a',
                    textFont: 'Inter',
                    textBold: false,
                    textLayout: 'left',
                    textColor: '#0f172a',
                    font: 'Inter',
                    bold: false,
                    layout: 'left',
                    color: '#0f172a',
                    width: 700,
                    height: 200,
                    left: 0,
                    top: 0,
                    order: page.blocks.length,
                    id: null,
                });
                nextChapterData.lessons[l].pages[p] = { ...nextChapterData.lessons[l].pages[p], blocks };
                
                // Log the change
                await logChapterChange(`Added card block: ${modalHeading}`, { 
                    block_title: modalHeading,
                    block_type: 'card'
                });
            } else {
                // Updating existing block
                const blocks = [...page.blocks];
                blocks[b] = {
                    ...blocks[b],
                    title: modalHeading,
                    text: modalBody,
                    titleSize: modalHeadingSize,
                    textSize: modalBodySize,
                    titleFont: blocks[b].titleFont || blocks[b].font || 'Inter',
                    titleBold: blocks[b].titleBold ?? blocks[b].bold ?? false,
                    titleLayout: blocks[b].titleLayout || blocks[b].layout || 'left',
                    titleColor: blocks[b].titleColor || blocks[b].color || '#0f172a',
                    textFont: blocks[b].textFont || blocks[b].font || 'Inter',
                    textBold: blocks[b].textBold ?? blocks[b].bold ?? false,
                    textLayout: blocks[b].textLayout || blocks[b].layout || 'left',
                    textColor: blocks[b].textColor || blocks[b].color || '#0f172a',
                };
                nextChapterData.lessons[l].pages[p] = { ...nextChapterData.lessons[l].pages[p], blocks };
                
                // Log the change
                await logChapterChange(`Updated card block: ${modalHeading}`, { 
                    block_title: modalHeading,
                    block_type: 'card'
                });
            }
            
            setChapterData(nextChapterData);
            resetSessionHistory(nextChapterData);
            closeModal(false);
        } catch (err) {
            isRestoringSessionRef.current = false;
            console.error('Error saving block:', err);
            alert(t('chapter.errors.saveBlockFailed'));
        }
    };

    // Delete block
    const deleteBlock = async (lessonIdx, pageIdx, blkIdx) => {
        // if (window.confirm("Delete block?")) {
            const block = chapterData.lessons[lessonIdx].pages[pageIdx].blocks[blkIdx];
            // Update chapter data after deletion; the Save action will persist the removal.
            setChapterData(prev => {
                const updatedLessons = [...prev.lessons];
                const updatedPages = [...updatedLessons[lessonIdx].pages];
                const updatedBlocks = [...updatedPages[pageIdx].blocks];
                updatedBlocks.splice(blkIdx, 1);
                updatedPages[pageIdx] = { ...updatedPages[pageIdx], blocks: updatedBlocks };
                updatedLessons[lessonIdx] = { ...updatedLessons[lessonIdx], pages: updatedPages };
                return { ...prev, lessons: updatedLessons };
            });
            
            // Log the change
            const blockType = block?.type || 'unknown';
            await logChapterChange(`Deleted ${blockType} block`, { block_type: blockType });
        // }
    };

    // Duplicate block
    const duplicateBlock = async (lessonIdx, pageIdx, blkIdx) => {
        const sourceBlock = chapterData.lessons[lessonIdx].pages[pageIdx].blocks[blkIdx];
        if (!sourceBlock) return;

        const duplicatedBlock = cloneSessionData(sourceBlock);
        duplicatedBlock.id = null;
        duplicatedBlock.order = (chapterData.lessons[lessonIdx].pages[pageIdx].blocks?.length || 0);

        if (duplicatedBlock.type === 'quiz') {
            duplicatedBlock.quizBlock = duplicatedBlock.quizBlock
                ? {
                    ...duplicatedBlock.quizBlock,
                    id: null,
                    lesson_page_block: null,
                    questions: (duplicatedBlock.quizBlock.questions || []).map((question) => ({
                        ...question,
                        id: null,
                        choices: (question.choices || []).map((choice) => ({
                            ...choice,
                            id: null,
                        })),
                    })),
                }
                : duplicatedBlock.quizBlock;
            duplicatedBlock.questions = (duplicatedBlock.questions || []).map((question) => ({
                ...question,
                id: null,
                choices: (question.choices || []).map((choice) => ({
                    ...choice,
                    id: null,
                })),
            }));
        }

        setChapterData((prev) => {
            const lessons = [...prev.lessons];
            const pages = [...lessons[lessonIdx].pages];
            const blocks = [...pages[pageIdx].blocks];
            blocks.splice(blkIdx + 1, 0, duplicatedBlock);
            pages[pageIdx] = { ...pages[pageIdx], blocks };
            lessons[lessonIdx] = { ...lessons[lessonIdx], pages };
            return { ...prev, lessons };
        });

        // Log the change
        await logChapterChange(`Duplicated ${sourceBlock.type || 'content'} block`, {
            block_type: sourceBlock.type || 'unknown',
        });
    };

    // ==================== Multi-block selection: marquee select, ctrl+click, multi-drag, clipboard, context menu ====================

    const isBlockSelected = useCallback((lessonIdx, pageIdx, blkIdx) => (
        selectedBlocks.some((b) => b.lessonIdx === lessonIdx && b.pageIdx === pageIdx && b.blkIdx === blkIdx)
    ), [selectedBlocks]);

    const toggleBlockSelection = useCallback((lessonIdx, pageIdx, blkIdx) => {
        setSelectedBlocks((prev) => {
            const exists = prev.some((b) => b.lessonIdx === lessonIdx && b.pageIdx === pageIdx && b.blkIdx === blkIdx);
            if (exists) return prev.filter((b) => !(b.lessonIdx === lessonIdx && b.pageIdx === pageIdx && b.blkIdx === blkIdx));
            return [...prev, { lessonIdx, pageIdx, blkIdx }];
        });
    }, []);

    const clearBlockSelection = useCallback(() => setSelectedBlocks([]), []);

    // Marquee (click-drag) selection over empty canvas area
    const handleCanvasMouseDown = useCallback((e, lessonIdx, pageIdx) => {
        if (!isStaff() || !editorMode) return;
        if (e.button !== 0) return;
        if (e.target.closest('.content-block')) return;

        if (!e.ctrlKey && !e.metaKey) {
            clearBlockSelection();
        }

        setMarqueeState({
            lessonIdx,
            pageIdx,
            containerEl: e.currentTarget,
            startX: e.clientX,
            startY: e.clientY,
            curX: e.clientX,
            curY: e.clientY,
        });
    }, [isStaff, editorMode, clearBlockSelection]);

    // Right-click on empty canvas: with a selection, clear it; otherwise show the "new block" menu at the cursor
    const handleCanvasContextMenu = useCallback((e, lessonIdx, pageIdx) => {
        if (!isStaff() || !editorMode) return;
        if (e.target.closest('.content-block')) return;
        e.preventDefault();
        e.stopPropagation();

        if (selectedBlocks.length > 0) {
            clearBlockSelection();
            return;
        }

        const stagePoint = clientPointToStage(findStageEl(e.currentTarget), e.clientX, e.clientY);
        setBlockCreateMenu({
            x: e.clientX,
            y: e.clientY,
            lessonIdx,
            pageIdx,
            left: Math.max(0, Math.round(stagePoint.x)),
            top: Math.max(0, Math.round(stagePoint.y)),
        });
    }, [isStaff, editorMode, selectedBlocks, clearBlockSelection]);

    useEffect(() => {
        if (!blockCreateMenu) return;
        const handleClickAway = (e) => {
            if (e.target.closest && e.target.closest('.block-create-menu')) return;
            setBlockCreateMenu(null);
        };
        document.addEventListener('mousedown', handleClickAway);
        return () => document.removeEventListener('mousedown', handleClickAway);
    }, [blockCreateMenu]);

    useEffect(() => {
        if (!marqueeState) return;

        const handleMouseMove = (e) => {
            setMarqueeState((prev) => (prev ? { ...prev, curX: e.clientX, curY: e.clientY } : prev));
        };

        const handleMouseUp = () => {
            setMarqueeState((current) => {
                if (!current) return null;
                const rectLeft = Math.min(current.startX, current.curX);
                const rectRight = Math.max(current.startX, current.curX);
                const rectTop = Math.min(current.startY, current.curY);
                const rectBottom = Math.max(current.startY, current.curY);
                const dragged = (rectRight - rectLeft) > 4 || (rectBottom - rectTop) > 4;

                if (dragged && current.containerEl) {
                    const blockEls = current.containerEl.querySelectorAll('.content-block');
                    const newlySelected = [];
                    blockEls.forEach((el) => {
                        const r = el.getBoundingClientRect();
                        const overlaps = r.left < rectRight && r.right > rectLeft && r.top < rectBottom && r.bottom > rectTop;
                        if (overlaps) {
                            newlySelected.push({
                                lessonIdx: Number(el.dataset.lessonIdx),
                                pageIdx: Number(el.dataset.pageIdx),
                                blkIdx: Number(el.dataset.blkIdx),
                            });
                        }
                    });

                    if (newlySelected.length) {
                        setSelectedBlocks((prevSelected) => {
                            const merged = [...prevSelected];
                            newlySelected.forEach((nb) => {
                                if (!merged.some((b) => b.lessonIdx === nb.lessonIdx && b.pageIdx === nb.pageIdx && b.blkIdx === nb.blkIdx)) {
                                    merged.push(nb);
                                }
                            });
                            return merged;
                        });
                    }
                }

                return null;
            });
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [marqueeState]);

    // Calculate snap position for center/left/right guides.
    //
    // Works entirely in STAGE units: visualLeft/startLeft and element.offsetWidth are already
    // stage-space (offsetWidth is the pre-transform layout width), while parentRect/canvasRect are
    // post-transform client rects -- so anything crossing between the two is divided or multiplied
    // by `scale`. The guides themselves are positioned in canvas (client) space, hence the
    // multiply on the way back out.
    const calculateSnap = useCallback((element, visualLeft, parentRect, canvasRect, startLeft, scale = 1) => {
        const guideCenter = guideCenterRef.current;
        const guideLeft = guideLeftRef.current;
        const guideRight = guideRightRef.current;
        if (!guideCenter || !guideLeft) return visualLeft;

        const stageWidth = element.parentElement?.offsetWidth || STAGE_WIDTH;
        // Canvas centre expressed as a stage x-coordinate.
        const canvasMiddleStageX = ((canvasRect.left + (canvasRect.width / 2)) - parentRect.left) / scale;
        const diffCenter = (visualLeft + element.offsetWidth / 2) - canvasMiddleStageX;
        const diffLeft = visualLeft - startLeft;
        // Right boundary: the furthest left the block can sit without its right edge spilling past
        // the stage's own right border, the same reference the left snap uses via startLeft.
        const maxLeft = Math.max(0, stageWidth - element.offsetWidth);
        const diffRight = visualLeft - maxLeft;

        guideCenter.classList.remove('locked');
        guideLeft.classList.remove('locked');
        if (guideRight) guideRight.classList.remove('locked');

        let snappedLeft = visualLeft;

        // Thresholds stay in stage units so the snap feels the same however the page is scaled.
        if (Math.abs(diffCenter) < 30) {
            guideCenter.classList.add('locked');
            snappedLeft = canvasMiddleStageX - (element.offsetWidth / 2);
        } else if (Math.abs(diffLeft) < 30) {
            guideLeft.style.left = (parentRect.left - canvasRect.left + startLeft * scale) + 'px';
            guideLeft.classList.add('locked');
            snappedLeft = startLeft;
        } else if (diffRight > -30) {
            if (guideRight) {
                guideRight.style.left = (parentRect.left - canvasRect.left + (maxLeft + element.offsetWidth) * scale) + 'px';
                guideRight.classList.add('locked');
            }
            snappedLeft = maxLeft;
        }

        // Hard boundary: never let the block be dragged past the right edge, regardless of snap zone
        return Math.min(snappedLeft, maxLeft);
    }, []);

    // Calculate object snap alignment with other blocks.
    //
    // Same two coordinate spaces as calculateSnap: target.offsetWidth/Height and the visualLeft/Top
    // being snapped are stage units, every sibling rect is post-transform client px. Siblings are
    // divided back into stage units to compare; the guides (position: fixed) are multiplied back
    // out to client px to draw.
    const calculateObjectSnap = useCallback((target, visualLeft, visualTop, parentRect, scale = 1) => {
        const threshold = 20;
        let newLeft = visualLeft;
        let newTop = visualTop;
        const hGuide = guideAlignHRef.current;
        const vGuide = guideAlignVRef.current;

        if (!hGuide || !vGuide) return { left: newLeft, top: newTop };

        hGuide.style.display = 'none';
        vGuide.style.display = 'none';

        const myW = target.offsetWidth;
        const myH = target.offsetHeight;
        const contRect = parentRect;
        const blocks = document.querySelectorAll('.content-block');
        // Stage x/y -> client px, for positioning the guides.
        const toClientX = (stageX) => contRect.left + stageX * scale;
        const toClientY = (stageY) => contRect.top + stageY * scale;

        blocks.forEach(sibling => {
            if (sibling === target) return;
            const sibRect = sibling.getBoundingClientRect();
            const sibLeft = (sibRect.left - contRect.left) / scale;
            const sibTop = (sibRect.top - contRect.top) / scale;
            const sibW = sibRect.width / scale;
            const sibH = sibRect.height / scale;
            const sibCenterX = sibLeft + (sibW / 2);
            const sibCenterY = sibTop + (sibH / 2);
            const myCenterX = visualLeft + (myW / 2);
            const myCenterY = visualTop + (myH / 2);

            // Horizontal alignment
            if (Math.abs(visualTop - sibTop) < threshold) {
                newTop = sibTop;
                hGuide.style.top = toClientY(newTop) + 'px';
                hGuide.style.display = 'block';
            } else if (Math.abs((visualTop + myH) - (sibTop + sibH)) < threshold) {
                newTop = (sibTop + sibH) - myH;
                hGuide.style.top = toClientY(newTop + myH) + 'px';
                hGuide.style.display = 'block';
            } else if (Math.abs(myCenterY - sibCenterY) < threshold) {
                newTop = sibCenterY - (myH / 2);
                hGuide.style.top = toClientY(sibCenterY) + 'px';
                hGuide.style.display = 'block';
            }

            // Vertical alignment
            if (Math.abs(visualLeft - sibLeft) < threshold) {
                newLeft = sibLeft;
                vGuide.style.left = toClientX(newLeft) + 'px';
                vGuide.style.display = 'block';
            } else if (Math.abs((visualLeft + myW) - (sibLeft + sibW)) < threshold) {
                newLeft = (sibLeft + sibW) - myW;
                vGuide.style.left = toClientX(newLeft + myW) + 'px';
                vGuide.style.display = 'block';
            } else if (Math.abs(myCenterX - sibCenterX) < threshold) {
                newLeft = sibCenterX - (myW / 2);
                vGuide.style.left = toClientX(sibCenterX) + 'px';
                vGuide.style.display = 'block';
            }
        });

        return { left: newLeft, top: newTop };
    }, []);

    // Drag all selected blocks together
    const handleMultiBlockDragStart = useCallback((e, anchorLessonIdx, anchorPageIdx, anchorBlkIdx) => {
        if (!isStaff() || !editorMode) return;
        e.preventDefault();
        e.stopPropagation();

        isSessionHistoryPausedRef.current = true;

        // The clicked block acts as the anchor for center/left/right snapping; the rest of the
        // selection is translated by the same effective delta so the group moves together.
        const anchorBlockEl = e.target.closest('.content-block');
        const anchorStageEl = findStageEl(anchorBlockEl);
        const anchorContainerRect = anchorStageEl?.getBoundingClientRect();
        const anchorScale = readStageScale(anchorStageEl);

        const items = selectedBlocks.map(({ lessonIdx, pageIdx, blkIdx }) => {
            const block = chapterData.lessons[lessonIdx]?.pages[pageIdx]?.blocks[blkIdx];
            const blockEl = document.querySelector(`.content-block[data-lesson-idx="${lessonIdx}"][data-page-idx="${pageIdx}"][data-blk-idx="${blkIdx}"]`);
            const stageEl = findStageEl(blockEl);
            const containerRect = stageEl?.getBoundingClientRect();
            const scale = readStageScale(stageEl);
            const blockRect = blockEl?.getBoundingClientRect();
            // Scaled px -> stage units, as in handleBlockDragStart.
            const fallbackLeft = (blockRect && containerRect) ? (blockRect.left - containerRect.left) / scale : 0;
            const fallbackTop = (blockRect && containerRect) ? (blockRect.top - containerRect.top) / scale : 0;
            const startLeft = coerceInteger(block?.left, fallbackLeft);
            const startTop = coerceInteger(block?.top, fallbackTop);
            return { lessonIdx, pageIdx, blkIdx, startLeft, startTop, originalLeft: startLeft, originalTop: startTop };
        });

        setMultiDragState({
            startX: e.clientX,
            startY: e.clientY,
            scale: anchorScale,
            items,
            anchorLessonIdx,
            anchorPageIdx,
            anchorBlkIdx,
            anchorBlockEl,
            anchorContainerRect,
        });

        document.body.classList.add('sliding-block');
    }, [isStaff, editorMode, selectedBlocks, chapterData]);

    useEffect(() => {
        if (!multiDragState) return;

        const handleMouseMove = (e) => {
            // Screen px -> stage units, as in the single-block drag.
            const scale = multiDragState.scale || 1;
            const deltaX = (e.clientX - multiDragState.startX) / scale;
            const deltaY = (e.clientY - multiDragState.startY) / scale;

            // Snap only the anchor block (the one under the cursor) against the canvas
            // center/left/right guides, then translate every selected block by the same
            // effective horizontal delta so the whole group keeps its relative layout.
            let effectiveDeltaX = deltaX;
            const anchorItem = multiDragState.items.find((item) => (
                item.lessonIdx === multiDragState.anchorLessonIdx &&
                item.pageIdx === multiDragState.anchorPageIdx &&
                item.blkIdx === multiDragState.anchorBlkIdx
            ));
            const canvasRect = canvasWrapperRef.current?.getBoundingClientRect();

            if (anchorItem && multiDragState.anchorBlockEl && multiDragState.anchorContainerRect && canvasRect) {
                const rawAnchorLeft = Math.max(0, anchorItem.startLeft + deltaX);
                const snappedAnchorLeft = calculateSnap(
                    multiDragState.anchorBlockEl,
                    rawAnchorLeft,
                    multiDragState.anchorContainerRect,
                    canvasRect,
                    anchorItem.startLeft,
                    scale
                );
                effectiveDeltaX = snappedAnchorLeft - anchorItem.startLeft;
            }

            setChapterData((prev) => {
                const lessons = [...prev.lessons];
                const touchedPages = new Set();

                multiDragState.items.forEach(({ lessonIdx, pageIdx, blkIdx, startLeft, startTop }) => {
                    const newLeft = Math.max(0, startLeft + effectiveDeltaX);
                    const newTop = Math.max(0, startTop + deltaY);
                    const pageKey = `${lessonIdx}-${pageIdx}`;
                    if (!touchedPages.has(pageKey)) {
                        lessons[lessonIdx] = { ...lessons[lessonIdx], pages: [...lessons[lessonIdx].pages] };
                        touchedPages.add(pageKey);
                    }
                    const blocks = [...lessons[lessonIdx].pages[pageIdx].blocks];
                    blocks[blkIdx] = { ...blocks[blkIdx], left: newLeft, top: newTop };
                    lessons[lessonIdx].pages[pageIdx] = { ...lessons[lessonIdx].pages[pageIdx], blocks };
                });

                return { ...prev, lessons };
            });
        };

        const handleMouseUp = async () => {
            try {
                await Promise.all(multiDragState.items.map(async ({ lessonIdx, pageIdx, blkIdx, originalLeft, originalTop }) => {
                    const block = chapterData.lessons[lessonIdx]?.pages[pageIdx]?.blocks[blkIdx];
                    if (block?.id) {
                        const movedLeft = coerceInteger(block.left, originalLeft);
                        const movedTop = coerceInteger(block.top, originalTop);
                    }
                }));
                await logChapterChange(`Moved ${multiDragState.items.length} blocks`, { count: multiDragState.items.length });
            } catch (err) {
                console.error('Error saving multi-block drag position:', err);
            }
            setMultiDragState(null);
            document.body.classList.remove('sliding-block');
            guideCenterRef.current?.classList.remove('locked');
            guideLeftRef.current?.classList.remove('locked');
            guideRightRef.current?.classList.remove('locked');
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [multiDragState, chapterData, logChapterChange, calculateSnap]);

    // Shared duplicate/delete logic for a list of block refs ({ lessonIdx, pageIdx, blkIdx })
    const duplicateBlockRefs = useCallback(async (blockRefs) => {
        if (!blockRefs || !blockRefs.length) return;

        const groups = new Map();
        blockRefs.forEach(({ lessonIdx, pageIdx, blkIdx }) => {
            const key = `${lessonIdx}-${pageIdx}`;
            if (!groups.has(key)) groups.set(key, { lessonIdx, pageIdx, indices: [] });
            groups.get(key).indices.push(blkIdx);
        });

        setChapterData((prev) => {
            const lessons = [...prev.lessons];
            groups.forEach(({ lessonIdx, pageIdx, indices }) => {
                const pages = [...lessons[lessonIdx].pages];
                const blocks = [...pages[pageIdx].blocks];
                const sortedIndices = [...indices].sort((a, b) => b - a);

                sortedIndices.forEach((idx) => {
                    const sourceBlock = blocks[idx];
                    if (!sourceBlock) return;

                    const duplicatedBlock = cloneSessionData(sourceBlock);
                    duplicatedBlock.id = null;
                    duplicatedBlock.order = blocks.length;

                    if (duplicatedBlock.type === 'quiz') {
                        duplicatedBlock.quizBlock = duplicatedBlock.quizBlock
                            ? {
                                ...duplicatedBlock.quizBlock,
                                id: null,
                                lesson_page_block: null,
                                questions: (duplicatedBlock.quizBlock.questions || []).map((question) => ({
                                    ...question,
                                    id: null,
                                    choices: (question.choices || []).map((choice) => ({ ...choice, id: null })),
                                })),
                            }
                            : duplicatedBlock.quizBlock;
                        duplicatedBlock.questions = (duplicatedBlock.questions || []).map((question) => ({
                            ...question,
                            id: null,
                            choices: (question.choices || []).map((choice) => ({ ...choice, id: null })),
                        }));
                    }

                    blocks.splice(idx + 1, 0, duplicatedBlock);
                });

                pages[pageIdx] = { ...pages[pageIdx], blocks };
                lessons[lessonIdx] = { ...lessons[lessonIdx], pages };
            });
            return { ...prev, lessons };
        });

        await logChapterChange(`Duplicated ${blockRefs.length} block(s)`, { count: blockRefs.length });
    }, [cloneSessionData, logChapterChange]);

    const pasteBlockRefs = useCallback(async (blockRefs) => {
        if (!blockRefs || !blockRefs.length) return;

        // Paste at wherever the right-click menu was opened -- either on an existing/selected
        // block (blockContextMenu) or on empty canvas (blockCreateMenu). Both already carry the
        // target page and STAGE-relative left/top, captured at click time (not looked
        // up lazily here, since by now the menu itself would be sitting on top of that position).
        let target = blockContextMenu || blockCreateMenu;

        if (!target || target.lessonIdx == null || target.pageIdx == null) {
            // No menu open (e.g. Ctrl+V with no prior right-click) -- resolve the target page and
            // position from the live cursor instead, since nothing is drawn over it right now.
            const { x: cursorX, y: cursorY } = lastCursorPositionRef.current;
            const elementUnderCursor = document.elementFromPoint(cursorX, cursorY);
            const containerEl = elementUnderCursor?.closest('.blocks-container');
            const containerMatch = containerEl?.id?.match(/^blocks-page-(\d+)-(\d+)$/);

            if (containerEl && containerMatch) {
                const stagePoint = clientPointToStage(findStageEl(containerEl), cursorX, cursorY);
                target = {
                    lessonIdx: Number(containerMatch[1]),
                    pageIdx: Number(containerMatch[2]),
                    left: Math.max(0, Math.round(stagePoint.x)),
                    top: Math.max(0, Math.round(stagePoint.y)),
                };
            }
        }

        // Last resort (cursor isn't over any page's canvas at all): duplicate in place.
        if (!target || target.lessonIdx == null || target.pageIdx == null) {
            await duplicateBlockRefs(blockRefs);
            return;
        }

        const { lessonIdx: destLessonIdx, pageIdx: destPageIdx, left: targetLeft, top: targetTop } = target;

        const groups = new Map();
        blockRefs.forEach(({ lessonIdx, pageIdx, blkIdx }) => {
            const key = `${lessonIdx}-${pageIdx}`;
            if (!groups.has(key)) groups.set(key, { lessonIdx, pageIdx, indices: [] });
            groups.get(key).indices.push(blkIdx);
        });

        setChapterData((prev) => {
            const lessons = [...prev.lessons];
            const destPages = [...lessons[destLessonIdx].pages];
            const destBlocks = [...destPages[destPageIdx].blocks];

            // Place the first pasted block at the cursor and stack subsequent ones below it.
            let offsetY = 0;
            groups.forEach(({ lessonIdx, pageIdx, indices }) => {
                const sortedIndices = [...indices].sort((a, b) => a - b);
                sortedIndices.forEach((idx) => {
                    const sourceBlock = lessons[lessonIdx]?.pages?.[pageIdx]?.blocks?.[idx];
                    if (!sourceBlock) return;

                    const duplicatedBlock = cloneSessionData(sourceBlock);
                    duplicatedBlock.id = null;
                    duplicatedBlock.order = destBlocks.length;
                    duplicatedBlock.left = Math.max(0, targetLeft ?? 0);
                    duplicatedBlock.top = Math.max(0, (targetTop ?? 0) + offsetY);
                    offsetY += (duplicatedBlock.height || 160) + 10;

                    if (duplicatedBlock.type === 'quiz') {
                        duplicatedBlock.quizBlock = duplicatedBlock.quizBlock
                            ? {
                                ...duplicatedBlock.quizBlock,
                                id: null,
                                lesson_page_block: null,
                                questions: (duplicatedBlock.quizBlock.questions || []).map((question) => ({
                                    ...question,
                                    id: null,
                                    choices: (question.choices || []).map((choice) => ({ ...choice, id: null })),
                                })),
                            }
                            : duplicatedBlock.quizBlock;
                        duplicatedBlock.questions = (duplicatedBlock.questions || []).map((question) => ({
                            ...question,
                            id: null,
                            choices: (question.choices || []).map((choice) => ({ ...choice, id: null })),
                        }));
                    }

                    destBlocks.push(duplicatedBlock);
                });
            });

            destPages[destPageIdx] = { ...destPages[destPageIdx], blocks: destBlocks };
            lessons[destLessonIdx] = { ...lessons[destLessonIdx], pages: destPages };
            return { ...prev, lessons };
        });

        await logChapterChange(`Pasted ${blockRefs.length} block(s)`, { count: blockRefs.length });
    }, [blockContextMenu, blockCreateMenu, cloneSessionData, duplicateBlockRefs, logChapterChange]);

    const deleteBlockRefs = useCallback(async (blockRefs) => {
        if (!blockRefs || !blockRefs.length) return;

        const groups = new Map();
        blockRefs.forEach(({ lessonIdx, pageIdx, blkIdx }) => {
            const key = `${lessonIdx}-${pageIdx}`;
            if (!groups.has(key)) groups.set(key, { lessonIdx, pageIdx, indices: [] });
            groups.get(key).indices.push(blkIdx);
        });

        setChapterData((prev) => {
            const lessons = [...prev.lessons];
            groups.forEach(({ lessonIdx, pageIdx, indices }) => {
                const pages = [...lessons[lessonIdx].pages];
                const blocks = [...pages[pageIdx].blocks];
                const sortedIndices = [...indices].sort((a, b) => b - a);
                sortedIndices.forEach((idx) => blocks.splice(idx, 1));
                pages[pageIdx] = { ...pages[pageIdx], blocks };
                lessons[lessonIdx] = { ...lessons[lessonIdx], pages };
            });
            return { ...prev, lessons };
        });

        await logChapterChange(`Deleted ${blockRefs.length} block(s)`, { count: blockRefs.length });
    }, [logChapterChange]);

    const duplicateSelectedBlocks = useCallback(async () => {
        if (!selectedBlocks.length) return;
        await duplicateBlockRefs(selectedBlocks);
        clearBlockSelection();
    }, [selectedBlocks, duplicateBlockRefs, clearBlockSelection]);

    const copySelectedBlocks = useCallback(() => {
        if (!selectedBlocks.length) return;
        // Store references to the selected block objects instead of copying them
        clipboardBlocksRef.current = selectedBlocks.map((b) => b);
    }, [selectedBlocks]);

    const pasteSelectedBlocks = useCallback(async () => {
        if (!clipboardBlocksRef.current.length) return;
        await pasteBlockRefs(clipboardBlocksRef.current);
    }, [clipboardBlocksRef, pasteBlockRefs]);

    const deleteSelectedBlocks = useCallback(async () => {
        if (!selectedBlocks.length) return;
        // if (!window.confirm(`Delete ${selectedBlocks.length} selected block(s)?`)) return;
        await deleteBlockRefs(selectedBlocks);
        clearBlockSelection();
    }, [selectedBlocks, deleteBlockRefs, clearBlockSelection]);

    const selectAllBlocks = useCallback(() => {
        const allBlocks = [];
        chapterData.lessons.forEach((lesson, lessonIdx) => {
            lesson.pages.forEach((page, pageIdx) => {
                page.blocks.forEach((block, blkIdx) => {
                    allBlocks.push({ lessonIdx, pageIdx, blkIdx });
                });
            });
        });
        setSelectedBlocks(allBlocks);
    }, [chapterData]);

    // Right-click context menu (duplicate / delete for the current selection)
    const handleBlockContextMenu = useCallback((e, lessonIdx, pageIdx, blkIdx) => {
        if (!isStaff() || !editorMode) return;
        e.preventDefault();
        e.stopPropagation();

        if (!isBlockSelected(lessonIdx, pageIdx, blkIdx)) {
            setSelectedBlocks([{ lessonIdx, pageIdx, blkIdx }]);
        }

        // Capture the click position relative to its own design stage now, while the
        // underlying block is still the actual event target -- doing this later (e.g. lazily
        // via document.elementFromPoint when Paste is clicked) would hit the context menu's own
        // DOM instead, since the menu renders directly on top of the click position.
        const stageEl = findStageEl(e.target);
        const stagePoint = stageEl ? clientPointToStage(stageEl, e.clientX, e.clientY) : null;

        setBlockContextMenu({
            x: e.clientX,
            y: e.clientY,
            lessonIdx,
            pageIdx,
            left: stagePoint ? Math.max(0, Math.round(stagePoint.x)) : 0,
            top: stagePoint ? Math.max(0, Math.round(stagePoint.y)) : 0,
        });
    }, [isStaff, editorMode, isBlockSelected]);

    useEffect(() => {
        if (!blockContextMenu) return;
        const handleClickAway = (e) => {
            if (e.target.closest && e.target.closest('.block-context-menu')) return;
            setBlockContextMenu(null);
        };
        document.addEventListener('mousedown', handleClickAway);
        return () => document.removeEventListener('mousedown', handleClickAway);
    }, [blockContextMenu]);

    // Keyboard shortcuts for selected blocks: Ctrl+C / Ctrl+V (clipboard) and Delete (remove)
    useEffect(() => {
        if (!isStaff() || !editorMode) return;

        const handleKeyDown = (e) => {
            const activeEl = document.activeElement;
            const isEditingText = activeEl && (activeEl.isContentEditable || ['INPUT', 'TEXTAREA'].includes(activeEl.tagName));
            if (isEditingText) return;

            const isCtrlOrCmd = e.ctrlKey || e.metaKey;

            if (isCtrlOrCmd && (e.key === 'a' || e.key === 'A')) {
                e.preventDefault();
                selectAllBlocks();
            } else if (isCtrlOrCmd && (e.key === 'c' || e.key === 'C')) {
                if (!selectedBlocks.length) return;
                e.preventDefault();
                copySelectedBlocks();
            } else if (isCtrlOrCmd && (e.key === 'v' || e.key === 'V')) {
                if (!clipboardBlocksRef.current.length) return;
                e.preventDefault();
                pasteBlockRefs(clipboardBlocksRef.current);
            } else if (isCtrlOrCmd && (e.key === 'z' || e.key === 'Z')) {
                e.preventDefault();
                handleUndo();
            } else if (isCtrlOrCmd && (e.key === 'y' || e.key === 'Y')) {
                e.preventDefault();
                handleRedo();
            } else if (isCtrlOrCmd && (e.key === 's' || e.key === 'S')) {
                e.preventDefault();
                saveChapterSession();
            } else if (!isCtrlOrCmd && e.key === 'Delete') {
                if (!selectedBlocks.length) return;
                e.preventDefault();
                deleteSelectedBlocks();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isStaff, editorMode, selectedBlocks, pasteBlockRefs, deleteSelectedBlocks]);

    const applySessionSnapshot = useCallback((snapshot) => {
        isRestoringSessionRef.current = true;
        setChapterData(cloneSessionData(snapshot));
    }, [cloneSessionData]);

    const handleUndo = useCallback(() => {
        if (sessionHistoryIndexRef.current <= 0) return;
        const nextIndex = sessionHistoryIndexRef.current - 1;
        sessionHistoryIndexRef.current = nextIndex;
        setSessionHistoryMeta({ index: nextIndex, length: sessionHistoryRef.current.length });
        applySessionSnapshot(sessionHistoryRef.current[nextIndex]);
    }, [applySessionSnapshot]);

    const handleRedo = useCallback(() => {
        if (sessionHistoryIndexRef.current >= sessionHistoryRef.current.length - 1) return;
        const nextIndex = sessionHistoryIndexRef.current + 1;
        sessionHistoryIndexRef.current = nextIndex;
        setSessionHistoryMeta({ index: nextIndex, length: sessionHistoryRef.current.length });
        applySessionSnapshot(sessionHistoryRef.current[nextIndex]);
    }, [applySessionSnapshot]);

    // Adjust the height of the current page's own lesson-section (persisted per-page via
    // LessonPage.height), rather than a single scale applied to every page.
    const adjustCurrentPageHeight = useCallback((delta) => {
        setChapterData((prev) => {
            const lessons = [...prev.lessons];
            const pages = [...lessons[activeLessonIdx].pages];
            const page = pages[currentSnappedPageIdx];
            if (!page) return prev;

            const currentHeight = page.height ?? 100;
            const newHeight = Math.min(250, Math.max(100, Math.round(currentHeight + delta)));
            if (newHeight === currentHeight) return prev;

            pages[currentSnappedPageIdx] = { ...page, height: newHeight };
            lessons[activeLessonIdx] = { ...lessons[activeLessonIdx], pages };
            return { ...prev, lessons };
        });
    }, [activeLessonIdx, currentSnappedPageIdx]);

    const decreaseLessonSectionSize = useCallback(() => {
        adjustCurrentPageHeight(-10);
    }, [adjustCurrentPageHeight]);

    const increaseLessonSectionSize = useCallback(() => {
        adjustCurrentPageHeight(10);
    }, [adjustCurrentPageHeight]);

    // Page backgrounds are STAGED locally and only written to the backend by saveChapterSession,
    // exactly like every other page/block edit -- so leaving without saving discards them and the
    // "unsaved changes" guard stays truthful.
    //
    // The pending upload can't live in chapterData: cloneSessionData is JSON.parse(JSON.stringify),
    // which would turn a File into {} on the very first history push or save snapshot. So the File
    // sits in this ref, keyed by a plain-string `_bgKey` stamped onto the page -- the key survives
    // cloning, the File never needs to.
    const pendingPageBackgroundsRef = useRef(new Map()); // _bgKey -> { file, type, objectUrl } | { url, type } | { cleared: true }
    const nextPageBgKeyRef = useRef(0);

    // Single staging path shared by the upload / clear / apply-template / clear-page flows, so they
    // can't drift apart. Pass null to drop any staged change for the page.
    const stagePageBackground = useCallback((lessonIdx, pageIdx, pending) => {
        setChapterData((previous) => {
            const lessons = [...previous.lessons];
            const pages = [...lessons[lessonIdx].pages];
            const page = pages[pageIdx];
            if (!page) return previous;
            const bgKey = page._bgKey || `pagebg-${++nextPageBgKeyRef.current}`;

            // Drop any previously staged file for this page, so replacing a background twice before
            // saving doesn't leak the first object URL.
            const previousPending = pendingPageBackgroundsRef.current.get(bgKey);
            if (previousPending?.objectUrl) URL.revokeObjectURL(previousPending.objectUrl);

            if (pending) pendingPageBackgroundsRef.current.set(bgKey, pending);
            else pendingPageBackgroundsRef.current.delete(bgKey);

            pages[pageIdx] = {
                ...page,
                _bgKey: bgKey,
                background_image_url: pending?.objectUrl || pending?.url || '',
                background_url: pending?.url || '',
                background_type: pending?.type || 'image',
            };
            lessons[lessonIdx] = { ...lessons[lessonIdx], pages };
            return { ...previous, lessons };
        });
    }, []);

    // Revoke any object URLs still staged when the editor unmounts.
    useEffect(() => () => {
        pendingPageBackgroundsRef.current.forEach((pending) => {
            if (pending?.objectUrl) URL.revokeObjectURL(pending.objectUrl);
        });
        pendingPageBackgroundsRef.current.clear();
    }, []);

    // Staged like every other edit on the canvas: the blocks are dropped from chapterData and
    // the backend rows go with the next save (syncBlocks deletes any baseline block missing
    // from the snapshot). Deleting them here instead committed a destructive change the user
    // never asked to save, and put it out of reach of undo -- the session history only tracks
    // chapterData, so the rows were already gone server-side by the time you could Ctrl+Z.
    // That undo is also why there's no confirm prompt any more.
    const clearCurrentPageBlocks = useCallback(() => {
        const lesson = chapterData.lessons[activeLessonIdx];
        const page = lesson?.pages?.[currentSnappedPageIdx];
        if (!page) return;

        setChapterData((previous) => {
            const lessons = [...previous.lessons];
            const pages = [...lessons[activeLessonIdx].pages];
            pages[currentSnappedPageIdx] = { ...pages[currentSnappedPageIdx], blocks: [] };
            lessons[activeLessonIdx] = { ...lessons[activeLessonIdx], pages };
            return { ...previous, lessons };
        });

        // "Clear page" means the whole page, background included -- leaving the artwork behind
        // after wiping every block reads as a failed clear.
        if (page.background_image_url || page.background_url) {
            stagePageBackground(activeLessonIdx, currentSnappedPageIdx, { cleared: true, type: 'image' });
        }
    }, [activeLessonIdx, chapterData.lessons, currentSnappedPageIdx, stagePageBackground]);

    const saveChapterSession = useCallback(async () => {
        // Ctrl+S and the Navbar button share this handler, so a second trigger while
        // the first save is still running would double-post every create/delete.
        if (isSavingChapterRef.current) return;

        const snapshot = cloneSessionData(chapterData);
        const baseline = sessionBaselineRef.current || snapshot;

        // Collect all IDs currently in the chapter to avoid accidental deletions when moving items between containers
        const allCurrentLessonIds = new Set();
        const allCurrentPageIds = new Set();
        const allCurrentBlockIds = new Set();

        snapshot.lessons.forEach(l => {
            if (l.id && typeof l.id === 'number') allCurrentLessonIds.add(l.id);
            l.pages?.forEach(p => {
                if (p.id) allCurrentPageIds.add(p.id);
                p.blocks?.forEach(b => { if (b.id) allCurrentBlockIds.add(b.id); });
            });
        });

        const syncQuizChoices = async (questionSnapshot, questionBaseline) => {
            const currentChoices = questionSnapshot?.choices || [];
            const baselineChoices = questionBaseline?.choices || [];
            const currentChoiceIds = new Set(currentChoices.map((choice) => choice.id).filter(Boolean));

            for (const choice of currentChoices) {
                if (choice.id) {
                    try {
                        await updateQuizChoice(choice.id, {
                            text: choice.text || '',
                            is_correct: !!choice.is_correct,
                        });
                    } catch (err) {
                        const createdChoice = await createQuizChoice({
                            quiz_question: questionSnapshot.id,
                            text: choice.text || '',
                            is_correct: !!choice.is_correct,
                        });
                        choice.id = createdChoice.id;
                    }
                } else {
                    const createdChoice = await createQuizChoice({
                        quiz_question: questionSnapshot.id,
                        text: choice.text || '',
                        is_correct: !!choice.is_correct,
                    });
                    choice.id = createdChoice.id;
                }
            }

            for (const choice of baselineChoices) {
                if (choice.id && !currentChoiceIds.has(choice.id)) {
                    await deleteQuizChoice(choice.id);
                }
            }
        };

        const syncQuizQuestions = async (quizBlockSnapshot, quizBaseline) => {
            const currentQuestions = quizBlockSnapshot?.questions || [];
            const baselineQuestions = quizBaseline?.questions || [];
            const currentQuestionIds = new Set(currentQuestions.map((question) => question.id).filter(Boolean));

            for (const question of currentQuestions) {
                if (question.id) {
                    try {
                        await updateQuizQuestion(question.id, { 
                            text: question.text || '',
                            choice_type: question.type || 'single'
                        });
                    } catch (err) {
                            const createdQuestion = await createQuizQuestion({
                            quiz_block: quizBlockSnapshot.id,
                            text: question.text || '',
                            choice_type: question.type || 'single'
                        });
                        question.id = createdQuestion.id;
                        question.quiz_block = quizBlockSnapshot.id;
                    }
                } else {
                    const createdQuestion = await createQuizQuestion({
                        quiz_block: quizBlockSnapshot.id,
                        text: question.text || '',
                        choice_type: question.type || 'single'
                    });
                    question.id = createdQuestion.id;
                    question.quiz_block = quizBlockSnapshot.id;
                }

                const baselineQuestion = baselineQuestions.find((item) => item.id === question.id);
                await syncQuizChoices(question, baselineQuestion || { choices: [] });
            }

            for (const question of baselineQuestions) {
                if (question.id && !currentQuestionIds.has(question.id)) {
                    await deleteQuizQuestion(question.id);
                }
            }
        };

        const syncBlocks = async (pageSnapshot, pageBaseline) => {
            const currentBlocks = pageSnapshot?.blocks || [];
            const baselineBlocks = pageBaseline?.blocks || [];
            
            for (const block of currentBlocks) {
                const payload = {
                    block_type: normalizeBlockType(block.type) || 'card',
                    title: block.title || '',
                    text: block.text || '',
                    title_size: block.titleSize || 24,
                    text_size: block.textSize || 16,
                    title_font: block.titleFont || block.font || 'Inter',
                    title_bold: !!(block.titleBold ?? block.bold),
                    title_layout: block.titleLayout || block.layout || 'left',
                    title_color: block.titleColor || block.color || '#0f172a',
                    title_toggle: !!(block.titleToggle ?? true),
                    text_font: block.textFont || block.font || 'Inter',
                    text_bold: !!(block.textBold ?? block.bold),
                    text_layout: block.textLayout || block.layout || 'left',
                    text_color: block.textColor || block.color || '#0f172a',
                    background_color: (block.backgroundColor || '#ffffff').slice(0, 7),
                    background_opacity: block.backgroundOpacity ?? 1,
                    background_toggle_width: !!block.backgroundToggleWidth,
                    background_corners: block.backgroundCorners || 'Round',
                    background_text: block.backgroundText || '',
                    background_text_toggle: block.backgroundTextToggle ?? true,
                    background_text_size: block.backgroundTextSize || 16,
                    background_text_font: block.backgroundTextFont || block.font || 'Inter',
                    background_text_bold: !!(block.backgroundTextBold ?? block.bold),
                    background_text_layout: block.backgroundTextLayout || block.layout || 'left',
                    background_text_color: block.backgroundTextColor || block.color || '#0f172a',
                    font: block.font || 'Inter',
                    bold: !!block.bold,
                    layout: block.layout || 'left',
                    color: block.color || '#0f172a',
                    width: coerceInteger(block.width, 700),
                    height: coerceInteger(block.height, 200),
                    left_position: coerceInteger(block.left, 0),
                    top_position: coerceInteger(block.top, 0),
                    order: block.order ?? 0,
                    page: pageSnapshot.id,
                };

                // A media block that has never been persisted (id: null) can only have got its
                // media by reference -- currently that means it came from an applied template,
                // whose snapshots carry a URL rather than a copied file. Without sending it here
                // the block would be created with no media at all. Existing blocks are left alone:
                // these are PATCHes, and their media_file already lives server-side.
                if (!block.id && (block.type === 'image' || block.type === 'video') && block.mediaUrl) {
                    payload.URL_media = block.mediaUrl;
                }

                if (block.id) {
                    try {
                        await updateLessonPageBlock(block.id, payload);
                    } catch (err) {
                        const createdBlock = await createLessonPageBlock(payload);
                        block.id = createdBlock.id;
                    }
                } else {
                    const createdBlock = await createLessonPageBlock(payload);
                    block.id = createdBlock.id;
                }

                if (block.type === 'quiz') {
                    const quizBlockSnapshot = block.quizBlock || { id: null, lesson_page_block: block.id, choice_type: 'single', questions: block.questions || [] };
                    if (quizBlockSnapshot.id) {
                        try {
                            await updateQuizBlock(quizBlockSnapshot.id, {
                                choice_type: quizBlockSnapshot.choice_type || 'single',
                            });
                        } catch (err) {
                            const createdQuizBlock = await createQuizBlock({
                                lesson_page_block: block.id,
                                question: quizBlockSnapshot.questions?.[0]?.text || '',
                                choice_type: quizBlockSnapshot.choice_type || 'single',
                            });
                            quizBlockSnapshot.id = createdQuizBlock.id;
                            quizBlockSnapshot.lesson_page_block = block.id;
                        }
                    } else {
                        const createdQuizBlock = await createQuizBlock({
                            lesson_page_block: block.id,
                            choice_type: quizBlockSnapshot.choice_type || 'single',
                        });
                        quizBlockSnapshot.id = createdQuizBlock.id;
                        quizBlockSnapshot.lesson_page_block = block.id;
                        block.quizBlock = quizBlockSnapshot;
                    }
                    await syncQuizQuestions(quizBlockSnapshot, (pageBaseline?.blocks || []).find((item) => item.id === block.id)?.quizBlock || { questions: [] });
                }
            }

            for (const block of baselineBlocks) {
                if (block.id && !allCurrentBlockIds.has(block.id)) {
                    try {
                        await deleteLessonPageBlock(block.id);
                    } catch (err) {
                        // On Windows, video files are often locked by the browser/server during dev.
                        // We catch this so the rest of the chapter save isn't blocked.
                        console.warn(`Server could not delete block ${block.id}. It may be locked by the OS.`, err);
                        // Note: The record remains in baseline until successfully deleted or the UI is refreshed.
                    }
                }
            }
        };

        const syncPages = async (lessonSnapshot, lessonBaseline) => {
            const currentPages = lessonSnapshot?.pages || [];
            const baselinePages = lessonBaseline?.pages || [];

            for (const page of currentPages) {
                const pagePayload = {
                    title: page.title || '',
                    lesson: lessonSnapshot.id,
                    order: page.order ?? 0,
                    part_title_left: page.partTitleLeft || 0,
                    part_title_top: page.partTitleTop || 0,
                    height: page.height ?? 100,
                    // The staged background (see stagePageBackground) is committed here rather than
                    // when it was picked. Carrying it in this payload also means it survives the
                    // createLessonPage fallback below: if the PATCH fails (e.g. the
                    // unique_together (title, lesson) constraint), that branch creates a
                    // REPLACEMENT row and repoints page.id at it, which would otherwise orphan the
                    // background on the abandoned row. An uploaded file can't ride along in JSON,
                    // so it goes as a separate multipart call just after this.
                    background_url: page.background_url || null,
                    background_type: page.background_type || 'image',
                };

                if (page.id) {
                    try {
                        await updateLessonPage(page.id, pagePayload);
                    } catch (err) {
                        const createdPage = await createLessonPage(pagePayload);
                        page.id = createdPage.id;
                    }
                } else {
                    const createdPage = await createLessonPage(pagePayload);
                    page.id = createdPage.id;
                }

                // Commit a staged background change (see stagePageBackground). An uploaded file has
                // to go as multipart, so it's a second call rather than part of pagePayload above.
                // The staged File lives in a ref keyed by _bgKey because `page` here is a JSON
                // clone that could never have carried it.
                const pendingBackground = page._bgKey ? pendingPageBackgroundsRef.current.get(page._bgKey) : null;
                if (pendingBackground?.file) {
                    const backgroundForm = new FormData();
                    backgroundForm.append('background_image', pendingBackground.file);
                    backgroundForm.append('background_type', pendingBackground.type || 'image');
                    // A direct upload supersedes any by-reference background left by a template.
                    backgroundForm.append('background_url', '');
                    const updatedPage = await updateLessonPage(page.id, backgroundForm);
                    // Swap the local blob: preview for the real URL, so the canvas keeps working
                    // after the object URL is revoked below.
                    page.background_image_url = updatedPage.background_image_url || '';
                    page.background_url = updatedPage.background_url || '';
                    page.background_type = updatedPage.background_type || 'image';
                    if (pendingBackground.objectUrl) URL.revokeObjectURL(pendingBackground.objectUrl);
                    pendingPageBackgroundsRef.current.delete(page._bgKey);
                    delete page._bgKey;
                } else if (pendingBackground?.cleared) {
                    await updateLessonPage(page.id, {
                        background_image: null,
                        background_url: null,
                        background_type: 'image',
                    });
                    page.background_image_url = '';
                    page.background_url = '';
                    page.background_type = 'image';
                    pendingPageBackgroundsRef.current.delete(page._bgKey);
                    delete page._bgKey;
                } else if (pendingBackground?.url) {
                    // Template-applied background: already carried by pagePayload's background_url,
                    // but the page may still hold a superseded upload that would otherwise win in
                    // get_background_image_url, so clear the file explicitly.
                    const updatedPage = await updateLessonPage(page.id, { background_image: null });
                    page.background_image_url = updatedPage.background_image_url || pendingBackground.url;
                    pendingPageBackgroundsRef.current.delete(page._bgKey);
                    delete page._bgKey;
                }

                await syncBlocks(page, baselinePages.find((item) => item.id === page.id) || { blocks: [] });
            }

            for (const page of baselinePages) {
                if (page.id && !allCurrentPageIds.has(page.id)) {
                    await deleteLessonPage(page.id);
                }
            }
        };

        const syncLessons = async () => {
            const currentLessons = snapshot.lessons || [];
            const baselineLessons = baseline.lessons || [];

            for (const lesson of currentLessons) {
                const lessonPayload = {
                    title: lesson.title || '',
                    chapter: snapshot.id,
                    order: lesson.order ?? 0,
                    hero_left: lesson.heroLeft || 0,
                    hero_top: lesson.heroTop || 0,
                    header_padding_top: lesson.headerPaddingTop || 60,
                    header_resizer_left: lesson.headerResizerLeft || 50,
                    header_resizer_width: lesson.headerResizerWidth || 0,
                };

                if (lesson.id && typeof lesson.id === 'number') {
                    try {
                        await updateLesson(lesson.id, lessonPayload);
                    } catch (err) {
                        // If title/chapter unique constraint fails, try appending a suffix or ignore if it's the same object
                        if (err.response?.data?.non_field_errors) {
                            await updateLesson(lesson.id, { ...lessonPayload, title: `${lessonPayload.title} (dup)` });
                        } else {
                            throw err;
                        }
                    }
                } else {
                    const createdLesson = await createLesson(lessonPayload);
                    lesson.id = createdLesson.id;
                    lesson.pages = (lesson.pages || []).map((page) => ({
                        ...page,
                        lesson: createdLesson.id,
                    }));
                }

                await syncPages(lesson, baselineLessons.find((item) => item.id === lesson.id) || { pages: [] });
            }

            for (const lesson of baselineLessons) {
                if (lesson.id && !allCurrentLessonIds.has(lesson.id)) {
                    await deleteLesson(lesson.id);
                }
            }
        };

        setSavingChapter(true);
        try {
            await syncLessons();
            isRestoringSessionRef.current = true;
            setChapterData(cloneSessionData(snapshot));
            resetSessionHistory(snapshot);

            sessionBaselineRef.current = cloneSessionData(snapshot);
            sessionHistoryRef.current[sessionHistoryIndexRef.current] = cloneSessionData(snapshot);
            setSessionHistoryMeta({
                index: sessionHistoryIndexRef.current,
                length: sessionHistoryRef.current.length,
            });
            syncCurrentSessionLists(snapshot);
            showToast(t('chapter.changesSaved'));
        } catch (err) {
            console.error('Failed to save chapter session:', err);
            showToast(t('chapter.errors.saveChangesFailed'), 'error');
        } finally {
            setSavingChapter(false);
        }
    }, [chapterData, cloneSessionData, resetSessionHistory, syncCurrentSessionLists, setSavingChapter, showToast, t]);

    // Update block data
    const updateBlockData = (lessonIdx, pageIdx, blkIdx, field, value) => {
        setChapterData(prev => {
            const lessons = [...prev.lessons];
            lessons[lessonIdx].pages[pageIdx].blocks[blkIdx] = {
                ...lessons[lessonIdx].pages[pageIdx].blocks[blkIdx],
                [field]: value
            };
            return { ...prev, lessons };
        });
    };

    const updateTextBlockField = async (lessonIdx, pageIdx, blkIdx, field, value) => {
        const block = chapterData.lessons?.[lessonIdx]?.pages?.[pageIdx]?.blocks?.[blkIdx];
        const previousValue = field === 'title'
            ? (block?.title || '')
            : field === 'background_text'
                ? (block?.backgroundText || '')
                : (block?.text || '');
        const nextValue = value ?? '';

        if (previousValue === nextValue) return;

        updateBlockData(lessonIdx, pageIdx, blkIdx, field, nextValue);

        await logChapterChange(`Updated ${field} content`, {
            block_type: normalizeBlockType(block?.type) || 'text',
            field,
            old_value: previousValue,
            new_value: nextValue,
        });
    };

    const createTextualBlock = useCallback(async (blockType, options = {}) => {
        const targetLessonIdx = options.lessonIdx ?? activeLessonIdx;
        const targetPageIdx = options.pageIdx ?? currentSnappedPageIdx;
        const defaultBlock = {
            id: null,
            type: blockType,
            title: blockType === 'title' ? 'New Title' : (blockType === 'card' ? 'title' : ''),
            text: blockType === 'card' ? 'text' : '',
            placeholder: blockType === 'text' ? 'Type your text here...' : '',
            titleSize: 28,
            textSize: 18,
            titleFont: 'Inter',
            titleBold: blockType === 'title',
            titleLayout: 'left',
            titleColor: '#0f172a',
            titleToggle: blockType === 'card',
            textFont: 'Inter',
            textBold: false,
            textLayout: 'left',
            textColor: '#0f172a',
            backgroundColor: (modalType === 'title' || modalType === 'text') ? 'transparent' : '#ffffff',
            backgroundOpacity: 1,
            backgroundToggleWidth: false,
            backgroundCorners: 'Round',
            backgroundText: blockType === 'background' ? 'Background text' : '',
            backgroundTextToggle: blockType === 'background',
            backgroundTextSize: 16,
            backgroundTextFont: 'Inter',
            backgroundTextBold: false,
            backgroundTextLayout: 'left',
            backgroundTextColor: '#0f172a',
            font: 'Inter',
            bold: blockType === 'title',
            layout: 'left',
            color: '#0f172a',
            width: blockType === 'title' ? 520 : 700,
            height: 160,
            left: options.left ?? 0,
            top: options.top ?? 0,
            order: 0,
        };

        setChapterData((previous) => {
            const lessons = [...previous.lessons];
            const pages = [...lessons[targetLessonIdx].pages];
            const blocks = [...pages[targetPageIdx].blocks];
            defaultBlock.order = blocks.length;
            blocks.push(defaultBlock);
            pages[targetPageIdx] = { ...pages[targetPageIdx], blocks };
            lessons[targetLessonIdx] = { ...lessons[targetLessonIdx], pages };
            return { ...previous, lessons };
        });

        await logChapterChange(`Added ${blockType} block`, { block_type: blockType });
    }, [activeLessonIdx, currentSnappedPageIdx, logChapterChange]);

    const createBackgroundBlock = useCallback(async (options = {}) => {
        const targetLessonIdx = options.lessonIdx ?? activeLessonIdx;
        const targetPageIdx = options.pageIdx ?? currentSnappedPageIdx;
        const defaultBlock = {
            id: null,
            type: 'background',
            title: '',
            text: '',
            backgroundColor: '#dbeafe',
            backgroundOpacity: 1,
            backgroundToggleWidth: false,
            backgroundCorners: 'Round',
            backgroundText: 'Background text',
            backgroundTextToggle: true,
            backgroundTextSize: 16,
            backgroundTextFont: 'Inter',
            backgroundTextBold: false,
            backgroundTextLayout: 'left',
            backgroundTextColor: '#0f172a',
            font: 'Inter',
            bold: false,
            layout: 'left',
            color: '#0f172a',
            width: 700,
            height: 220,
            left: options.left ?? 0,
            top: options.top ?? 0,
            order: 0,
        };

        setChapterData((previous) => {
            const lessons = [...previous.lessons];
            const pages = [...lessons[targetLessonIdx].pages];
            const blocks = [...pages[targetPageIdx].blocks];
            defaultBlock.order = blocks.length;
            blocks.push(defaultBlock);
            pages[targetPageIdx] = { ...pages[targetPageIdx], blocks };
            lessons[targetLessonIdx] = { ...lessons[targetLessonIdx], pages };
            return { ...previous, lessons };
        });

        await logChapterChange('Added background block', { block_type: 'background' });
    }, [activeLessonIdx, currentSnappedPageIdx, logChapterChange]);

    // A lesson/page added locally (e.g. via "+ Add New Lesson") only exists in chapterData --
    // id: null -- until the whole chapter is explicitly saved. Media blocks, unlike text/quiz
    // blocks, are persisted to the backend immediately on creation (ModalBlock.handleMediaSave
    // needs a real page id to attach to), so trying to add one before that save previously failed
    // outright. Persist the lesson/page here first so media blocks can be added right away too.
    const ensureLessonPageSaved = useCallback(async (lessonIdx, pageIdx) => {
        const lesson = chapterData.lessons[lessonIdx];
        let lessonId = lesson.id;

        if (!lessonId) {
            const createdLesson = await createLesson({
                title: lesson.title || '',
                chapter: chapterData.id,
                order: lesson.order ?? lessonIdx,
                hero_left: lesson.heroLeft || 0,
                hero_top: lesson.heroTop || 0,
                header_padding_top: lesson.headerPaddingTop || 60,
                header_resizer_left: lesson.headerResizerLeft || 50,
                header_resizer_width: lesson.headerResizerWidth || 0,
            });
            lessonId = createdLesson.id;
        }

        const page = lesson.pages[pageIdx];
        let pageId = page.id;

        if (!pageId) {
            const createdPage = await createLessonPage({
                title: page.title || '',
                lesson: lessonId,
                order: page.order ?? pageIdx,
                part_title_left: page.partTitleLeft || 0,
                part_title_top: page.partTitleTop || 0,
                height: page.height ?? 100,
            });
            pageId = createdPage.id;
        }

        if (lessonId !== lesson.id || pageId !== page.id) {
            setChapterData((previous) => {
                const lessons = [...previous.lessons];
                const pages = [...lessons[lessonIdx].pages];
                pages[pageIdx] = { ...pages[pageIdx], id: pageId, lesson: lessonId };
                lessons[lessonIdx] = { ...lessons[lessonIdx], id: lessonId, pages };
                return { ...previous, lessons };
            });
        }

        return { lessonId, pageId };
    }, [chapterData]);

    // Creates a block of any type at a stage-relative (left, top) on the given
    // lesson/page -- shared by the right-click "new block" menu and toolbar drag-and-drop.
    const createBlockAt = useCallback(async (type, lessonIdx, pageIdx, left, top) => {
        if (type === 'card' || type === 'text' || type === 'title') {
            await createTextualBlock(type, { lessonIdx, pageIdx, left, top });
        } else if (type === 'background') {
            await createBackgroundBlock({ lessonIdx, pageIdx, left, top });
        } else {
            // image / video / quiz are created through the modal flow. Image/video are persisted
            // to the backend as soon as the modal is submitted, so they need a real lesson/page
            // id to attach to; quiz blocks stay fully local until the chapter is saved.
            if (type === 'image' || type === 'video') {
                try {
                    await ensureLessonPageSaved(lessonIdx, pageIdx);
                } catch (err) {
                    console.error('Error saving lesson/page before adding media block:', err);
                    alert(t('chapter.errors.saveLessonBeforeBlock'));
                    return;
                }
            }
            setEditingBlock({ l: lessonIdx, p: pageIdx, b: null, left, top });
            setModalBlockAction('Add');
            setModalHeading('');
            setModalBody('');
            setModalHeadingSize(24);
            setModalBodySize(16);
            setModalType(type);
            setIsModalOpen(true);
        }
    }, [createTextualBlock, createBackgroundBlock, ensureLessonPageSaved, t]);

    // Create a new block of any type at the position the "new block" context menu was opened at
    const handleCreateBlockFromMenu = useCallback(async (type) => {
        if (!blockCreateMenu) return;
        const { lessonIdx, pageIdx, left, top } = blockCreateMenu;
        setBlockCreateMenu(null);
        await createBlockAt(type, lessonIdx, pageIdx, left, top);
    }, [blockCreateMenu, createBlockAt]);

    // Toolbar tool-pill dropped onto a page's canvas: create a block of the dragged type at the
    // drop point, using the same stage-relative math as the right-click menu.
    const handleBlockTypeDrop = useCallback(async (e, lessonIdx, pageIdx) => {
        e.preventDefault();
        setDragOverPageKey(null);
        const type = e.dataTransfer.getData(DRAG_BLOCK_TYPE_MIME);
        if (!type) return;
        const stagePoint = clientPointToStage(findStageEl(e.currentTarget), e.clientX, e.clientY);
        await createBlockAt(
            type, lessonIdx, pageIdx,
            Math.max(0, Math.round(stagePoint.x)),
            Math.max(0, Math.round(stagePoint.y)),
        );
    }, [createBlockAt]);

    // ---------------------------------------------------------------- page background / templates
    // Page backgrounds are STAGED locally and only written to the backend by saveChapterSession,
    // exactly like every other page/block edit -- so leaving without saving discards them and the
    // "unsaved changes" guard stays truthful.
    //
    const setCurrentPageBackground = useCallback(async ({ file, type }) => {
        // Local object URL for immediate preview; swapped for the real server URL on save.
        stagePageBackground(activeLessonIdx, currentSnappedPageIdx, {
            file,
            type: type || 'image',
            objectUrl: URL.createObjectURL(file),
        });
        await logChapterChange('Set page background', { page: currentSnappedPageIdx });
    }, [activeLessonIdx, currentSnappedPageIdx, stagePageBackground, logChapterChange]);

    const clearCurrentPageBackground = useCallback(async () => {
        stagePageBackground(activeLessonIdx, currentSnappedPageIdx, { cleared: true, type: 'image' });
        await logChapterChange('Removed page background', { page: currentSnappedPageIdx });
    }, [activeLessonIdx, currentSnappedPageIdx, stagePageBackground, logChapterChange]);

    const applyTemplateToCurrentPage = useCallback(async ({ blocks = [], backgroundUrl = '', backgroundType = 'image' }) => {
        const lessonIdx = activeLessonIdx;
        const pageIdx = currentSnappedPageIdx;

        // Template snapshots are stored in the API's snake_case block shape (see models.py's
        // Template docstring), so they go back through the same mapper the initial chapter load
        // uses -- with id nulled so applying creates fresh rows instead of trying to overwrite the
        // blocks the template was captured from.
        const localBlocks = blocks.map((snapshot, index) => ({
            ...mapApiBlockToLocal({ ...snapshot, id: null }),
            order: index,
        }));

        setChapterData((previous) => {
            const lessons = [...previous.lessons];
            const pages = [...lessons[lessonIdx].pages];
            pages[pageIdx] = { ...pages[pageIdx], blocks: localBlocks };
            lessons[lessonIdx] = { ...lessons[lessonIdx], pages };
            return { ...previous, lessons };
        });
        clearBlockSelection();

        if (backgroundUrl) {
            // By-reference URL, so no file to stage -- but it still only reaches the backend on
            // save, same as the blocks above.
            stagePageBackground(lessonIdx, pageIdx, { url: backgroundUrl, type: backgroundType });
        }

        await logChapterChange('Applied template to page', { blocks: localBlocks.length });
    }, [activeLessonIdx, currentSnappedPageIdx, stagePageBackground, clearBlockSelection, logChapterChange]);

    // Flat lesson/page list the slideshow steps through, in reading order.
    const slideshowSlides = useMemo(() => {
        const slides = [];
        (chapterData.lessons || []).forEach((lesson, lessonIdx) => {
            (lesson.pages || []).forEach((page, pageIdx) => {
                slides.push({ lessonIdx, pageIdx });
            });
        });
        return slides;
    }, [chapterData]);

    // Set when a slideshow step crosses into another lesson, and consumed by the effect below once
    // that lesson has actually rendered.
    const pendingSlideScrollRef = useRef(null);

    const handleSlideshowGoTo = useCallback((lessonIdx, pageIdx) => {
        if (lessonIdx !== activeLessonIdx) {
            // Deferred rather than scrolled here: loadLesson only queues a state update, so the
            // target page isn't in the DOM yet. (A single requestAnimationFrame isn't enough
            // either -- it can fire before React commits, leaving getElementById empty and the
            // slide stuck on the new lesson's first page, which is exactly what stepping back into
            // a previous lesson used to do.)
            pendingSlideScrollRef.current = { lessonIdx, pageIdx };
            loadLesson(lessonIdx);
            return;
        }

        // Deliberately NOT scrollToPage: that animates over 2s, which is the wrong feel for a
        // slide change and leaves a long window for the page-clamp handlers to interfere. A slide
        // should just be there.
        setCurrentSnappedPageIdx(pageIdx);
        const target = document.getElementById(`page-${lessonIdx}-${pageIdx}`);
        const wrapper = canvasWrapperRef.current;
        if (target && wrapper) wrapper.scrollTop = target.offsetTop;
    }, [activeLessonIdx, loadLesson]);

    useEffect(() => {
        const pending = pendingSlideScrollRef.current;
        if (!pending || pending.lessonIdx !== activeLessonIdx) return;
        pendingSlideScrollRef.current = null;

        // Set explicitly instead of leaving it to scrollToPage: that only updates the snapped index
        // when the target matches the activeLessonIdx captured in its own closure, which is still
        // the OLD lesson on the render that switches lessons. Left at loadLesson's 0, the
        // scroll-clamping effect would then fight this scroll back to the first page.
        setCurrentSnappedPageIdx(pending.pageIdx);

        const target = document.getElementById(`page-${pending.lessonIdx}-${pending.pageIdx}`);
        const wrapper = canvasWrapperRef.current;
        // Jump rather than smooth-scroll: this is a slide change, and a 2s animated scroll across a
        // whole lesson would both feel broken and risk being interrupted mid-flight.
        if (target && wrapper) wrapper.scrollTop = target.offsetTop;
    }, [activeLessonIdx, chapterData]);

    // Handle image upload
    const handleImageUpload = async (e) => {
        try {
            await ensureLessonPageSaved(activeLessonIdx, currentSnappedPageIdx);
        } catch (err) {
            console.error('Error saving lesson/page before adding image block:', err);
            alert(t('chapter.errors.saveLessonBeforeBlock'));
            return;
        }
        setEditingBlock({ l: activeLessonIdx, p: currentSnappedPageIdx, b: null });
        setModalBlockAction('Add');
        setModalHeading('');
        setModalBody('');
        setModalHeadingSize(24);
        setModalBodySize(16);
        setModalType('image');
        setIsModalOpen(true);
    };

    // Handle video upload
    const handleVideoUpload = async (e) => {
        try {
            await ensureLessonPageSaved(activeLessonIdx, currentSnappedPageIdx);
        } catch (err) {
            console.error('Error saving lesson/page before adding video block:', err);
            alert(t('chapter.errors.saveLessonBeforeBlock'));
            return;
        }
        setEditingBlock({ l: activeLessonIdx, p: currentSnappedPageIdx, b: null });
        setModalBlockAction('Add');
        setModalHeading('');
        setModalBody('');
        setModalHeadingSize(24);
        setModalBodySize(16);
        setModalType('video');
        setIsModalOpen(true);
    };

    // Trigger uploads
    const triggerImageUpload = () => handleImageUpload();
    const triggerVideoUpload = () => handleVideoUpload();

    // Handle lesson reorder
    const handleLessonReorder = async (newOrder) => {
        setChapterData(prev => ({ ...prev, lessons: newOrder }));
        
        // Update order in backend
        try {
            await Promise.all(newOrder.map((lesson, index) => {
                if (lesson.id && lesson.order !== index) {
                    return updateLesson(lesson.id, { order: index });
                }
                return Promise.resolve();
            }));
        } catch (err) {
            console.error('Error reordering lessons:', err);
        }
    };

    // Handle block drag start
    const handleBlockDragStart = (e, lessonIdx, pageIdx, blkIdx) => {
        if (!isStaff() || !editorMode) return;
        e.preventDefault();
        e.stopPropagation();

        isSessionHistoryPausedRef.current = true;
        
        const block = chapterData.lessons[lessonIdx].pages[pageIdx].blocks[blkIdx];
        const blockEl = e.target.closest('.content-block');
        const stageEl = findStageEl(blockEl);
        const containerRect = stageEl.getBoundingClientRect();
        const scale = readStageScale(stageEl);
        const blockRect = blockEl.getBoundingClientRect();
        // Both rects are post-transform, so their difference is in scaled px -- divide back to
        // stage units before it can stand in for a stored block.left/top.
        const fallbackLeft = (blockRect.left - containerRect.left) / scale;
        const fallbackTop = (blockRect.top - containerRect.top) / scale;

        setDragState({
            lessonIdx,
            pageIdx,
            blkIdx,
            blockEl,
            startX: e.clientX,
            startY: e.clientY,
            scale,
            startLeft: coerceInteger(block.left, fallbackLeft),
            startTop: coerceInteger(block.top, fallbackTop),
            originalLeft: coerceInteger(block.left, fallbackLeft),
            originalTop: coerceInteger(block.top, fallbackTop),
            containerRect
        });
        
        document.body.classList.add('sliding-block');
    };

    // Handle block drag move
    useEffect(() => {
        if (!dragState) return;
        
        const handleMouseMove = (e) => {
            // Cursor travel is in screen px; the stage is scaled, so a 100px drag on a
            // half-scale stage must move the block 200 stage px to stay under the pointer.
            const scale = dragState.scale || 1;
            const deltaX = (e.clientX - dragState.startX) / scale;
            const deltaY = (e.clientY - dragState.startY) / scale;

            let newLeft = Math.max(0, dragState.startLeft + deltaX);
            let newTop = Math.max(0, dragState.startTop + deltaY);

            // Apply snap guides
            const canvasRect = canvasWrapperRef.current?.getBoundingClientRect();
            if (canvasRect && dragState.blockEl) {
                // Apply center/left snap (use 0 for left snap like hero-card)
                const snappedLeft = calculateSnap(dragState.blockEl, newLeft, dragState.containerRect, canvasRect, 0, scale);
                if (snappedLeft !== newLeft) newLeft = snappedLeft;

                // Apply object-to-object snap
                const objectSnap = calculateObjectSnap(dragState.blockEl, newLeft, newTop, dragState.containerRect, scale);
                if (objectSnap.left !== newLeft) newLeft = objectSnap.left;
                if (objectSnap.top !== newTop) newTop = objectSnap.top;
            }
            
            setChapterData(prev => {
                const lessons = [...prev.lessons];
                const blocks = [...lessons[dragState.lessonIdx].pages[dragState.pageIdx].blocks];
                blocks[dragState.blkIdx] = {
                    ...blocks[dragState.blkIdx],
                    left: newLeft,
                    top: newTop
                };
                lessons[dragState.lessonIdx].pages[dragState.pageIdx] = {
                    ...lessons[dragState.lessonIdx].pages[dragState.pageIdx],
                    blocks
                };
                return { ...prev, lessons };
            });
        };
        
        const handleMouseUp = async () => {
            // Save position to backend
            const block = chapterData.lessons[dragState.lessonIdx].pages[dragState.pageIdx].blocks[dragState.blkIdx];
            if (block.id) {
                try {
                    const movedLeft = coerceInteger(block.left, dragState.startLeft);
                    const movedTop = coerceInteger(block.top, dragState.startTop);

                    if (
                        movedLeft !== dragState.originalLeft ||
                        movedTop !== dragState.originalTop
                    ) {
                        await logChapterChange(`Moved ${block.type || 'content'} block`, {
                            block_type: block.type || 'unknown',
                            from: {
                                left: dragState.originalLeft,
                                top: dragState.originalTop,
                            },
                            to: {
                                left: movedLeft,
                                top: movedTop,
                            },
                        });
                    }
                } catch (err) {
                    console.error('Error saving block position:', err);
                }
            }
            setDragState(null);
            document.body.classList.remove('sliding-block');

            // Clear snap guides
            guideCenterRef.current?.classList.remove('locked');
            guideLeftRef.current?.classList.remove('locked');
            guideRightRef.current?.classList.remove('locked');
            if (guideAlignHRef.current) guideAlignHRef.current.style.display = 'none';
            if (guideAlignVRef.current) guideAlignVRef.current.style.display = 'none';
        };
        
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [dragState, chapterData, calculateSnap, calculateObjectSnap, logChapterChange]);

    // Handle block resize start
    const handleResizeStart = (e, lessonIdx, pageIdx, blkIdx, direction) => {
        if (!isStaff() || !editorMode) return;
        e.preventDefault();
        e.stopPropagation();
        
        const block = chapterData.lessons[lessonIdx].pages[pageIdx].blocks[blkIdx];

        setResizeState({
            lessonIdx,
            pageIdx,
            blkIdx,
            direction,
            startX: e.clientX,
            startY: e.clientY,
            // block.width/height are stage units, so the cursor delta below has to be
            // converted into the same space before it is added to them.
            scale: readStageScale(findStageEl(e.target)),
            startWidth: block.width || 700,
            startHeight: block.height || 200
        });
        
        document.body.classList.add('resizing');
    };

    // Handle block resize move
    useEffect(() => {
        if (!resizeState) return;
        
        const handleMouseMove = (e) => {
            const scale = resizeState.scale || 1;
            const deltaX = (e.clientX - resizeState.startX) / scale;
            const deltaY = (e.clientY - resizeState.startY) / scale;

            let newWidth = resizeState.startWidth;
            let newHeight = resizeState.startHeight;
            
            if (resizeState.direction.includes('r')) {
                newWidth = Math.max(100, resizeState.startWidth + deltaX);
            }
            if (resizeState.direction.includes('b')) {
                newHeight = Math.max(50, resizeState.startHeight + deltaY);
            }
            
            setChapterData(prev => {
                const lessons = [...prev.lessons];
                const blocks = [...lessons[resizeState.lessonIdx].pages[resizeState.pageIdx].blocks];
                blocks[resizeState.blkIdx] = {
                    ...blocks[resizeState.blkIdx],
                    width: newWidth,
                    height: newHeight
                };
                lessons[resizeState.lessonIdx].pages[resizeState.pageIdx] = {
                    ...lessons[resizeState.lessonIdx].pages[resizeState.pageIdx],
                    blocks
                };
                return { ...prev, lessons };
            });
        };
        
        const handleMouseUp = async () => {
            // Save size to backend
            const block = chapterData.lessons[resizeState.lessonIdx].pages[resizeState.pageIdx].blocks[resizeState.blkIdx];

            setResizeState(null);
            document.body.classList.remove('resizing');
        };
        
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [resizeState, chapterData]);

    // Handle element drag start (hero card, part title, header area)
    const handleElementDragStart = useCallback((e, elementType, lessonIdx = null, pageIdx = null) => {
        if (!isStaff() || !editorMode) return;
        
        const target = e.target.closest('.lesson-hero-card, .part-title-container, .header-area');
        if (!target || e.target.isContentEditable || e.target.closest('button, input, textarea')) return;
        
        e.preventDefault();
        
        const computedStyle = getComputedStyle(target);
        const styleLeft = parseInt(computedStyle.left, 10) || 0;
        const styleTop = parseInt(computedStyle.top, 10) || 0;
        const flowLeft = target.offsetLeft - styleLeft;
        const flowTop = target.offsetTop - styleTop;
        
        setElementDragState({
            target,
            elementType,
            lessonIdx,
            pageIdx,
            startX: e.clientX,
            startY: e.clientY,
            styleLeft,
            styleTop,
            flowLeft,
            flowTop,
            defaultStartLeft: 0
        });
        
        document.body.classList.add('sliding-block');
    }, [isStaff, editorMode]);

    // Handle element drag move
    useEffect(() => {
        if (!elementDragState) return;
        
        const { target, elementType, lessonIdx, pageIdx, startX, startY, styleLeft, styleTop, flowLeft, flowTop, defaultStartLeft } = elementDragState;
        
        const handleMouseMove = (e) => {
            const deltaX = e.clientX - startX;
            const deltaY = e.clientY - startY;
            
            let currentVisualLeft = flowLeft + styleLeft + deltaX;
            let currentVisualTop = flowTop + styleTop + deltaY;
            
            const canvasRect = canvasWrapperRef.current?.getBoundingClientRect();
            const parentRect = target.parentElement?.getBoundingClientRect();
            
            if (canvasRect && parentRect) {
                // Apply center/left snap
                const screenSnapLeft = calculateSnap(target, currentVisualLeft, parentRect, canvasRect, defaultStartLeft);
                if (screenSnapLeft !== currentVisualLeft) currentVisualLeft = screenSnapLeft;
                
                // Apply object-to-object snap
                const objectSnap = calculateObjectSnap(target, currentVisualLeft, currentVisualTop, parentRect);
                if (objectSnap.left !== currentVisualLeft) currentVisualLeft = objectSnap.left;
                if (objectSnap.top !== currentVisualTop) currentVisualTop = objectSnap.top;
            }
            
            target.style.left = (currentVisualLeft - flowLeft) + 'px';
            target.style.top = (currentVisualTop - flowTop) + 'px';
        };
        
        const handleMouseUp = async () => {
            // Get final position from target style
            const finalLeft = parseInt(target.style.left, 10) || 0;
            const finalTop = parseInt(target.style.top, 10) || 0;
            
            // Save position to backend based on element type
            try {
                if (elementType === 'hero-card' && lessonIdx !== null) {
                    const lesson = chapterData.lessons[lessonIdx];
                    if (lesson.id) {
                        await updateLesson(lesson.id, {
                            hero_left: finalLeft,
                            hero_top: finalTop
                        });
                        // Update local state
                        setChapterData(prev => {
                            const lessons = [...prev.lessons];
                            lessons[lessonIdx] = { ...lessons[lessonIdx], heroLeft: finalLeft, heroTop: finalTop };
                            return { ...prev, lessons };
                        });
                    }
                } else if (elementType === 'part-title' && lessonIdx !== null && pageIdx !== null) {
                    const page = chapterData.lessons[lessonIdx].pages[pageIdx];
                    if (page.id) {
                        await updateLessonPage(page.id, {
                            part_title_left: finalLeft,
                            part_title_top: finalTop
                        });
                        // Update local state
                        setChapterData(prev => {
                            const lessons = [...prev.lessons];
                            lessons[lessonIdx].pages[pageIdx] = { 
                                ...lessons[lessonIdx].pages[pageIdx], 
                                partTitleLeft: finalLeft, 
                                partTitleTop: finalTop 
                            };
                            return { ...prev, lessons };
                        });
                    }
                }
            } catch (err) {
                console.error('Error saving element position:', err);
            }
            
            document.body.classList.remove('sliding-block');
            guideCenterRef.current?.classList.remove('locked');
            guideLeftRef.current?.classList.remove('locked');
            guideRightRef.current?.classList.remove('locked');
            if (guideAlignHRef.current) guideAlignHRef.current.style.display = 'none';
            if (guideAlignVRef.current) guideAlignVRef.current.style.display = 'none';
            setElementDragState(null);
        };
        
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [elementDragState, calculateSnap, calculateObjectSnap, chapterData]);

    // Handle header resizer drag
    const handleHeaderResizerDrag = useCallback((e, lessonIdx) => {
        if (!isStaff() || !editorMode) return;
        e.preventDefault();
        e.stopPropagation();
        
        const resizer = e.target.closest('.header-resizer');
        if (!resizer) return;
        
        const header = resizer.closest('.header-area');
        const hero = header?.querySelector('.lesson-hero-card');
        
        if (!header || !hero) return;
        
        const headerRect = header.getBoundingClientRect();
        const heroRect = hero.getBoundingClientRect();
        const heroStyle = window.getComputedStyle(hero);
        const heroPaddingLeft = parseFloat(heroStyle.paddingLeft) || 0;
        const textStartRelativeLeft = (heroRect.left - headerRect.left) + heroPaddingLeft;
        const heroCenter = (heroRect.left - headerRect.left) + (hero.offsetWidth / 2);
        
        setHeaderResizeState({
            resizer,
            header,
            lessonIdx,
            startX: e.clientX,
            startY: e.clientY,
            startLeft: parseInt(resizer.style.left || resizer.offsetLeft, 10),
            startPadding: parseInt(window.getComputedStyle(header).paddingTop, 10),
            textStartRelativeLeft,
            heroCenter
        });
        
        document.body.classList.add('resizing-header');
    }, [isStaff, editorMode]);

    // Handle header resizer move
    useEffect(() => {
        if (!headerResizeState) return;
        
        const { resizer, header, lessonIdx, startX, startY, startLeft, startPadding, textStartRelativeLeft, heroCenter } = headerResizeState;
        
        const handleMouseMove = (e) => {
            const deltaY = e.clientY - startY;
            let newPad = startPadding + deltaY;
            if (newPad < 20) newPad = 20;
            header.style.paddingTop = newPad + 'px';
            
            const deltaX = e.clientX - startX;
            let finalLeft = startLeft + deltaX;
            const canvasRect = canvasWrapperRef.current?.getBoundingClientRect();
            const parentRect = header.getBoundingClientRect();
            let snapped = false;
            
            if (Math.abs(finalLeft - textStartRelativeLeft) < 25) {
                finalLeft = textStartRelativeLeft;
                if (guideLeftRef.current) {
                    guideLeftRef.current.style.left = (parentRect.left + finalLeft - canvasRect.left) + 'px';
                    guideLeftRef.current.classList.add('locked');
                }
                snapped = true;
            }
            
            if (!snapped && Math.abs((finalLeft + resizer.offsetWidth / 2) - heroCenter) < 20) {
                finalLeft = heroCenter - (resizer.offsetWidth / 2);
                guideCenterRef.current?.classList.add('locked');
                snapped = true;
            }
            
            if (!snapped) {
                guideCenterRef.current?.classList.remove('locked');
                guideLeftRef.current?.classList.remove('locked');
            }
            
            resizer.style.left = finalLeft + 'px';
        };
        
        const handleMouseUp = async () => {
            // Get final values
            const finalPadding = parseInt(header.style.paddingTop, 10) || 60;
            const finalLeft = parseInt(resizer.style.left, 10) || 50;
            const finalWidth = resizer.offsetWidth || 0;
            
            // Save to backend
            try {
                const lesson = chapterData.lessons[lessonIdx];
                if (lesson.id) {
                    await updateLesson(lesson.id, {
                        header_padding_top: finalPadding,
                        header_resizer_left: finalLeft,
                        header_resizer_width: finalWidth
                    });
                    // Update local state
                    setChapterData(prev => {
                        const lessons = [...prev.lessons];
                        lessons[lessonIdx] = { 
                            ...lessons[lessonIdx], 
                            headerPaddingTop: finalPadding,
                            headerResizerLeft: finalLeft,
                            headerResizerWidth: finalWidth
                        };
                        return { ...prev, lessons };
                    });
                }
            } catch (err) {
                console.error('Error saving header resizer position:', err);
            }
            
            document.body.classList.remove('resizing-header');
            guideCenterRef.current?.classList.remove('locked');
            guideLeftRef.current?.classList.remove('locked');
            setHeaderResizeState(null);
        };
        
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [headerResizeState, chapterData]);

    // Handle header line resize (left/right handles)
    const handleHeaderLineResize = useCallback((e, lessonIdx) => {
        if (!isStaff() || editorMode) return;
        e.preventDefault();
        e.stopPropagation();
        
        const handle = e.target;
        const resizer = handle.closest('.header-resizer');
        if (!resizer) return;
        
        const isRight = handle.classList.contains('hr-handle-r');
        const header = resizer.closest('.header-area');
        const headerRect = header.getBoundingClientRect();
        const canvasRect = canvasWrapperRef.current?.getBoundingClientRect();
        const originalLeft = 50;
        const originalWidth = headerRect.width - 100;
        
        const startX = e.clientX;
        const startWidth = resizer.offsetWidth;
        const startLeft = parseInt(resizer.style.left || resizer.offsetLeft, 10);
        
        document.body.classList.add('resizing-line');
        
        const onMove = (me) => {
            const dx = me.clientX - startX;
            let newWidth = startWidth;
            let newLeft = startLeft;
            let isSnapped = false;
            
            if (isRight) {
                newWidth = Math.max(50, startWidth + dx);
                if (Math.abs(newWidth - originalWidth) < 25) {
                    newWidth = originalWidth;
                    isSnapped = true;
                    const guideX = (headerRect.left - canvasRect.left) + originalLeft + originalWidth;
                    if (guideLeftRef.current) {
                        guideLeftRef.current.style.left = guideX + 'px';
                        guideLeftRef.current.classList.add('locked');
                    }
                }
                resizer.style.width = newWidth + 'px';
            } else {
                newWidth = Math.max(50, startWidth - dx);
                if (newWidth !== startWidth) {
                    newLeft = startLeft + (startWidth - newWidth);
                    if (Math.abs(newLeft - originalLeft) < 25) {
                        const diff = newLeft - originalLeft;
                        newLeft = originalLeft;
                        newWidth = newWidth + diff;
                        isSnapped = true;
                        const guideX = (headerRect.left - canvasRect.left) + originalLeft;
                        if (guideLeftRef.current) {
                            guideLeftRef.current.style.left = guideX + 'px';
                            guideLeftRef.current.classList.add('locked');
                        }
                    }
                    resizer.style.width = newWidth + 'px';
                    resizer.style.left = newLeft + 'px';
                }
            }
            
            if (!isSnapped) guideLeftRef.current?.classList.remove('locked');
        };
        
        const onUp = () => {
            document.body.classList.remove('resizing-line');
            guideLeftRef.current?.classList.remove('locked');
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
        };
        
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    }, [isStaff, editorMode]);

    // Reports a video block's watched progress to the backend, creating the UserLessonVideoProgress
    // record on first report or updating it thereafter (the endpoint itself create-or-updates by
    // lesson_page_block, so the frontend doesn't need to track which case it is). The backend
    // clamps watched_timestamp/video_completed so they can never regress, so it's safe to always
    // send whatever Video.jsx last computed (which is itself the max watched, not the raw playhead).
    const handleVideoProgress = useCallback(async (blockId, { watchedTimestamp, totalDuration, completed }) => {
        if (!currentUser?.id || blockId == null) return;
        try {
            const updated = await createUserLessonVideoProgress({
                lesson_page_block: blockId,
                total_duration: totalDuration,
                watched_timestamp: watchedTimestamp,
                video_completed: completed,
            });
            setUserLessonVideoProgresses(prev => {
                const idx = prev.findIndex(p => p.id === updated.id);
                if (idx === -1) return [...prev, updated];
                const next = [...prev];
                next[idx] = updated;
                return next;
            });
        } catch (error) {
            console.error('Error updating user lesson video progress:', error);
        }
    }, [currentUser?.id]);

    // Creates a UserQuizAttempt the moment a quiz block is encountered (so it exists before the
    // user ever submits), and updates it on submit -- mirrors handleVideoProgress. The endpoint
    // create-or-updates by quiz_block, so an empty attemptData just ensures the record exists
    // without touching an already-recorded completed/passed state.
    const handleQuizAttempt = useCallback(async (quizBlockId, attemptData = {}) => {
        if (!currentUser?.id || quizBlockId == null) return;
        try {
            const updated = await createUserQuizAttempt({ quiz_block: quizBlockId, ...attemptData });
            setUserQuizAttempts(prev => {
                const idx = prev.findIndex(a => a.id === updated.id);
                if (idx === -1) return [...prev, updated];
                const next = [...prev];
                next[idx] = updated;
                return next;
            });
        } catch (error) {
            console.error('Error updating user quiz attempt:', error);
        }
    }, [currentUser?.id]);

    // Render block element
    const renderBlock = (block, lessonIdx, pageIdx, blkIdx) => {
        const normalizedType = normalizeBlockType(block.type);
        const isInlineTextBlock = isInlineTextBlockType(normalizedType);
        const isDragging = dragState && dragState.lessonIdx === lessonIdx && 
                          dragState.pageIdx === pageIdx && dragState.blkIdx === blkIdx;
        const isResizing = resizeState && resizeState.lessonIdx === lessonIdx &&
                          resizeState.pageIdx === pageIdx && resizeState.blkIdx === blkIdx;
        const isSelected = isBlockSelected(lessonIdx, pageIdx, blkIdx);
        const titleStyle = {
            fontFamily: block.titleFont || block.font || 'Inter',
            fontWeight: (block.titleBold ?? block.bold) ? 700 : 400,
            textAlign: block.titleLayout || block.layout || 'left',
            color: block.titleColor || block.color || '#0f172a',
        };
        const textStyle = {
            fontFamily: block.textFont || block.font || 'Inter',
            fontWeight: (block.textBold ?? block.bold) ? 700 : 400,
            textAlign: block.textLayout || block.layout || 'left',
            color: block.textColor || block.color || '#0f172a',
        };
        const backgroundTextStyle = {
            fontFamily: block.backgroundTextFont || block.font || 'Inter',
            fontWeight: (block.backgroundTextBold ?? block.bold) ? 700 : 400,
            textAlign: block.backgroundTextLayout || block.layout || 'left',
            color: block.backgroundTextColor || block.color || '#0f172a',
        };
        const backgroundColor = block.backgroundColor || '#ffffff';
        const backgroundOpacity = block.backgroundOpacity ?? 1;
        const backgroundStyleColor = hexToRgba(backgroundColor, backgroundOpacity);
        const backgroundToggleWidth = !!block.backgroundToggleWidth;
        const isFullBleedBackground = normalizedType === 'background' && backgroundToggleWidth;
        const backgroundCorners = block.backgroundCorners || 'Round';

        const style = {
            // "Full width" now means the full design stage, not the browser viewport: inside a
            // scaled stage a vw-based width would grow and shrink independently of everything
            // around it, so the bar drifted out of alignment at any size but the authoring one.
            width: isFullBleedBackground ? '100%' : (block.width ? `${block.width}px` : '700px'),
            // text/title blocks are never vertically resizable (see the resizer-b/-br below) --
            // their height always tracks the editable-area content, ignoring any stored
            // block.height (e.g. a leftover value from before this restriction existed).
            height: isInlineTextBlock ? 'auto' : (block.height ? `${block.height}px` : 'auto'),
            position: (block.left !== undefined || block.top !== undefined) ? 'absolute' : 'relative',
            left: isFullBleedBackground ? '0px' : (block.left ? `${block.left}px` : undefined),
            top: block.top ? `${block.top}px` : undefined,
            transform: isFullBleedBackground ? 'none' : undefined,
            zIndex: isDragging || isResizing ? 1000 : 1,
            transition: isDragging || isResizing ? 'none' : undefined,
            cursor: isDragging ? 'grabbing' : (isStaff() && editorMode ? 'grab' : 'default'),
            userSelect: isDragging || isResizing ? 'none' : 'auto',
            background: (normalizedType === 'title' || normalizedType === 'text') ? 'transparent !important' : normalizedType === 'background'
                ? backgroundStyleColor
                : normalizedType === 'card' ? backgroundStyleColor : (normalizedType === 'quiz') ? 'var(--bg-secondary)' : 'transparent',
            boxShadow: (normalizedType === 'title' || normalizedType === 'text') ? 'none' : normalizedType === 'background'
                ? 'none'
                : (normalizedType === 'card' || normalizedType === 'quiz') ? 'var(--shadow-card)' : 'none',
            border: (normalizedType === 'title' || normalizedType === 'text') ? 'none' : normalizedType === 'background'
                ? 'none'
                : (normalizedType === 'card' || normalizedType === 'quiz') ? `1px solid ${backgroundStyleColor}` : 'none',
            borderRadius: normalizedType === 'background' ? (backgroundToggleWidth ? '0px' : (backgroundCorners === 'Square' ? '0px' : '28px')) : undefined,
            padding: (normalizedType === 'background' || normalizedType === 'card') ? '24px' : (normalizedType === 'quiz' ? undefined : 0),
        };

        return (
            <div
                key={block.id || `temp-block-${blkIdx}`}
                className={`content-block ${isInlineTextBlock ? 'textual-block' : ''} ${normalizedType === 'card' ? 'card-block' : ''} ${normalizedType === 'background' ? 'background-block' : ''} ${normalizedType === 'image' || normalizedType === 'video' ? 'media-block' : ''} ${normalizedType === 'quiz' ? 'quiz-block' : ''} ${isDragging ? 'sliding-block' : ''} ${isResizing ? 'resizing' : ''} ${isSelected ? 'block-selected' : ''}`}
                style={style}
                data-lesson-idx={lessonIdx}
                data-page-idx={pageIdx}
                data-blk-idx={blkIdx}
                onMouseDown={isStaff() && editorMode ? (e) => {
                    if (e.target.isContentEditable || e.target.closest('button, input, textarea, .resizer')) return;

                    if (e.ctrlKey || e.metaKey) {
                        e.stopPropagation();
                        e.preventDefault();
                        toggleBlockSelection(lessonIdx, pageIdx, blkIdx);
                        return;
                    }

                    if (isSelected && selectedBlocks.length > 1) {
                        handleMultiBlockDragStart(e, lessonIdx, pageIdx, blkIdx);
                    } else {
                        if (selectedBlocks.length) clearBlockSelection();
                        handleBlockDragStart(e, lessonIdx, pageIdx, blkIdx);
                    }
                } : undefined}
                onClick={(normalizedType === 'background') && isStaff() && editorMode ? (e) => {
                    if (e.target.closest('.background-textual-editable')) return;
                    if (e.ctrlKey || e.metaKey) return;
                    showShapeToolbar(lessonIdx, pageIdx, blkIdx, e.currentTarget);
                } : undefined}
                onContextMenu={isStaff() && editorMode ? (e) => handleBlockContextMenu(e, lessonIdx, pageIdx, blkIdx) : undefined}
            >
                <div className="block-controls">
                    {isStaff() && editorMode && (
                        <button
                            className="ctrl-btn drag-handle"
                            onMouseDown={(e) => {
                                if (isSelected && selectedBlocks.length > 1) {
                                    handleMultiBlockDragStart(e, lessonIdx, pageIdx, blkIdx);
                                } else {
                                    handleBlockDragStart(e, lessonIdx, pageIdx, blkIdx);
                                }
                            }}
                            style={{ cursor: 'grab' }}
                        >
                            ⋮⋮
                        </button>
                    )}
                    {isStaff() && editorMode && (normalizedType === 'card' || normalizedType === 'quiz') && (
                        <>
                            <button className="ctrl-btn" onClick={(e) => { 
                                if (normalizedType === 'card' || normalizedType === 'background') showShapeToolbar(lessonIdx, pageIdx, blkIdx, e.currentTarget.closest('.content-block'));
                                else { setModalBlockAction("Update"); openContentModal(block.type, lessonIdx, pageIdx, blkIdx); }
                            }}>✎</button>
                            <button className="ctrl-btn" onClick={() => duplicateBlock(lessonIdx, pageIdx, blkIdx)} title={t('chapter.duplicateTooltip')}>📄</button>
                            <button className="ctrl-btn" onClick={() => deleteBlock(lessonIdx, pageIdx, blkIdx)}>🗑</button>
                        </>
                    )}
                    {isStaff() && editorMode && (normalizedType !== 'card' && normalizedType !== 'quiz') && (
                        <>
                            <button className="ctrl-btn" onClick={() => duplicateBlock(lessonIdx, pageIdx, blkIdx)} title={t('chapter.duplicateTooltip')}>📄</button>
                            <button className="ctrl-btn" onClick={() => deleteBlock(lessonIdx, pageIdx, blkIdx)}>🗑</button>
                        </>
                    )}
                </div>

                {normalizedType === 'card' && (
                    <>
                        <h3 
                            className="editable-area textual-editable textual-title" 
                            contentEditable={isStaff() && editorMode}
                            suppressContentEditableWarning
                            onFocus={(e) => showTextToolbar(lessonIdx, pageIdx, blkIdx, 'title', e.currentTarget)}
                            onMouseDown={(e) => {
                                if (isStaff() && editorMode && !e.target.isContentEditable) {
                                    handleBlockDragStart(e, lessonIdx, pageIdx, blkIdx);
                                }
                            }}
                            onMouseUp={(e) => showTextToolbar(lessonIdx, pageIdx, blkIdx, 'title', e.currentTarget)}
                            onBlur={(e) => updateTextBlockField(lessonIdx, pageIdx, blkIdx, 'title', e.target.innerText)}
                            style={{ ...titleStyle, margin: '0 0 10px 0', fontSize: `${block.titleSize || 24}px`, display: block.titleToggle === false ? 'none' : 'block' }}
                        >
                            {isStaff() && editorMode ? (block.title || '') : renderInlineBoldText(block.title || '')}
                        </h3>
                        <p 
                            className="editable-area textual-editable textual-body" 
                            contentEditable={isStaff() && editorMode}
                            suppressContentEditableWarning
                            onFocus={(e) => showTextToolbar(lessonIdx, pageIdx, blkIdx, 'text', e.currentTarget)}
                            onMouseDown={(e) => {
                                if (isStaff() && editorMode && !e.target.isContentEditable) {
                                    handleBlockDragStart(e, lessonIdx, pageIdx, blkIdx);
                                }
                            }}
                            onMouseUp={(e) => showTextToolbar(lessonIdx, pageIdx, blkIdx, 'text', e.currentTarget)}
                            onBlur={(e) => updateTextBlockField(lessonIdx, pageIdx, blkIdx, 'text', e.target.innerText)}
                            style={{ ...textStyle, lineHeight: 1.6, margin: 0, fontSize: `${block.textSize || 16}px` }}
                            data-placeholder={block.placeholder || 'Type your text here...'}
                        >
                            {isStaff() && editorMode ? (block.text || '') : renderInlineBoldText(block.text || '')}
                        </p>
                    </>
                )}

                {normalizedType === 'background' && (
                    <div className="background-shape-shell" style={{ width: '100%', height: '100%' }}>
                        {block.backgroundTextToggle !== false && (
                            <div
                                className="editable-area textual-editable background-textual-editable"
                                contentEditable={isStaff() && editorMode}
                                suppressContentEditableWarning
                                onFocus={(e) => showTextToolbar(lessonIdx, pageIdx, blkIdx, 'background_text', e.currentTarget)}
                                onMouseDown={(e) => {
                                    if (isStaff() && editorMode && !e.target.isContentEditable) {
                                        handleBlockDragStart(e, lessonIdx, pageIdx, blkIdx);
                                    }
                                }}
                                onMouseUp={(e) => showTextToolbar(lessonIdx, pageIdx, blkIdx, 'background_text', e.currentTarget)}
                                onBlur={(e) => updateTextBlockField(lessonIdx, pageIdx, blkIdx, 'background_text', e.target.innerText)}
                                style={{ ...backgroundTextStyle, fontSize: `${block.backgroundTextSize || 16}px`, margin: 0, minHeight: '48px' }}
                                data-placeholder={block.placeholder || 'Background text...'}
                            >
                                {isStaff() && editorMode ? (block.backgroundText || '') : renderInlineBoldText(block.backgroundText || '')}
                            </div>
                        )}
                    </div>
                )}

                {normalizedType === 'title' && (
                    <h2 
                        className="editable-area textual-editable textual-title" 
                        contentEditable={isStaff() && editorMode}
                        suppressContentEditableWarning
                        onFocus={(e) => showTextToolbar(lessonIdx, pageIdx, blkIdx, 'title', e.currentTarget)}
                        onMouseDown={(e) => {
                            if (isStaff() && editorMode && !e.target.isContentEditable) {
                                handleBlockDragStart(e, lessonIdx, pageIdx, blkIdx);
                            }
                        }}
                        onMouseUp={(e) => showTextToolbar(lessonIdx, pageIdx, blkIdx, 'title', e.currentTarget)}
                        onBlur={(e) => updateTextBlockField(lessonIdx, pageIdx, blkIdx, 'title', e.target.innerText)}
                        style={{ ...titleStyle, margin: 0, fontSize: `${block.titleSize || 28}px` }}
                    >
                        {isStaff() && editorMode ? (block.title || '') : renderInlineBoldText(block.title || '')}
                    </h2>
                )}

                {normalizedType === 'text' && (
                    <p 
                        className="editable-area textual-editable textual-body" 
                        contentEditable={isStaff() && editorMode}
                        suppressContentEditableWarning
                        onFocus={(e) => showTextToolbar(lessonIdx, pageIdx, blkIdx, 'text', e.currentTarget)}
                        onMouseDown={(e) => {
                            if (isStaff() && editorMode && !e.target.isContentEditable) {
                                handleBlockDragStart(e, lessonIdx, pageIdx, blkIdx);
                            }
                        }}
                        onMouseUp={(e) => showTextToolbar(lessonIdx, pageIdx, blkIdx, 'text', e.currentTarget)}
                        onBlur={(e) => updateTextBlockField(lessonIdx, pageIdx, blkIdx, 'text', e.target.innerText)}
                        style={{ ...textStyle, lineHeight: 1.6, margin: 0, fontSize: `${block.textSize || 18}px` }}
                        data-placeholder={block.placeholder || 'Type your text here...'}
                    >
                        {isStaff() && editorMode ? (block.text || '') : renderInlineBoldText(block.text || '')}
                    </p>
                )}

                {normalizedType === 'quiz' && 
                (    
                    <QuizBlock
                        lessonPageBlockId={block.id}
                        localQuizBlock={block.quizBlock || null}
                        refreshTrigger={lastFetchTime}
                        onAttempt={(data) => handleQuizAttempt(block.quizBlock?.id, data)}
                    />
                )}

                {normalizedType === 'image' && (
                    <div className="media-display">
                        <img src={block.mediaUrl || block.value} alt="" loading="lazy" />
                    </div>
                )}

                {normalizedType === 'video' && (
                    <div className="media-display">
                        <Video
                            videoPath={block.mediaUrl || block.value}
                            initialTime={userLessonVideoProgresses.find((p) => p.user === currentUser?.id && p.lesson_page_block === block.id)?.watched_timestamp || 0}
                            onProgress={(progress) => handleVideoProgress(block.id, progress)}
                        />
                    </div>
                )}

                {isStaff() && editorMode && (
                    <>
                        <div
                            className="resizer resizer-r"
                            onMouseDown={(e) => handleResizeStart(e, lessonIdx, pageIdx, blkIdx, 'r')}
                        ></div>
                        {/* text/title blocks are horizontal-only: their height tracks content
                            (see the style object above), so no bottom/corner resize handle. */}
                        {!isInlineTextBlock && (
                            <>
                                <div
                                    className="resizer resizer-b"
                                    onMouseDown={(e) => handleResizeStart(e, lessonIdx, pageIdx, blkIdx, 'b')}
                                ></div>
                                <div
                                    className="resizer resizer-br"
                                    onMouseDown={(e) => handleResizeStart(e, lessonIdx, pageIdx, blkIdx, 'br')}
                                ></div>
                            </>
                        )}
                    </>
                )}
            </div>
        );
    };

    // Render page section
    const renderPage = (page, pageIdx, lesson, lessonIdx) => {
        const isFirstPage = pageIdx === 0;
        const isLastPage = pageIdx === lesson.pages.length - 1;
        const isLastLesson = lessonIdx === chapterData.lessons.length - 1;

        const pageBackgroundUrl = page.background_image_url || '';
        const pageBackgroundIsVideo = page.background_type === 'video';

        // Only the slide actually on screen carries the viewer's zoom transform. Applying it to
        // every section (the transform lives on .lesson-section) scaled the neighbouring pages
        // too, so they grew past their own box into the viewport and -- being later in DOM order
        // at the same stacking level -- painted OVER the presented slide, showing the adjacent
        // page's background creeping in as you zoomed.
        const isPresentingThisPage = isSlideshowActive
            && lessonIdx === activeLessonIdx
            && pageIdx === currentSnappedPageIdx;

        return (
            <div
                key={page.id || `temp-page-${pageIdx}`}
                className={`lesson-section${pageBackgroundUrl ? ' has-page-background' : ''}${isPresentingThisPage ? ' is-presenting' : ''}`}
                id={`page-${lessonIdx}-${pageIdx}`}
                style={{ '--lesson-section-height': `${page.height ?? 100}vh` }}
            >
                {/* Per-page background layer (set directly or by applying a Template -- see
                    TemplateManager.jsx). Rendered per lesson-section rather than on the shared
                    .canvas-wrapper so each page keeps its own background while scrolling, and
                    pointer-events:none via CSS so it never intercepts block drags/marquee select. */}
                {pageBackgroundUrl && (
                    <div className="lesson-section-background" aria-hidden="true">
                        {pageBackgroundIsVideo ? (
                            <video src={pageBackgroundUrl} muted loop autoPlay playsInline />
                        ) : (
                            <img src={pageBackgroundUrl} alt="" />
                        )}
                    </div>
                )}
                {/* Sibling of .header-area, not a child of it: the header is capped at
                    max-width: 1100px and is the positioned ancestor, so a left:50% button inside it
                    centred on the header rather than on the page and visibly leaned left on wide
                    screens. As a direct child of the (full-width, position: relative)
                    .lesson-section it centres on the page itself. */}
                {isFirstPage && lessonIdx > 0 && (
                    <button
                        className="prev-lesson-btn"
                        onClick={() => {
                            const nextLesson = chapterData.lessons[lessonIdx - 1];
                            const nextProgress = nextLesson && userLessonProgresses.find(p => p.lesson === nextLesson.id);
                            const unlocked = editorMode || (nextProgress ? !!nextProgress.unlocked : false);
                            if (unlocked) {
                                loadLesson(lessonIdx - 1);
                            }
                        }}
                    >
                        {t('chapter.previousLesson')}
                    </button>
                )}

                {isFirstPage ? (
                    <div
                        className="header-area"
                        id={`headerArea-${lessonIdx}`}
                        style={{ 
                            cursor: isStaff() && editorMode ? 'grab' : 'default',
                            paddingTop: lesson.headerPaddingTop ? `${lesson.headerPaddingTop}px` : undefined
                        }}
                    >
                        <div className="lesson-badge">{t('chapter.lessonBadge', { n: lessonIdx + 1 })}</div>
                        <div 
                            className="lesson-hero-card" 
                            id={`heroCard-${lessonIdx}`}
                            style={{ 
                                cursor: isStaff() && editorMode ? 'grab' : 'default',
                                left: lesson.heroLeft ? `${lesson.heroLeft}px` : undefined,
                                top: lesson.heroTop ? `${lesson.heroTop}px` : undefined
                            }}
                            onMouseDown={(e) => handleElementDragStart(e, 'hero-card', lessonIdx)}
                        >
                            <h2 
                                className="hero-title" 
                                contentEditable={isStaff() && editorMode}
                                suppressContentEditableWarning
                                onBlur={(e) => updateLessonTitleFromMain(lessonIdx, e.target.innerText)}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); }}}
                                style={{ cursor: isStaff() && editorMode ? 'text' : 'default' }}
                            >
                                {lesson.title}
                            </h2>
                        </div>
                        <div 
                            className="header-resizer" 
                            id={`headerResizer-${lessonIdx}`} 
                            title={isStaff() && editorMode ? t('chapter.dragToMoveOrResize') : undefined}
                            style={{
                                left: lesson.headerResizerLeft ? `${lesson.headerResizerLeft}px` : undefined,
                                width: lesson.headerResizerWidth ? `${lesson.headerResizerWidth}px` : undefined,
                                cursor: isStaff() && editorMode ? 'all-scroll' : 'default'
                            }}
                            onMouseDown={isStaff() && editorMode ? (e) => {
                                if (e.target.classList.contains('hr-handle')) {
                                    handleHeaderLineResize(e, lessonIdx);
                                } else {
                                    handleHeaderResizerDrag(e, lessonIdx);
                                }
                            } : undefined}
                        >
                            {isStaff() && editorMode && (
                                <>
                                    <div className="hr-handle hr-handle-l"></div>
                                    <div className="hr-handle hr-handle-r"></div>
                                </>
                            )}
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="page-break-line"></div>
                        {/* Stands in for the header-area that only the first page of a lesson has,
                            so blocks start at the same vertical offset on every page. Classed (not
                            inline-styled) so it can be dropped on full-bleed background pages,
                            where the header-area is hidden and this would leave a dead strip. */}
                        <div className="page-header-spacer"></div>
                    </>
                )}

                <div 
                    className="part-title-container"
                    style={{ 
                        cursor: isStaff() && editorMode ? 'grab' : 'default',
                        left: page.partTitleLeft ? `${page.partTitleLeft}px` : undefined,
                        top: page.partTitleTop ? `${page.partTitleTop}px` : undefined
                    }}
                    onMouseDown={isStaff() && editorMode ? (e) => handleElementDragStart(e, 'part-title', lessonIdx, pageIdx) : undefined}
                >
                    <div 
                        className="part-title-text" 
                        contentEditable={isStaff() && editorMode}
                        suppressContentEditableWarning
                        onBlur={(e) => updatePageTitleFromMain(lessonIdx, pageIdx, e.target.innerText)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); }}}
                        style={{ cursor: isStaff() && editorMode ? 'text' : 'default' }}
                    >
                        {page.title || `Part ${pageIdx + 1}`}
                    </div>
                </div>

                {pageIdx > 0 && (
                    <div className="scroll-up-btn" onClick={() => scrollToPage(lessonIdx, pageIdx - 1)}>↑</div>
                )}

                {/* Renders the fluid container plus the fixed-size, self-measuring .blocks-stage
                    inside it -- every block coordinate below is relative to THAT stage, never to
                    the container around it. See chapterStage.js. */}
                <LessonPageCanvas
                    containerProps={{
                        className: `blocks-container${isStaff() && editorMode && dragOverPageKey === `${lessonIdx}-${pageIdx}` ? ' drag-over' : ''}`,
                        id: `blocks-page-${lessonIdx}-${pageIdx}`,
                        onMouseDown: isStaff() && editorMode ? (e) => handleCanvasMouseDown(e, lessonIdx, pageIdx) : undefined,
                        onContextMenu: isStaff() && editorMode ? (e) => handleCanvasContextMenu(e, lessonIdx, pageIdx) : undefined,
                        onDragOver: isStaff() && editorMode ? (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; } : undefined,
                        onDragEnter: isStaff() && editorMode ? () => setDragOverPageKey(`${lessonIdx}-${pageIdx}`) : undefined,
                        onDragLeave: isStaff() && editorMode ? (e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverPageKey(null); } : undefined,
                        onDrop: isStaff() && editorMode ? (e) => handleBlockTypeDrop(e, lessonIdx, pageIdx) : undefined,
                    }}
                >
                    {page.blocks.map((block, blkIdx) => renderBlock(block, lessonIdx, pageIdx, blkIdx))}
                </LessonPageCanvas>

                {/* Navigation buttons */}
                {!isLastPage ? (
                    <div className="scroll-down-btn" onClick={() => scrollToPage(lessonIdx, pageIdx + 1)}>↓</div>
                ) : !isLastLesson ? (
                    <>
                        <button
                            className="next-lesson-btn"
                            onClick={() => {
                                const nextLesson = chapterData.lessons[lessonIdx + 1];
                                const nextProgress = nextLesson && userLessonProgresses.find(p => p.lesson === nextLesson.id);
                                const unlocked = editorMode || (nextProgress ? !!nextProgress.unlocked : false);
                                if (unlocked) {
                                    loadLesson(lessonIdx + 1);
                                }
                            }}
                        >
                            {t('chapter.nextLesson')}
                        </button>
                        {isStaff() && editorMode && (
                            <button className="add-page-secondary" onClick={addPage}>
                                {t('chapter.addPageToLesson')}
                            </button>
                        )}
                    </>
                ) : isStaff() && editorMode && (
                    <button className="add-page-btn" onClick={addPage}>{t('chapter.addNextPage')}</button>
                )}
            </div>
        );
    };

    if (loading) {
        return <div className="Chapter"><Loading message={t('chapter.loadingChapter')} /></div>;
    }

    if (error) {
        return <div className="Chapter"><p>{t('chapter.errorPrefix', { error })}</p></div>;
    }

    const currentLesson = chapterData.lessons[activeLessonIdx];

    return (
        <>
            <Navbar
                updateEditorMode={(mode) => updateEditorMode(mode)}
                editorMode={editorMode}
                onSave={saveChapterSession}
                onUndo={handleUndo}
                onRedo={handleRedo}
                canUndo={sessionHistoryMeta.index > 0}
                canRedo={sessionHistoryMeta.index >= 0 && sessionHistoryMeta.index < sessionHistoryMeta.length - 1}
                onIncreaseLessonSectionSize={increaseLessonSectionSize}
                onDecreaseLessonSectionSize={decreaseLessonSectionSize}
                lessonSectionScale={(currentLesson?.pages?.[currentSnappedPageIdx]?.height ?? 100) / 100}
                onHistory={openHistoryModal}
                viewOnlyMode={levelViewOnly}
            />
            <ModalEditorHistory 
                chapterId={chapterData.id}
                isOpen={isHistoryModalOpen}
                closeModal={closeHistoryModal}
            />
            {textToolbarState.show && (
                <div
                    className="text-format-toolbar"
                    style={{ top: textToolbarState.position.top, left: textToolbarState.position.left }}
                >
                    <select className="fmt-select" value={textToolbarState.font || 'Inter'} onChange={(e) => changeTextFontFamily(e.target.value)}>
                        <option value="Inter">Inter</option>
                        <option value="Georgia">Georgia</option>
                        <option value="Arial">Arial</option>
                        <option value="Courier New">Courier New</option>
                        <option value="Times New Roman">Times New Roman</option>
                        <option value="Trebuchet MS">Trebuchet MS</option>
                        <option value="Verdana">Verdana</option>
                        <option value="Comic Sans MS">Comic Sans MS</option>
                    </select>
                    <div className="fmt-divider"></div>
                    <button type="button" className="fmt-btn" onClick={() => changeTextFontSize(-2)}>A-</button>
                    <button type="button" className="fmt-btn" onClick={() => changeTextFontSize(2)}>A+</button>
                    <div className="fmt-divider"></div>
                    <button type="button" className={`fmt-btn ${textToolbarState.bold ? 'active' : ''}`} onClick={toggleTextBold}>B</button>
                    <div className="fmt-divider"></div>
                    <button type="button" className="fmt-btn" onClick={() => changeTextAlignment('left')}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6"></line><line x1="3" y1="12" x2="15" y2="12"></line><line x1="3" y1="18" x2="21" y2="18"></line></svg>
                    </button>
                    <button type="button" className="fmt-btn" onClick={() => changeTextAlignment('center')}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6"></line><line x1="6" y1="12" x2="18" y2="12"></line><line x1="3" y1="18" x2="21" y2="18"></line></svg>
                    </button>
                    <button type="button" className="fmt-btn" onClick={() => changeTextAlignment('right')}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6"></line><line x1="3" y1="12" x2="15" y2="12"></line><line x1="3" y1="18" x2="21" y2="18"></line></svg>
                    </button>
                    <div className="fmt-divider"></div>
                    <input
                        type="color"
                        className="universal-text-color-picker"
                        value={textToolbarState.color || '#0f172a'}
                        onChange={(e) => changeTextColor(e.target.value)}
                        title={t('chapter.textColor')}
                    />
                </div>
            )}
            {shapeToolbarState.show && (
                <div
                    className="shape-format-toolbar text-format-toolbar"
                    style={{ top: shapeToolbarState.position.top, left: shapeToolbarState.position.left }}
                >
                    {chapterData.lessons?.[shapeToolbarState.lessonIdx]?.pages?.[shapeToolbarState.pageIdx]?.blocks?.[shapeToolbarState.blkIdx]?.type === 'background' ? (
                        <>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontSize: '0.8rem', fontWeight: '700', color: 'var(--text-muted)' }}>{t('chapter.bgColorLabel')}</span>
                            <input
                                type="color"
                                className="universal-text-color-picker"
                                value={shapeToolbarState.color || '#ffffff'}
                                onChange={(e) => changeBackgroundColor(e.target.value)}
                                title={t('chapter.backgroundColorTooltip')}
                            />
                        </div>
                        <div style={{ display: 'flex', gap: '6px' }}>
                            <div 
                            className="color-swatch" 
                            style={{ background: 'var(--accent)' }} 
                            onClick={() => changeBackgroundColor('var(--accent)')} 
                            title={t('chapter.themePurple')}
                            ></div>
                            <div 
                            className="color-swatch" 
                            style={{ background: '#334155' }} 
                            onClick={() => changeBackgroundColor('#334155')} 
                            title={t('chapter.darkGrey')}
                            ></div>
                            <div 
                            className="color-swatch" 
                            style={{ background: '#94a3b8' }} 
                            onClick={() => changeBackgroundColor('#94a3b8')} 
                            title={t('chapter.grey')}
                            ></div>
                            <div 
                            className="color-swatch" 
                            style={{ background: '#f1f5f9' }} 
                            onClick={() => changeBackgroundColor('#f1f5f9')} 
                            title={t('chapter.lightGrey')}
                            ></div>
                        </div>
                    </div>
                    <div className="fmt-divider"></div>
                    <span style={{ fontSize: '0.8rem', fontWeight: '700', color: 'var(--text-muted)' }}>{t('chapter.opacityLabel')}</span>
                    <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.05"
                        value={shapeToolbarState.opacity ?? 1}
                        onChange={(e) => changeBackgroundOpacity(e.target.value)}
                        title={t('chapter.backgroundOpacityTooltip')}
                    />
                    <div className="fmt-divider"></div>
                    <button type="button" className={`fmt-btn ${shapeToolbarState.toggleWidth ? 'active' : ''}`} onClick={toggleBackgroundWidth}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 12H3m18 0l-4-4m4 4l-4 4m-14 0l4-4-4-4"></path></svg>
                    </button>
                    <div className="fmt-divider"></div>
                    <span style={{ fontSize: '0.8rem', fontWeight: '700', color: 'var(--text-muted)' }}>{t('chapter.cornersLabel')}</span>
                    <div style={{ display: 'flex', gap: '6px' }}>
                        <button type="button" className={`fmt-btn ${shapeToolbarState.corners === 'Square' ? 'active' : ''}`} onClick={() => changeBackgroundCorners('Square')}>⬛</button>
                        <button type="button" className={`fmt-btn ${shapeToolbarState.corners === 'Round' ? 'active' : ''}`} onClick={() => changeBackgroundCorners('Round')}>🔲</button>
                    </div>
                    <div className="fmt-divider"></div>
                    <button type="button" className={`fmt-btn ${shapeToolbarState.textToggle ? 'active' : ''}`} onClick={toggleBackgroundText}>{t('chapter.aaText')}</button>
                        </>
                    ) : (
                        <>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'center' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span style={{ fontSize: '0.8rem', fontWeight: '700', color: 'var(--text-muted)' }}>{t('chapter.bgColorLabel')}</span>
                                    <input
                                        type="color"
                                        className="universal-text-color-picker"
                                        value={shapeToolbarState.color || '#ffffff'}
                                        onChange={(e) => changeBackgroundColor(e.target.value)}
                                        title={t('chapter.backgroundColorTooltip')}
                                    />
                                </div>
                                <div style={{ display: 'flex', gap: '6px' }}>
                                    <div
                                        className="color-swatch"
                                        style={{ background: 'var(--accent)' }}
                                        onClick={() => changeBackgroundColor('var(--accent)')}
                                        title={t('chapter.themePurple')}
                                    ></div>
                                    <div
                                        className="color-swatch"
                                        style={{ background: '#334155' }}
                                        onClick={() => changeBackgroundColor('#334155')}
                                        title={t('chapter.darkGrey')}
                                    ></div>
                                    <div
                                        className="color-swatch"
                                        style={{ background: '#94a3b8' }}
                                        onClick={() => changeBackgroundColor('#94a3b8')}
                                        title={t('chapter.grey')}
                                    ></div>
                                    <div
                                        className="color-swatch"
                                        style={{ background: '#f1f5f9' }}
                                        onClick={() => changeBackgroundColor('#f1f5f9')}
                                        title={t('chapter.lightGrey')}
                                    ></div>
                                </div>
                            </div>
                            <div className="fmt-divider"></div>
                            <span style={{ fontSize: '0.8rem', fontWeight: '700', color: 'var(--text-muted)' }}>{t('chapter.opacityLabel')}</span>
                            <input
                                type="range"
                                min="0"
                                max="1"
                                step="0.05"
                                value={shapeToolbarState.opacity ?? 1}
                                onChange={(e) => changeBackgroundOpacity(e.target.value)}
                                title={t('chapter.backgroundOpacityTooltip')}
                            />
                            <div className="fmt-divider"></div>
                            <button type="button" className={`fmt-btn ${shapeToolbarState.titleToggle ? 'active' : ''}`} onClick={toggleTitle}>{t('chapter.aaTitle')}</button>
                        </>
                    )}
                </div>
            )}
            <div
                className={`Chapter ${animationClass} ${isAnimating ? 'sliding-block' : ''}`}
            >
                <button 
                    id="centerToggleBtn" 
                    className="sidebar-toggle-btn" 
                    onClick={toggleSidebar}
                >
                    {isSidebarOpen ? '‹' : '›'}
                </button>

                <aside className={`chapter-sidebar ${!isSidebarOpen ? 'closed' : ''}`} id="appSidebar">
                    <div className="sidebar-header">
                        <span className="chapter-label">{t('chapter.currentChapterLabel')}</span>
                        <div className="chapter-title-wrapper">
                            <h1 className="chapter-title" id="chapterTitleDisplay">{chapterData.title}</h1>
                            {isStaff() && editorMode && <div className="edit-icon" onClick={renameChapter}>✎</div>}
                        </div>
                    </div>

                    <div className="lesson-list-container" id="lessonList">
                        <ReactSortable
                            list={chapterData.lessons}
                            setList={handleLessonReorder}
                            animation={200}
                            handle=".lesson-handle"
                            disabled={!isStaff() || !editorMode}
                            scroll={true}
                            scrollSensitivity={60}
                                   scrollSpeed={25}
                            bubbleScroll={true}
                            forceFallback={true}
                            fallbackOnBody={true}
                            fallbackTolerance={4}
                        >
                            {chapterData.lessons.map((lesson, i) => {
                                const userLessonProgress = userLessonProgresses.find(progress => progress.lesson === lesson.id && progress.user === currentUser?.id);
                                const isLocked = (userLessonProgress ? !userLessonProgress.unlocked : true) && !(isStaff() || isAdmin());
                                return (
                            <div
                                key={lesson.id || `temp-lesson-${i}`}
                                    className={`lesson-item ${i === activeLessonIdx ? 'active' : ''} ${isLocked && !editorMode ? 'locked-lesson' : ''}`}
                                    onClick={(e) => { if (!e.target.closest('button') && (!isLocked || editorMode)) loadLesson(i); }}
                                >
                                    <div className="lesson-item-top">
                                        <div style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0, overflow: 'hidden' }}>
                                            {isStaff() && editorMode && <span className="lesson-handle" style={{ flexShrink: 0 }}>⋮⋮</span>}
                                            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'clip', marginLeft: '4px' }}>
                                                {i + 1}. {lesson.title}
                                            </span>
                                        </div>
                                        {
                                            isLocked && !editorMode && (
                                                <span style={{ color: 'gray'}}>
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        width="25"
                                                        height="25"
                                                        viewBox="0 0 24 20"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        strokeWidth="1.8"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        aria-label="Locked module"
                                                        role="img"
                                                        >
                                                        <title>Locked module</title>
                                                        <rect x="3" y="10" width="18" height="11" rx="2.5" fill="currentColor" fillOpacity="0.12" />
                                                        <path d="M7 10V7a5 5 0 0 1 10 0v3" />
                                                        <circle cx="12" cy="15" r="1.25" fill="currentColor" stroke="none" />
                                                        <path d="M12 16.25V18" />
                                                        <path d="M5.5 10h13" opacity="0.45" />
                                                    </svg> {/* Detailed lock icon */}
                                                </span>
                                            )
                                        }
                                        {isStaff() && editorMode && (
                                            <div className="lesson-actions">
                                                <button className="mini-action" onClick={(e) => { e.stopPropagation(); renameLesson(i); }}>✎</button>
                                                <button className="mini-action" onClick={(e) => { e.stopPropagation(); handleDeleteLesson(i); }}>×</button>
                                            </div>
                                        )}
                                    </div>

                                    {i === activeLessonIdx && (
                                        <div className="lesson-sub-nav">
                                            {lesson.pages.map((page, pIdx) => (
                                                <div 
                                                    key={page.id || `temp-sub-page-${pIdx}`}
                                                    id={`sub-link-${pIdx}`}
                                                    className={`sub-link ${pIdx === currentSnappedPageIdx ? 'active-sub' : ''}`}
                                                    onClick={(e) => { e.stopPropagation(); scrollToPage(i, pIdx); } }
                                                >
                                                    <div className="sub-link-content">
                                                        <span className="sub-dot"></span>
                                                        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                            {page.title || `Part ${pIdx + 1}`}
                                                        </span>
                                                    </div>
                                                    {isStaff() && editorMode && (
                                                        <div className="page-actions-group">
                                                            <button className="tiny-btn" onClick={(e) => { e.stopPropagation(); renamePage(i, pIdx); }} title={t('chapter.renameTooltip')}>✎</button>
                                                            <button className="tiny-btn" onClick={(e) => { e.stopPropagation(); deletePage(i, pIdx); }} title={t('chapter.deletePartTooltip')}>×</button>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                );
                            })}
                        </ReactSortable>
                    </div>
                    <button className="btn-primary btn-back" style={{  display: 'flex', justifySelf: 'center', alignSelf: 'center', marginTop: '20px', marginBottom: '30px'}} onClick={handleBackToLevel}>{t('chapter.backToLevel')}</button>

                    {isStaff() && editorMode && (
                        <button className="add-lesson-btn" onClick={addNewLesson}>{t('chapter.addNewLesson')}</button>
                    )}
                </aside>

                <main className="chapter-main">
                    {isStaff() && (
                        <div className={`editor-toolbar ${editorMode && currentLesson ? 'editor-mode-active' : 'editor-mode-hidden'} ${isToolbarCollapsed ? 'collapsed' : ''}`} id="mainEditorToolbar">
                            <button type="button" className="toolbar-collapse-tab" onClick={() => setIsToolbarCollapsed((current) => !current)} title={t('chapter.toggleToolbar')}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M6 9l6 6 6-6"></path>
                                </svg>
                            </button>
                            <div className="toolbar-actions">
                                <div
                                    className="tool-pill"
                                    draggable
                                    onDragStart={(e) => e.dataTransfer.setData(DRAG_BLOCK_TYPE_MIME, 'card')}
                                    onClick={() => createTextualBlock('card')}
                                >
                                    <span>🃏</span> {t('chapter.card')}
                                </div>
                                <div
                                    className="tool-pill"
                                    draggable
                                    onDragStart={(e) => e.dataTransfer.setData(DRAG_BLOCK_TYPE_MIME, 'text')}
                                    onClick={() => createTextualBlock('text')}
                                >
                                    <span>📝</span> {t('chapter.text')}
                                </div>
                                <div
                                    className="tool-pill"
                                    draggable
                                    onDragStart={(e) => e.dataTransfer.setData(DRAG_BLOCK_TYPE_MIME, 'title')}
                                    onClick={() => createTextualBlock('title')}
                                >
                                    <span>Aa</span> {t('chapter.title')}
                                </div>
                                <div
                                    className="tool-pill"
                                    draggable
                                    onDragStart={(e) => e.dataTransfer.setData(DRAG_BLOCK_TYPE_MIME, 'background')}
                                    onClick={() => createBackgroundBlock()}
                                >
                                    <span>▣</span> {t('chapter.background')}
                                </div>
                                <div
                                    className="tool-pill accent"
                                    draggable
                                    onDragStart={(e) => e.dataTransfer.setData(DRAG_BLOCK_TYPE_MIME, 'image')}
                                    onClick={triggerImageUpload}
                                >
                                    <span>📷</span> {t('chapter.image')}
                                </div>
                                <div
                                    className="tool-pill accent"
                                    draggable
                                    onDragStart={(e) => e.dataTransfer.setData(DRAG_BLOCK_TYPE_MIME, 'video')}
                                    onClick={triggerVideoUpload}
                                >
                                    <span>▶</span> {t('chapter.video')}
                                </div>
                                <div
                                    className="tool-pill accent"
                                    draggable
                                    onDragStart={(e) => e.dataTransfer.setData(DRAG_BLOCK_TYPE_MIME, 'quiz')}
                                    onClick={() => {
                                        setEditingBlock({ l: activeLessonIdx, p: currentSnappedPageIdx, b: null });
                                        setIsModalOpen(true);
                                        setModalBlockAction('Add');
                                        setModalHeading('');
                                        setModalBody('');
                                        setModalHeadingSize(24);
                                        setModalBodySize(16);
                                        setModalType('quiz');
                                    }}
                                >
                                    <span>❓</span> {t('chapter.quiz')}
                                </div>
                                <div className="fmt-divider"></div>
                                <div className="tool-pill use-thumbnail" onClick={() => setOpenUseThumbnailModal(true)}>
                                    <span>🖼️</span> {t('chapter.useTemplate')}
                                </div>
                                <div className="tool-pill clear-page" onClick={clearCurrentPageBlocks}>
                                    <span>🗑️</span> {t('chapter.clearPage')}
                                </div>
                            </div>
                        </div>
                    )}

                    <div
                        className="canvas-wrapper"
                        id="canvasWrapper"
                        ref={canvasWrapperRef}
                        style={{
                            paddingBottom: '0px',
                            // Pinch/wheel zoom, slideshow only -- see the gesture effect above.
                            '--slide-zoom': slideZoom.scale,
                            '--slide-pan-x': `${slideZoom.x}px`,
                            '--slide-pan-y': `${slideZoom.y}px`,
                        }}
                    >
                        <div className="snap-guide-center" id="guideCenter" ref={guideCenterRef}></div>
                        <div className="snap-guide-left" id="guideLeft" ref={guideLeftRef}></div>
                        <div className="snap-guide-right" id="guideRight" ref={guideRightRef}></div>
                        <div className="alignment-line align-h" id="guideAlignH" ref={guideAlignHRef}></div>
                        <div className="alignment-line align-v" id="guideAlignV" ref={guideAlignVRef}></div>

                        {marqueeState && (
                            <div
                                className="marquee-select"
                                style={{
                                    left: Math.min(marqueeState.startX, marqueeState.curX),
                                    top: Math.min(marqueeState.startY, marqueeState.curY) - 60,
                                    width: Math.abs(marqueeState.curX - marqueeState.startX),
                                    height: Math.abs(marqueeState.curY - marqueeState.startY),
                                }}
                            ></div>
                        )}

                        <div id="pagesContainer">
                            {currentLesson && currentLesson.pages.map((page, pageIdx) => 
                                renderPage(page, pageIdx, currentLesson, activeLessonIdx)
                            )}
                        </div>
                    </div>
                </main>

                {/* Hidden file inputs */}
                <input 
                    type="file" 
                    ref={videoInputRef}
                    accept="video/*" 
                    style={{ display: 'none' }} 
                    onChange={handleVideoUpload}
                />
                <input
                    type="file"
                    ref={imageInputRef}
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleImageUpload}
                />

                {blockContextMenu && (
                    <div
                        className="block-context-menu"
                        style={{ left: blockContextMenu.x, top: blockContextMenu.y }}
                    >

                        <button onClick={() => { duplicateSelectedBlocks(); setBlockContextMenu(null); }}>
                            {t('chapter.duplicateLabel')} {selectedBlocks.length > 1 ? `(${selectedBlocks.length})` : ''}
                        </button>
                        <button onClick={() => {
                            copySelectedBlocks(); setBlockContextMenu(null); }}>
                            {t('chapter.copyLabel')} {selectedBlocks.length > 1 ? `(${selectedBlocks.length})` : ''}
                        </button>
                        { clipboardBlocksRef.current?.length > 0 && (
                            <button onClick={() => { pasteBlockRefs(clipboardBlocksRef.current); setBlockContextMenu(null); }}>
                                {t('chapter.pasteLabel')} {clipboardBlocksRef.current.length > 1 ? `(${clipboardBlocksRef.current.length})` : ''}
                            </button>
                        )}
                        <button onClick={() => { deleteSelectedBlocks(); setBlockContextMenu(null); }}>
                            {t('chapter.deleteLabel')} {selectedBlocks.length > 1 ? `(${selectedBlocks.length})` : ''}
                        </button>
                    </div>
                )}

                {blockCreateMenu && (
                    <div
                        className="block-context-menu block-create-menu"
                        style={{ left: blockCreateMenu.x, top: blockCreateMenu.y - 75 }}
                    >
                        <button onClick={() => { selectAllBlocks(); setBlockCreateMenu(null); }}>
                            {t('chapter.selectAll')}
                        </button>
                        { clipboardBlocksRef.current?.length > 0 && (
                            <button onClick={() => { pasteBlockRefs(clipboardBlocksRef.current); setBlockCreateMenu(null); }}>
                                {t('chapter.pasteLabel')} {clipboardBlocksRef.current.length > 1 ? `(${clipboardBlocksRef.current.length})` : ''}
                            </button>
                        )}
                        <button onClick={() => handleCreateBlockFromMenu('card')}>{t('chapter.newCard')}</button>
                        <button onClick={() => handleCreateBlockFromMenu('text')}>{t('chapter.newText')}</button>
                        <button onClick={() => handleCreateBlockFromMenu('title')}>{t('chapter.newTitle')}</button>
                        <button onClick={() => handleCreateBlockFromMenu('background')}>{t('chapter.newBackground')}</button>
                        <button onClick={() => handleCreateBlockFromMenu('image')}>{t('chapter.newImage')}</button>
                        <button onClick={() => handleCreateBlockFromMenu('video')}>{t('chapter.newVideo')}</button>
                        <button onClick={() => handleCreateBlockFromMenu('quiz')}>{t('chapter.newQuiz')}</button>
                    </div>
                )}

                {isModalOpen && (
                    <ModalBlock
                        type={modalType}
                        Action={modalBlockAction}
                        selectedBlock={editingBlock}
                        lessonPage={currentLesson?.pages[currentSnappedPageIdx]}
                        closeModal={closeModal}
                        modalHeading={modalHeading}
                        setModalHeading={setModalHeading}
                        modalHeadingSize={modalHeadingSize}
                        setModalHeadingSize={setModalHeadingSize}
                        modalBody={modalBody}
                        setModalBody={setModalBody}
                        modalBodySize={modalBodySize}
                        setModalBodySize={setModalBodySize}
                        saveContent={saveContent}
                        onSaveQuizDraft={saveQuizDraft}
                        onModalSave={handleModalSave}
                        onLogChange={logChapterChange}
                    />
                )}
            </div>
            <TemplateManager
                isEditing={editorMode}
                isUseTemplateOpen={openUseThumbnailModal}
                onCloseUseTemplate={() => setOpenUseThumbnailModal(false)}
                currentPageBlocks={currentLesson?.pages?.[currentSnappedPageIdx]?.blocks || []}
                currentPageBackgroundUrl={currentLesson?.pages?.[currentSnappedPageIdx]?.background_image_url || ''}
                currentPageBackgroundType={currentLesson?.pages?.[currentSnappedPageIdx]?.background_type || 'image'}
                onApplyTemplate={applyTemplateToCurrentPage}
                onSetPageBackground={setCurrentPageBackground}
                onClearPageBackground={clearCurrentPageBackground}
                slideshowSlides={slideshowSlides}
                slideshowStartAt={{ lessonIdx: activeLessonIdx, pageIdx: currentSnappedPageIdx }}
                onSlideshowGoTo={handleSlideshowGoTo}
                // useState setters are referentially stable, so this can't retrigger the effect
                // that reports slideshow state back up.
                onSlideshowActiveChange={setIsSlideshowActive}
                // Route template feedback through this page's single toast instead of
                // TemplateManager's own -- both render at bottom-centre, so two live
                // instances would sit exactly on top of each other.
                onToast={showToast}
            />
            <SavingIndicator active={isSavingChapter} label={t('common.saving')} />
            <Toast toast={toast} />
        </>
    );
}

export default Chapter;